import {
  Button,
  Form,
  Input,
  message,
  Modal,
  Popover,
  Progress,
  Radio,
  Select,
  Badge,
  Checkbox,
  Drawer,
  Steps,
  Tag,
  Upload,
} from "antd";
import { FC, useCallback, useEffect, useRef, useState } from "react";
import * as ReactFlowLib from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import moment from "moment";
import "moment/locale/zh-cn.js";
import {
  AuthSession,
  AUTH_INVALID_EVENT,
  clearAuthStorage,
  getAuthSession,
  resolveAssetURL,
  saveAuthStorage,
} from "../../api";
import Login from "../../components/Login/Index";
import {
  CalendarOutlined,
  DownOutlined,
  EditOutlined,
  PictureOutlined,
  ProjectOutlined,
  SearchOutlined,
  SettingOutlined,
  PlusOutlined,
  InboxOutlined,
  UserOutlined,
  ThunderboltOutlined,
  WarningOutlined,
  ClockCircleOutlined,
  FullscreenOutlined,
  FullscreenExitOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  CommentOutlined,
  UploadOutlined,
  DownloadOutlined,
  DeleteOutlined,
  PaperClipOutlined,
  LeftOutlined,
  AuditOutlined,
  AppstoreOutlined,
  FileTextOutlined,
} from "@ant-design/icons";
import { Avatar, Card, Dropdown, Space } from "antd";
import type { MenuProps } from "antd";
import type { UploadProps } from "antd";
import createApi, { baseURL } from "../../api";
import DeleteDepartmentModal from "./components/DeleteDepartmentModal";
import ContentDueRiskPanel from "./components/ContentDueRiskPanel";
import HeaderUserAvatar from "./components/HeaderUserAvatar";
import HomeSidebar from "./components/HomeSidebar";
import MemberAvatar from "./components/MemberAvatar";
import ProjectPhasePermissionModal from "./components/ProjectPhasePermissionModal";
import ProfileEditorModal, {
  ProfileEditorValues,
} from "./components/ProfileEditorModal";
import ProjectPhaseSwitcher from "./components/ProjectPhaseSwitcher";
import ProjectFlowModeSwitch, {
  ProjectFlowViewMode,
} from "./components/ProjectFlowModeSwitch";
import ProjectFlowViewStateOverlay from "./components/ProjectFlowViewStateOverlay";
import ProjectContentItemList, {
  collectProjectOverdueContentItems,
} from "./components/ProjectContentItemList";
import ProjectOverdueBadge from "./components/ProjectOverdueBadge";
import PersonalBoardPage from "./components/PersonalBoardPage";
import MarkdownRequirementEditor from "./components/MarkdownRequirementEditor";
import ProjectRequirementModal from "./components/ProjectRequirementModal";
import ProjectRequirementCardSummary from "./components/ProjectRequirementCardSummary";
import TaskCommentModal from "./components/TaskCommentModal";
import TaskConflictModal, {
  TaskConflictSnapshot,
} from "./components/TaskConflictModal";
import TaskNodeDetailModal from "./components/TaskNodeDetailModal";
import TaskEditorModal from "./components/TaskEditorModal";
import TaskPriorityTag, {
  TaskPriority,
} from "./components/TaskPriorityTag";
import TaskLeaderboard, {
  WeeklyTaskRanking,
} from "./components/TaskLeaderboard";
import {
  TaskSubtaskDraft,
  TaskSubtaskItem,
} from "./components/TaskSubtaskManager";
import { getTaskSubtaskContentAssignees } from "./taskSubtaskContentAssignees";
import { normalizeProjectRequirementMarkdown } from "./projectRequirement";
import ProjectTimelineView, {
  ProjectTimelineTask,
} from "./components/ProjectTimelineView";
import ProjectTaskMatrixView, {
  ProjectTaskMatrixTask,
} from "./components/ProjectTaskMatrixView";
import ProjectTaskOverviewModal, {
  ProjectTaskOverviewTask,
} from "./components/ProjectTaskOverviewModal";
import MemberDashboard from "./MemberDashboard";
import { createRequestLock } from "./requestLock";
import {
  PROJECT_PHASE_LABELS,
  PROJECT_PHASES,
  PROJECT_PROGRESS_STEP_NAMES,
  ProjectFlowLayouts,
  ProjectPhase,
  ProjectPhasePermissions,
  ProjectPhaseSummaries,
  ProjectProgressStepStatus,
  canArchiveByPhaseSummaries,
  createEmptyPhaseSummaries,
  getOngoingProjects,
  getProjectProgressStep,
  getProjectProgressStepStatuses,
  getProjectTaskListBody,
  normalizeProjectPhaseRuntimeFields,
} from "./projectPhase";
import {
  getTaskAssigneeKey,
  taskHasAssignee,
  uniqueAssignees,
} from "./taskAssignees";
import { getFocusedReadyTask, getReadyTasks } from "./taskGraph";
import { getTaskOverdueDays } from "./taskDue";
import {
  isContentItemActiveInRange,
  selectMemberWeeklyContentItems,
  selectProjectWeeklyContentItems,
} from "./memberWeeklyContent";
import { selectMemberCalendarContentItems } from "./memberCalendarContent";
import { getDashboardProjectListBody } from "./dashboardProjectScope";
import { searchProjectOverviewProjects } from "./projectOverviewSearch";
import { useDebouncedValue } from "./useDebouncedValue";
import {
  buildProjectMemberAddOptions,
  buildProjectTaskParticipantOptions,
} from "./projectMemberCandidates";
import {
  getTaskMessageActionText,
  getTaskMessageFlowTarget,
  isSubtaskReviewRequestMessage,
  isTaskNodeMessage,
} from "./messageNavigation";
import {
  ProjectFlowNodeLock,
  getRemoteProjectFlowNodeLocks,
} from "./projectFlowAwareness";
import {
  canStartProjectFlowCollaboration,
  loadStableProjectFlowSnapshot,
  shouldFinishProjectFlowLoading,
  shouldRefreshUnreadTaskComments,
} from "./projectFlowSnapshot";
import "./Index.css";

type DashboardView =
  | "workspace"
  | "board"
  | "personalBoard"
  | "profile"
  | "projectFlow";
type ProjectFlowPanelTab =
  | "logs"
  | "announcements"
  | "attachments"
  | "members";
type UserType = "team_admin" | "user";
type RoutePath = string;

interface ProjectFlowCollaborator {
  clientId: number;
  name: string;
  userId: string;
}

interface ApiProjectMember {
  user_id: string;
  role: string;
  joined_at: number;
}

interface ApiProjectMemberProfile {
  username: string;
  nickname: string;
}

interface ApiProjectAttachment {
  attachment_id: string;
  folder_id?: string;
  name: string;
  size: number;
  type: string;
  url: string;
  uploaded_by: string;
  uploaded_at: number;
}

interface ApiProjectAttachmentFolder {
  folder_id: string;
  name: string;
  system?: boolean;
}

interface ApiProjectItem {
  id?: string;
  project_id: string;
  project_name: string;
  template: "phased" | "single";
  creator: string;
  team_code: string;
  team_admin_id?: string;
  normalized_project_name?: string;
  members: ApiProjectMember[];
  member_profiles?: ApiProjectMemberProfile[];
  avatar?: string;
  status: "planning" | "active" | "blocked" | "done";
  task_summary?: {
    total: number;
    completed: number;
    pending: number;
  };
  phase_permissions: ProjectPhasePermissions;
  phase_summaries: ProjectPhaseSummaries;
  flow_layouts: ProjectFlowLayouts;
  flow_graph_versions: Record<ProjectPhase, number>;
  remark?: string;
  created_at?: number;
  updated_at?: number;
  attachments?: ApiProjectAttachment[];
  attachment_folders?: ApiProjectAttachmentFolder[];
}

interface ApiTaskItem {
  id?: string;
  project_id: string;
  phase: ProjectPhase;
  creator: string;
  assignees: string[];
  predecessor_ids: string[];
  title: string;
  content?: string;
  priority?: "low" | "medium" | "high" | "urgent";
  tags?: string[];
  attachments?: any[];
  due_time?: number;
  end_time?: number;
  created_at?: number;
  updated_at?: number;
  comments?: ApiTaskComment[];
  status: "todo" | "doing" | "blocked" | "review" | "done" | "discarded";
  discarded_reason?: string;
  revision: number;
  subtask_summary: {
    total: number;
    completed: number;
    reviewing: number;
    progress: number;
    overdue_content_count?: number;
    next_content_due_time?: number;
    assignees: string[];
    pending_reviewers: string[];
    items?: Array<{
      id?: string;
      title: string;
      status: "todo" | "transfer" | "review" | "rejected" | "done";
      assignees: string[];
      transferees?: string[];
      reviewers: string[];
      content_items: Array<{
        item_id: string;
        assignees: string[];
        content: string;
        completed: boolean;
        start_time: number;
        due_time: number;
        priority: "low" | "medium" | "high" | "urgent";
      }>;
      start_time: number;
      due_time: number;
      priority: "low" | "medium" | "high" | "urgent";
    }>;
  };
}

interface TaskUpdateDraft extends TaskConflictSnapshot {
  title: string;
  assignees: string[];
  content: string;
  priority: "low" | "medium" | "high" | "urgent";
  status: ApiTaskItem["status"];
  due_time: number;
  tags: string[];
  attachments: any[];
}

interface ApiTaskComment {
  comment_id: string;
  author: string;
  content: string;
  subtask_ids: string[];
  replies?: ApiTaskCommentReply[];
  created_at: number;
  updated_at: number;
}

interface ApiTaskCommentReply {
  reply_id: string;
  author: string;
  content: string;
  created_at: number;
  updated_at: number;
}

interface ApiProjectLogItem {
  id?: string;
  action: string;
  operator: string;
  project_id: string;
  target_type: "project" | "task" | "subtask" | "member" | "attachment" | "announcement";
  target_id: string;
  created_at: number;
  detail?: {
    before?: any;
    after?: any;
    diff?: any;
    phase?: ProjectPhase;
    phase_label?: string;
    discard_reason?: string;
  };
  remark?: string;
}

interface TeamMember {
  username?: string;
  nickname?: string;
  mail?: string;
  phone?: string;
  avatar?: string;
  department?: string[];
  team_code?: string;
  role?: number;
}

interface ProjectFormValues {
  name: string;
  members?: string[];
  template: "phased" | "single";
}

interface ProjectMemberAddFormValues {
  user_id: string;
}

interface DepartmentFormValues {
  name: string;
}

interface DepartmentEditFormValues {
  name: string;
}

interface MemberEditFormValues {
  department: string[];
  role: number;
}

interface PasswordFormValues {
  old_password: string;
  new_password: string;
  confirm_password: string;
}

interface TaskCreateFormValues {
  title: string;
  due_time?: any;
  priority?: ApiTaskItem["priority"];
  status?: ApiTaskItem["status"];
  tags?: string[];
}

interface ProjectAnnouncementFormValues {
  action_required?: boolean;
}

interface ApiMessageItem {
  id?: string;
  message_id: string;
  receiver_id: string;
  receiver_type: "team_admin" | "user";
  team_code?: string;
  action_required: boolean;
  action_status: "none" | "pending" | "confirmed" | "ignored" | "expired";
  read_at?: number;
  created_at: number;
  updated_at: number;
  message?: {
    message_id: string;
    type: string;
    title: string;
    content: string;
    sender_id: string;
    sender_type: "team_admin" | "user" | "system";
    receiver_scope: "all" | "team" | "users" | "project_members";
    project_id?: string;
    task_id?: string;
    target_type?: "project" | "task" | "member" | "system";
    target_id?: string;
    created_at: number;
    payload?: {
      project_id?: string;
      project_name?: string;
      [key: string]: any;
    };
  };
}

interface ApiWeeklyStatus {
  title: string;
  text: string;
  completed: number;
  pending: number;
  due_tasks?: Array<{
    title: string;
    project_name: string;
    due_time?: number;
    status: ApiTaskItem["status"];
  }>;
}

interface WeekPlanDisplayItem {
  id: string;
  project_id: string;
  title: string;
  context: string;
  assignees: string[];
  start_time?: number;
  due_time?: number;
  priority?: TaskPriority;
  status: ApiTaskItem["status"];
  is_content_item: boolean;
}

interface MemberCalendarDisplayItem {
  id: string;
  project_id: string;
  phase: ProjectPhase;
  subtask_title: string;
  title: string;
  start_time: number;
  due_time: number;
  priority: TaskPriority;
  participant_role: "assignee" | "reviewer";
  state: "pending" | "overdue" | "done";
}

const MESSAGE_POLL_MIN_MS = 30 * 1000;
const MESSAGE_POLL_MAX_MS = 60 * 1000;
const DASHBOARD_POLL_MIN_MS = 30 * 1000;
const DASHBOARD_POLL_MAX_MS = 60 * 1000;
const PROJECT_FLOW_POLL_MIN_MS = 30 * 1000;
const PROJECT_FLOW_POLL_MAX_MS = 60 * 1000;
const PROJECT_FLOW_POSITION_LIMIT = 20000;

const viewRouteMap: Record<Exclude<DashboardView, "projectFlow">, RoutePath> = {
  workspace: "/workspace",
  board: "/projects",
  personalBoard: "/personal-board",
  profile: "/profile",
};

const memberViewRouteMap: Record<Exclude<DashboardView, "projectFlow">, RoutePath> = {
  workspace: "/member/workspace",
  board: "/member/projects",
  personalBoard: "/personal-board",
  profile: "/member/profile",
};

const routeViewMap: Record<RoutePath, DashboardView> = {
  "/workspace": "workspace",
  "/projects": "board",
  "/profile": "profile",
  "/member/workspace": "workspace",
  "/member/projects": "board",
  "/personal-board": "personalBoard",
  "/member/profile": "profile",
};

function getProjectIdFromPath(path: string) {
  const match = path.match(/^\/projects\/([^/]+)\/?$/);
  return match ? decodeURIComponent(match[1]) : "";
}

function getViewFromPath(path: string): DashboardView {
  if (getProjectIdFromPath(path)) {
    return "projectFlow";
  }
  return routeViewMap[path] || "workspace";
}

const projectStepNames = PROJECT_PROGRESS_STEP_NAMES;
const PROJECT_FLOW_LAYOUT_VERSION = 5;

const flowTaskStatusText: Record<ApiTaskItem["status"], string> = {
  todo: "待处理",
  doing: "进行中",
  blocked: "阻塞",
  review: "评审中",
  done: "已完成",
  discarded: "已废弃",
};
const flowTaskPriorityText: Record<NonNullable<ApiTaskItem["priority"]>, string> = {
  low: "低",
  medium: "中",
  high: "高",
  urgent: "紧急",
};
const FlowHandle = ReactFlowLib.Handle;
const FlowMarkerType = ReactFlowLib.MarkerType;
const FlowPosition = ReactFlowLib.Position;
moment.locale("zh-cn");

export function ProjectFlowProjectNode({ data }: any) {
  return (
    <div className="projectFlowProjectNode">
      <FlowHandle type="source" position={FlowPosition.Bottom} />
      <div className="projectFlowProjectNode__header">
        <span className="projectFlowProjectNode__icon">
          <ProjectOutlined />
        </span>
        <Tag>{data.phaseLabel}</Tag>
      </div>
      <span className="projectFlowProjectNode__eyebrow">项目总节点</span>
      <strong>{data.name}</strong>
      <div className="projectFlowProjectNode__footer">
        <span>当前阶段</span>
        <small>{data.taskCount} 个任务节点</small>
      </div>
    </div>
  );
}

export function ProjectFlowTaskNode({ data }: any) {
  const assignees =
    data.assignees ||
    (data.assigneeNames || []).map((name: string, index: number) => ({
      userId: `${name}-${index}`,
      name,
      isSelf: false,
    }));
  const handleAction = (
    event: React.MouseEvent<HTMLButtonElement>,
    action: "edit" | "delete"
  ) => {
    event.stopPropagation();
    data.onAction?.(action);
  };
  const handleSelect = () => {
    data.onSelect?.();
  };
  const handleComment = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    data.onComment?.();
  };

  return (
    <div
      className={`projectFlowTaskNode projectFlowTaskNode--${data.status} projectFlowTaskNode--priority-${data.priority} ${
        data.isCurrent ? "projectFlowTaskNode--current" : ""
      } ${data.isOverdue ? "projectFlowTaskNode--overdue" : ""} ${
        data.isOverdue && data.isCurrent
          ? "projectFlowTaskNode--overdueCurrent"
          : ""
      } ${data.lockedBy ? "projectFlowTaskNode--remoteLocked" : ""}`}
      onClick={handleSelect}
    >
      {data.isCurrent && (
        <>
          <div className="projectFlowTaskNode__borderPulse">
            <span />
            <span />
            <span />
            <span />
          </div>
          <div className="projectFlowTaskNode__liveRibbon">
            {data.currentLabel}
          </div>
          <div className="projectFlowTaskNode__liveIcon">
            <ThunderboltOutlined />
          </div>
          <div className="projectFlowTaskNode__liveDot" />
        </>
      )}
      {data.unreadCommentCount > 0 && (
        <div className="projectFlowTaskNode__commentUnread">
          {data.unreadCommentCount} 条未读批注
        </div>
      )}
      {data.lockedBy && (
        <div className="projectFlowTaskNode__remoteLock">
          {data.lockedBy} 正在移动
        </div>
      )}
      {data.canComment && (
        <button
          className="projectFlowTaskNode__commentButton"
          onClick={handleComment}
          title="新增节点批注"
          type="button"
        >
          <CommentOutlined />
        </button>
      )}
      <FlowHandle type="target" position={FlowPosition.Top} />
      <FlowHandle type="source" position={FlowPosition.Bottom} />
      <div
        className={`projectFlowTaskNode__statusBand projectFlowTaskNode__statusBand--${data.status} ${
          data.isOverdue ? "projectFlowTaskNode__statusBand--overdue" : ""
        }`}
      >
        <span>
          {data.isOverdue ? (
            <>
              <WarningOutlined className="projectFlowTaskNode__overdueIcon" />
              <b className="projectFlowTaskNode__overdueLabel">
                逾期 {data.overdueDays} 天
              </b>
              <strong>
                {flowTaskStatusText[data.status as ApiTaskItem["status"]]}
              </strong>
            </>
          ) : (
            <>
              <i />
              {flowTaskStatusText[data.status as ApiTaskItem["status"]]}
              {data.isCurrent && <strong>· {data.currentLabel}</strong>}
            </>
          )}
        </span>
        <Tag
          className={`projectFlowTaskNode__priorityTag projectFlowTaskNode__priorityTag--${data.priority}`}
        >
          {flowTaskPriorityText[
            data.priority as NonNullable<ApiTaskItem["priority"]>
          ]}
          优先级
        </Tag>
      </div>
      <div className="projectFlowTaskNode__body">
        <div className="projectFlowTaskNode__heading">
          <strong>{data.title}</strong>
          {data.isAssignedToCurrentUser && (
            <span className="projectFlowTaskNode__mineBadge">
              <UserOutlined />
              我负责
            </span>
          )}
        </div>
        <div className="projectFlowTaskNode__summary">
          {data.contentSummary || "暂无内容摘要"}
        </div>
        <div className="projectFlowTaskNode__assignees">
          {assignees.map((assignee: any) => (
            <Tag
              className={
                assignee.isSelf
                  ? "projectFlowTaskNode__assigneeTag--self"
                  : ""
              }
              key={assignee.userId}
              title={assignee.isSelf ? `${assignee.name}（我）` : assignee.name}
            >
              {assignee.isSelf && <UserOutlined />}
              {assignee.name}
              {assignee.isSelf ? "（我）" : ""}
            </Tag>
          ))}
        </div>
        {data.imageCount > 0 && (
          <small className="projectFlowTaskNode__media">
            含 {data.imageCount} 张图片
          </small>
        )}
        <div className="projectFlowTaskNode__footer">
          <small
            className={`projectFlowTaskNode__due ${
              data.isOverdue ? "projectFlowTaskNode__due--overdue" : ""
            }`}
          >
            {data.isOverdue && <ClockCircleOutlined />}
            {data.isOverdue ? "截止" : "预计"}{" "}
            {data.dueTimeText || "未设置"}
          </small>
          {data.commentCount > 0 && (
            <small className="projectFlowTaskNode__commentSummary">
              {data.commentCount} 条批注
            </small>
          )}
        </div>
        {data.isCurrent && <div className="projectFlowTaskNode__liveTrack" />}
        {data.canManage && (
          <div className="projectFlowTaskNode__actions">
            <button onClick={(event) => handleAction(event, "edit")} type="button">
              编辑
            </button>
            <button onClick={(event) => handleAction(event, "delete")} type="button">
              删除
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

const Home: FC = () => {
  const [isLogin, setIsLogin] = useState<boolean>(false);
  const [userType, setUserType] = useState<UserType>("user");
  const [userId, setUserId] = useState<string>("");
  const [userMail, setUserMail] = useState<string>("");
  const [userPhone, setUserPhone] = useState<string>("");
  const [userAvatar, setUserAvatar] = useState<string>("");
  const [nickname, setNickname] = useState<string>("");
  const [department, setDepartment] = useState<string[]>([]);
  const [userRole, setUserRole] = useState<number>(0);
  const [teamCode, setTeamCode] = useState<string>("");
  const [activeView, setActiveView] = useState<DashboardView>(() =>
    getViewFromPath(window.location.pathname)
  );
  const [apiProjects, setApiProjects] = useState<ApiProjectItem[]>([]);
  const [teamTasks, setTeamTasks] = useState<ApiTaskItem[]>([]);
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [teamDepartmentOptions, setTeamDepartmentOptions] = useState<string[]>([]);
  const [editingDepartment, setEditingDepartment] = useState<string>("");
  const [editingMember, setEditingMember] = useState<TeamMember | null>(null);
  const [selectedMemberCalendar, setSelectedMemberCalendar] =
    useState<TeamMember | null>(null);
  const [memberCalendarTasks, setMemberCalendarTasks] = useState<
    Record<string, ApiTaskItem[]>
  >({});
  const [memberCalendarLoading, setMemberCalendarLoading] = useState(false);
  const [weekPlanTasks, setWeekPlanTasks] = useState<ApiTaskItem[]>([]);
  const [weekPlanLoading, setWeekPlanLoading] = useState(false);
  const [weekPlanLoaded, setWeekPlanLoaded] = useState(false);
  const [deletingMember, setDeletingMember] = useState<TeamMember | null>(null);
  const [deletingDepartment, setDeletingDepartment] = useState<string>("");
  const [departmentDeleting, setDepartmentDeleting] = useState(false);
  const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);
  const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);
  const [dashboardLoading, setDashboardLoading] = useState(false);
  const [projectOverviewSearchText, setProjectOverviewSearchText] =
    useState("");
  const debouncedProjectOverviewSearchText = useDebouncedValue(
    projectOverviewSearchText
  );
  const [isProjectModalOpen, setIsProjectModalOpen] = useState(false);
  const [editingProject, setEditingProject] =
    useState<ApiProjectItem | null>(null);
  const [deletingProject, setDeletingProject] = useState<ApiProjectItem | null>(
    null
  );
  const [isDepartmentModalOpen, setIsDepartmentModalOpen] = useState(false);
  const [isMessageDrawerOpen, setIsMessageDrawerOpen] = useState(false);
  const [selectedMessage, setSelectedMessage] = useState<ApiMessageItem | null>(
    null
  );
  const [pendingTaskCommentFocus, setPendingTaskCommentFocus] = useState<{
    projectId: string;
    taskId: string;
    phase?: ProjectPhase;
    parentTaskId?: string;
    subtaskId?: string;
    contentItemId?: string;
    commentId?: string;
  } | null>(null);
  const [unreadTaskCommentMap, setUnreadTaskCommentMap] = useState<
    Record<string, ApiMessageItem[]>
  >({});
  const [flowProjectId, setFlowProjectId] = useState<string>(() =>
    getProjectIdFromPath(window.location.pathname)
  );
  const [flowProjectSnapshot, setFlowProjectSnapshot] =
    useState<ApiProjectItem | null>(null);
  const [flowTaskSnapshot, setFlowTaskSnapshot] = useState<ApiTaskItem[]>([]);
  const [activeProjectPhase, setActiveProjectPhase] =
    useState<ProjectPhase>("initiation");
  const [projectPhaseUpdating, setProjectPhaseUpdating] = useState(false);
  const [pendingProjectPhasePermission, setPendingProjectPhasePermission] =
    useState<{
      projectId: string;
      phase: ProjectPhase;
      editable: boolean;
    } | null>(null);
  const [creatingTaskProject, setCreatingTaskProject] = useState<{
    project: ApiProjectItem;
  } | null>(null);
  const [pendingTaskConnection, setPendingTaskConnection] = useState<{
    predecessor_ids: string[];
    position?: { x: number; y: number };
  } | null>(null);
  const [editingTaskNode, setEditingTaskNode] = useState<ApiTaskItem | null>(null);
  const [creatingTaskSubtasks, setCreatingTaskSubtasks] = useState<
    TaskSubtaskItem[]
  >([]);
  const [taskSubtaskEditing, setTaskSubtaskEditing] = useState(false);
  const [taskConflict, setTaskConflict] = useState<{
    baseTask: ApiTaskItem;
    serverTask: ApiTaskItem;
    draft: TaskUpdateDraft;
    cascade: boolean;
    discardReason?: string;
  } | null>(null);
  const [pendingTaskLifecycle, setPendingTaskLifecycle] = useState<{
    kind: "discard" | "restore";
    values: TaskCreateFormValues;
  } | null>(null);
  const [taskDiscardReason, setTaskDiscardReason] = useState("");
  const [taskConflictSaving, setTaskConflictSaving] = useState(false);
  const [isProjectFlowFullscreen, setIsProjectFlowFullscreen] = useState(false);
  const [isProjectTaskOverviewOpen, setIsProjectTaskOverviewOpen] =
    useState(false);
  const [projectTaskOverviewLoading, setProjectTaskOverviewLoading] =
    useState(false);
  const [projectTaskOverviewTasks, setProjectTaskOverviewTasks] = useState<
    Record<ProjectPhase, ApiTaskItem[]>
  >(() =>
    PROJECT_PHASES.reduce((result, phase) => {
      result[phase] = [];
      return result;
    }, {} as Record<ProjectPhase, ApiTaskItem[]>)
  );
  const [isProjectFlowArrangeMode, setIsProjectFlowArrangeMode] = useState(false);
  const [projectFlowRenderMode, setProjectFlowRenderMode] = useState<
    "matrix" | "timeline"
  >("matrix");
  const [projectFlowNodes, setProjectFlowNodes] = useState<any[]>([]);
  const [projectFlowEdges, setProjectFlowEdges] = useState<any[]>([]);
  const [projectFlowRenderMeta, setProjectFlowRenderMeta] = useState({
    memberCount: 0,
    taskMemberCount: 0,
    taskCount: 0,
  });
  const [projectFlowLoading, setProjectFlowLoading] = useState(false);
  const [projectFlowLoadFailed, setProjectFlowLoadFailed] = useState(false);
  const [projectLogList, setProjectLogList] = useState<ApiProjectLogItem[]>([]);
  const [projectLogLoading, setProjectLogLoading] = useState(false);
  const [selectedProjectLog, setSelectedProjectLog] =
    useState<ApiProjectLogItem | null>(null);
  const [projectAnnouncementList, setProjectAnnouncementList] = useState<
    ApiMessageItem[]
  >([]);
  const [projectAnnouncementLoading, setProjectAnnouncementLoading] =
    useState(false);
  const [projectFlowPanelTab, setProjectFlowPanelTab] =
    useState<ProjectFlowPanelTab>("announcements");
  const [removingProjectMember, setRemovingProjectMember] =
    useState<ApiProjectMember | null>(null);
  const [isProjectMemberAddModalOpen, setIsProjectMemberAddModalOpen] =
    useState(false);
  const [confirmingTaskDelete, setConfirmingTaskDelete] =
    useState<ApiTaskItem | null>(null);
  const [cascadeDelayConfirm, setCascadeDelayConfirm] = useState<{
    days: number;
    scope: "main";
    taskTitle: string;
    onSave: (cascade: boolean) => Promise<boolean>;
  } | null>(null);
  const [cascadeDelaySaving, setCascadeDelaySaving] = useState(false);
  const [removingProjectAttachment, setRemovingProjectAttachment] =
    useState<ApiProjectAttachment | null>(null);
  const [deletingTaskComment, setDeletingTaskComment] = useState<{
    comment: ApiTaskComment;
    task: ApiTaskItem;
  } | null>(null);
  const [isProjectFlowPanelCollapsed, setIsProjectFlowPanelCollapsed] =
    useState(false);
  const [isAnnouncementModalOpen, setIsAnnouncementModalOpen] = useState(false);
  const [announcementContentHtml, setAnnouncementContentHtml] = useState("");
  const [announcementReceivers, setAnnouncementReceivers] = useState<string[]>([]);
  const [announcementReceiverPicker, setAnnouncementReceiverPicker] = useState<
    string | undefined
  >(undefined);
  const [announcementReceiverSelectKey, setAnnouncementReceiverSelectKey] =
    useState(0);
  const [selectedTaskNode, setSelectedTaskNode] = useState<ApiTaskItem | null>(null);
  const [selectedTaskSubtasks, setSelectedTaskSubtasks] = useState<TaskSubtaskItem[]>([]);
  const [selectedTaskSubtasksLoading, setSelectedTaskSubtasksLoading] = useState(false);
  const [focusedTaskSubtaskId, setFocusedTaskSubtaskId] = useState("");
  const [focusedTaskContentItemId, setFocusedTaskContentItemId] = useState("");
  const [editingTaskSubtasks, setEditingTaskSubtasks] = useState<TaskSubtaskItem[]>([]);
  const [editingTaskSubtasksLoading, setEditingTaskSubtasksLoading] = useState(false);
  const [commentingTaskNode, setCommentingTaskNode] =
    useState<ApiTaskItem | null>(null);
  const [taskCommentHtml, setTaskCommentHtml] = useState("");
  const [taskCommentSubtaskIds, setTaskCommentSubtaskIds] = useState<string[]>(
    []
  );
  const [editingTaskComment, setEditingTaskComment] =
    useState<ApiTaskComment | null>(null);
  const [previewImage, setPreviewImage] = useState("");
  const [isProjectRequirementOpen, setIsProjectRequirementOpen] =
    useState(false);
  const [, setProjectFlowSaveStatus] = useState<
    "idle" | "saving" | "saved"
  >("idle");
  const [projectFlowCollabStatus, setProjectFlowCollabStatus] = useState<
    "idle" | "connecting" | "connected" | "disconnected"
  >("idle");
  const [projectFlowCollaborators, setProjectFlowCollaborators] = useState<
    ProjectFlowCollaborator[]
  >([]);
  const [projectFlowRemoteNodeLocks, setProjectFlowRemoteNodeLocks] = useState<
    Record<string, ProjectFlowNodeLock>
  >({});
  const [taskTagInput, setTaskTagInput] = useState("");
  const [taskDraftTags, setTaskDraftTags] = useState<string[]>([]);
  const [messageLoading, setMessageLoading] = useState(false);
  const [unreadMessageCount, setUnreadMessageCount] = useState(0);
  const [messageList, setMessageList] = useState<ApiMessageItem[]>([]);
  const [weeklyStatus, setWeeklyStatus] = useState<ApiWeeklyStatus>({
    title: "本周任务进度",
    text: "正在同步本周任务状态。",
    completed: 0,
    pending: 0,
    due_tasks: [],
  });
  const [weeklyRanking, setWeeklyRanking] =
    useState<WeeklyTaskRanking | null>(null);
  const [activeAttachmentFolderId, setActiveAttachmentFolderId] = useState("");
  const [mutationPendingKeys, setMutationPendingKeys] = useState<Set<string>>(
    () => new Set()
  );
  const [projectAvatar, setProjectAvatar] = useState<string>("");
  const [projectRequirementHtml, setProjectRequirementHtml] =
    useState<string>("");
  const [projectForm] = Form.useForm<ProjectFormValues>();
  const [projectMemberAddForm] = Form.useForm<ProjectMemberAddFormValues>();
  const [departmentForm] = Form.useForm<DepartmentFormValues>();
  const [departmentEditForm] = Form.useForm<DepartmentEditFormValues>();
  const [memberEditForm] = Form.useForm<MemberEditFormValues>();
  const [passwordForm] = Form.useForm<PasswordFormValues>();
  const [taskEditorForm] = Form.useForm<TaskCreateFormValues>();
  const [announcementForm] = Form.useForm<ProjectAnnouncementFormValues>();
  const projectFlowSaveTimer = useRef<number | undefined>();
  const projectFlowCanvasRef = useRef<HTMLDivElement | null>(null);
  const projectFlowInstanceRef = useRef<any>(null);
  const projectFlowDraggingRef = useRef(false);
  const projectFlowDraggingNodeIdsRef = useRef<Set<string>>(new Set());
  const projectFlowNodesRef = useRef<any[]>([]);
  const projectFlowAutoRefreshRef = useRef(false);
  const projectFlowPanelTabRef = useRef<ProjectFlowPanelTab>("announcements");
  const mutationLockRef = useRef<ReturnType<typeof createRequestLock>>();
  if (!mutationLockRef.current) {
    mutationLockRef.current = createRequestLock(setMutationPendingKeys);
  }
  const projectFlowGraphRefreshTimerRef = useRef<number | undefined>();
  const projectFlowPendingGraphRefreshRef = useRef(false);
  const projectFlowPendingGraphProjectIdRef = useRef("");
  const projectFlowGraphVersionRef = useRef(0);
  const taskDraftSubtaskIdRef = useRef(0);
  const projectFlowPendingGraphVersionRef = useRef(0);
  const projectFlowSnapshotRequestRef = useRef(0);
  const projectFlowLoadingRequestRef = useRef(0);
  const projectFlowCollabDocRef = useRef<any | null>(null);
  const projectFlowCollabProviderRef = useRef<any | null>(null);
  const activeProjectPhaseRef = useRef<ProjectPhase>("initiation");
  const selectedTaskNodeRef = useRef<ApiTaskItem | null>(null);
  const editingTaskNodeRef = useRef<ApiTaskItem | null>(null);
  const projectFlowCollabPositionsRef = useRef<
    Record<string, { x: number; y: number }>
  >({});
  const projectFlowCollabApplyingRef = useRef(false);
  const projectFlowRemoteNodeLocksRef = useRef<
    Record<string, ProjectFlowNodeLock>
  >({});
  const projectFlowDragClaimRef = useRef<{
    nodeId: string;
    readyAt: number;
  } | null>(null);
  const projectFlowDragSettleTimerRef = useRef<number | undefined>();
  const projectFlowCollabSyncTimerRef = useRef<number | undefined>();
  const projectFlowPendingSyncNodeIdsRef = useRef<Set<string>>(new Set());
  const projectFlowRemoteApplyTimerRef = useRef<number | undefined>();
  const announcementContentEditorRef = useRef<HTMLDivElement | null>(null);
  const taskCommentEditorRef = useRef<HTMLDivElement | null>(null);
  const departmentRef = useRef<string[]>([]);
  const isTeamAdmin = userType === "team_admin";
  const userAvatarUrl = resolveAssetURL(userAvatar);
  const currentRouteMap = isTeamAdmin ? viewRouteMap : memberViewRouteMap;
  const brandMark = isTeamAdmin ? "PM" : "MB";
  const brandTitle = isTeamAdmin ? "Project Flow" : "Member Flow";
  const brandSubtitle = isTeamAdmin ? "Team Management" : "Task Collaboration";
  const overviewProjects = apiProjects;
  const workspaceProjects = getOngoingProjects(overviewProjects);
  const workspaceProjectSearchResults = searchProjectOverviewProjects(
    workspaceProjects,
    teamTasks,
    debouncedProjectOverviewSearchText,
    getMemberDisplayName
  );
  const overviewTasks = teamTasks;
  const overviewTeamMembers = teamMembers;
  const flowProject =
    flowProjectSnapshot?.project_id === flowProjectId
      ? flowProjectSnapshot
      : overviewProjects.find((project) => project.project_id === flowProjectId) ||
        null;
  const activeProjectViewLabel =
    flowProject?.template === "single"
      ? "任务视图"
      : PROJECT_PHASE_LABELS[activeProjectPhase];
  const projectFlowTimelineTasks = flowTaskSnapshot.filter(
    (task) =>
      task.project_id === flowProjectId && task.phase === activeProjectPhase
  );
  const isActiveFlowProjectParticipant = !!flowProject && (
    isTeamAdmin ||
    flowProject.creator === userId ||
    flowProject.members.some((member) => member.user_id === userId)
  );
  const canManageProjectPhases = !!flowProject && (
    isTeamAdmin ||
    (userRole === 1 &&
      flowProject.members.some((member) => member.user_id === userId))
  );
  const isCurrentProjectPhaseEditable = !!(
    isActiveFlowProjectParticipant &&
    (flowProject?.template === "single" ||
      flowProject?.phase_permissions[activeProjectPhase])
  );
  const canManageCurrentPhaseContentStatus = !!(
    flowProject &&
    (isTeamAdmin || userRole === 1) &&
    (flowProject.template === "single" ||
      flowProject.phase_permissions[activeProjectPhase])
  );
  const isProjectFlowViewBlocked =
    projectFlowLoading || projectFlowLoadFailed;
  const activeProjects = workspaceProjects;
  const projectMemberOptions = teamMembers
    .filter((member) => !!member.username)
    .map((member) => ({
      label: `${member.nickname || member.username}（${member.username}）`,
      value: member.username || "",
    }));
  const canManageFlowProjectMembers =
    !!flowProject &&
    (isTeamAdmin ||
      flowProject.creator === userId ||
      (userRole === 1 &&
        flowProject.members.some((member) => member.user_id === userId)));
  const projectMemberAddOptions = flowProject
    ? buildProjectMemberAddOptions(flowProject, teamMembers)
    : [];
  const departmentMemberGroups = teamDepartmentOptions.map((departmentName) => ({
    departmentName,
    members: teamMembers.filter((member) =>
      (member.department || []).includes(departmentName)
    ),
  }));
  const ungroupedMembers = teamMembers.filter(
    (member) => !(member.department || []).length
  );

  function getNextMessagePollDelay() {
    return (
      MESSAGE_POLL_MIN_MS +
      Math.floor(Math.random() * (MESSAGE_POLL_MAX_MS - MESSAGE_POLL_MIN_MS))
    );
  }

  function getNextDashboardPollDelay() {
    return (
      DASHBOARD_POLL_MIN_MS +
      Math.floor(
        Math.random() * (DASHBOARD_POLL_MAX_MS - DASHBOARD_POLL_MIN_MS)
      )
    );
  }

  function getNextProjectFlowPollDelay() {
    return (
      PROJECT_FLOW_POLL_MIN_MS +
      Math.floor(
        Math.random() * (PROJECT_FLOW_POLL_MAX_MS - PROJECT_FLOW_POLL_MIN_MS)
      )
    );
  }

  function getProjectFlowCollabServerUrl() {
    return (
      process.env.REACT_APP_COLLAB_BASE_URL ||
      baseURL.replace(/^http/i, "ws")
    ).replace(/\/$/, "");
  }

  function normalizeNodePosition(position: any) {
    const x = Number(position?.x);
    const y = Number(position?.y);
    if (!Number.isFinite(x) || !Number.isFinite(y)) {
      return null;
    }
    if (
      Math.abs(x) > PROJECT_FLOW_POSITION_LIMIT ||
      Math.abs(y) > PROJECT_FLOW_POSITION_LIMIT
    ) {
      return null;
    }
    return {
      x: Math.round(x),
      y: Math.round(y),
    };
  }

  function isProjectFlowTaskLayoutNode(node: any) {
    return (
      node?.type === "taskNode" &&
      node.id?.startsWith("task-")
    );
  }

  function applyProjectFlowCollabPositions(nodes: any[]) {
    const positions = projectFlowCollabPositionsRef.current;
    if (!Object.keys(positions).length) {
      return nodes;
    }
    const nextNodes = nodes.map((node) =>
      isProjectFlowTaskLayoutNode(node) && positions[node.id]
        ? {
            ...node,
            position: positions[node.id],
          }
        : node
    );
    return nextNodes;
  }

  function syncProjectFlowPositionsToY(nodes: any[], nodeIds?: string[]) {
    const doc = projectFlowCollabDocRef.current;
    if (!doc || projectFlowCollabApplyingRef.current) {
      return;
    }
    const positionMap = doc.getMap("nodePositions");
    const idSet = nodeIds?.length ? new Set(nodeIds) : null;
    doc.transact(() => {
      nodes.forEach((node) => {
        if (!isProjectFlowTaskLayoutNode(node) || (idSet && !idSet.has(node.id))) {
          return;
        }
        const position = normalizeNodePosition(node.position);
        if (!position) {
          return;
        }
        projectFlowCollabPositionsRef.current[node.id] = position;
        positionMap.set(node.id, position);
      });
    }, "local-layout");
  }

  function flushProjectFlowPendingPositionSync() {
    if (projectFlowCollabSyncTimerRef.current) {
      window.clearTimeout(projectFlowCollabSyncTimerRef.current);
      projectFlowCollabSyncTimerRef.current = undefined;
    }
    const nodeIds = Array.from(projectFlowPendingSyncNodeIdsRef.current);
    projectFlowPendingSyncNodeIdsRef.current.clear();
    if (!nodeIds.length) {
      return;
    }
    syncProjectFlowPositionsToY(projectFlowNodesRef.current, nodeIds);
  }

  function setLocalProjectFlowDraggingNode(nodeId?: string) {
    const provider = projectFlowCollabProviderRef.current;
    if (!provider) return;
    provider.awareness.setLocalStateField(
      "dragging",
      nodeId ? { nodeId, startedAt: Date.now() } : null
    );
  }

  function clearProjectFlowDragClaim() {
    if (projectFlowDragSettleTimerRef.current) {
      window.clearTimeout(projectFlowDragSettleTimerRef.current);
      projectFlowDragSettleTimerRef.current = undefined;
    }
    projectFlowDragClaimRef.current = null;
  }

  function runProjectFlowGraphRefresh(projectId: string) {
    if (!projectId) {
      return;
    }
    if (projectFlowGraphRefreshTimerRef.current) {
      window.clearTimeout(projectFlowGraphRefreshTimerRef.current);
    }
    projectFlowGraphRefreshTimerRef.current = window.setTimeout(async () => {
      projectFlowGraphRefreshTimerRef.current = undefined;
      const refreshResult = await fetchProjectFlowSnapshot(projectId, {
        silent: true,
        showLoading: false,
      });
      if (refreshResult.success) {
        projectFlowGraphVersionRef.current = Math.max(
          projectFlowGraphVersionRef.current,
          refreshResult.graphVersion
        );
        // Only the version returned by the authenticated project snapshot is trusted.
        if (
          refreshResult.graphVersion >=
          projectFlowPendingGraphVersionRef.current
        ) {
          projectFlowPendingGraphVersionRef.current = 0;
        } else {
          projectFlowGraphRefreshTimerRef.current = window.setTimeout(
            () => runProjectFlowGraphRefresh(projectId),
            1200
          );
        }
      } else if (
        projectFlowPendingGraphVersionRef.current >
        projectFlowGraphVersionRef.current
      ) {
        projectFlowGraphRefreshTimerRef.current = window.setTimeout(
          () => runProjectFlowGraphRefresh(projectId),
          1200
        );
      }
      const currentTab = projectFlowPanelTabRef.current;
      if (currentTab === "announcements") {
        await fetchProjectAnnouncements(projectId, {
          silent: true,
          showLoading: false,
        });
      }
      if (currentTab === "logs") {
        await fetchProjectLogs(projectId, {
          silent: true,
          showLoading: false,
        });
      }
    }, 120);
  }

  function queueProjectFlowGraphRefresh(projectId: string) {
    if (!projectId) {
      return;
    }
    runProjectFlowGraphRefresh(projectId);
  }

  function getMemberRoleText(role?: number) {
    return role === 1 ? "管理员" : "成员";
  }

  function normalizeDepartmentList(nextDepartment: string[] = []) {
    return Array.from(
      new Set(
        nextDepartment
          .map((item) => String(item || "").trim())
          .filter(Boolean)
      )
    );
  }

  function isSameStringList(prev: string[], next: string[]) {
    return (
      prev.length === next.length &&
      prev.every((item, index) => item === next[index])
    );
  }

  const updateDepartmentState = useCallback((nextDepartment: string[] = []) => {
    const normalizedDepartment = normalizeDepartmentList(nextDepartment);
    departmentRef.current = normalizedDepartment;
    setDepartment((prev) =>
      isSameStringList(prev, normalizedDepartment) ? prev : normalizedDepartment
    );
  }, []);

  const hydrateAuthSession = useCallback((authSession: AuthSession | null) => {
    const currentUserId = authSession?.user?.username || "";
    setUserId(currentUserId);
    setUserMail(authSession?.user?.mail || "");
    setUserPhone(authSession?.user?.phone || "");
    setUserAvatar(authSession?.user?.avatar || "");
    setNickname(authSession?.user?.nickname || "");
    updateDepartmentState(authSession?.user?.department || []);
    setUserRole(authSession?.user?.role || 0);
    setTeamCode(authSession?.user?.team_code || authSession?.user?.link_code || "");
    setUserType(authSession?.user_type || "user");
    setIsLogin(!!currentUserId);
    return currentUserId;
  }, [updateDepartmentState]);

  const forceLogout = useCallback(
    (notice = "登录状态已失效，请重新登录") => {
      clearAuthStorage();
      hydrateAuthSession(null);
      setActiveView("workspace");
      setFlowProjectId("");
      setFlowProjectSnapshot(null);
      setFlowTaskSnapshot([]);
      setMessageList([]);
      setUnreadMessageCount(0);
      window.history.replaceState(null, "", "/");
      message.warning(notice);
    },
    [hydrateAuthSession]
  );

  useEffect(() => {
    const handleAuthInvalid = (event: Event) => {
      const detail = (event as CustomEvent<{ message?: string }>).detail;
      forceLogout(detail?.message || "登录状态已失效，请重新登录");
    };
    window.addEventListener(AUTH_INVALID_EVENT, handleAuthInvalid);
    return () => {
      window.removeEventListener(AUTH_INVALID_EVENT, handleAuthInvalid);
    };
  }, [forceLogout]);

  const refreshDashboardData = useCallback(
    async (options: { silent?: boolean } = {}) => {
      if (!isLogin || !userId) {
        return;
      }
      if (isTeamAdmin && !teamCode) {
        return;
      }

      if (!options.silent) {
        setDashboardLoading(true);
      }
      try {
        const api = createApi();
        if (isTeamAdmin) {
          const [projectRes, memberRes, departmentRes] = await Promise.all([
            api.post(
              "/project/list",
              getDashboardProjectListBody({
                isTeamAdmin,
                teamCode,
                userId,
                userRole,
              })
            ),
            api.post("/user/list", {
              team_code: teamCode,
              pageIndex: 0,
              pageSize: 1000,
            }),
            api.post("/user/department/list", {}),
          ]);

          if (projectRes.data.code === 0) {
            const projectList = (projectRes.data.data?.list || []).map(
              (project: ApiProjectItem) =>
                normalizeProjectPhaseRuntimeFields(project)
            );
            const activeProjectIds = projectList
              .filter(
                (project) => project.status !== "done"
              )
              .map((project) => project.project_id)
              .filter(Boolean);
            setApiProjects(projectList);
            if (activeProjectIds.length) {
              const taskRes = await api.post("/task/list", {
                project_ids: activeProjectIds,
                pageIndex: 0,
                pageSize: 1000,
              });
              if (taskRes.data.code === 0) {
                setTeamTasks(taskRes.data.data?.list || []);
              }
            } else {
              setTeamTasks([]);
            }
          }
          if (memberRes.data.code === 0) {
            setTeamMembers(memberRes.data.data?.list || []);
          }
          if (departmentRes.data.code === 0) {
            const list = departmentRes.data.data?.list || [];
            setTeamDepartmentOptions(list);
            updateDepartmentState(list);
          }
          return;
        }

        const [projectRes, memberRes] = await Promise.all([
          api.post(
            "/project/list",
            getDashboardProjectListBody({
              isTeamAdmin,
              teamCode,
              userId,
              userRole,
            })
          ),
          teamCode
            ? api.post("/user/list", {
                team_code: teamCode,
                pageIndex: 0,
                pageSize: 1000,
              })
            : Promise.resolve(null),
        ]);

        let projectList: ApiProjectItem[] = [];
        if (projectRes.data.code === 0) {
          projectList = (projectRes.data.data?.list || []).map(
            (project: ApiProjectItem) =>
              normalizeProjectPhaseRuntimeFields(project)
          );
          setApiProjects(projectList);
          const projectIds = projectList
            .map((project) => project.project_id)
            .filter(Boolean);
          if (projectIds.length) {
            const taskRes = await api.post("/task/list", {
              project_ids: projectIds,
              pageIndex: 0,
              pageSize: 1000,
            });
            if (taskRes.data.code === 0) {
              setTeamTasks(taskRes.data.data?.list || []);
            }
          } else {
            setTeamTasks([]);
          }
        }
        if (memberRes?.data?.code === 0) {
          setTeamMembers(memberRes.data.data?.list || []);
        }
      } catch {
        if (!options.silent) {
          message.error("工作台数据加载失败");
        }
      } finally {
        if (!options.silent) {
          setDashboardLoading(false);
        }
      }
    },
    [isLogin, isTeamAdmin, teamCode, updateDepartmentState, userId, userRole]
  );

  useEffect(() => {
    const authSession = getAuthSession();
    const currentUserId = hydrateAuthSession(authSession);
    const currentPath = window.location.pathname;
    const isKnownRoute =
      !!routeViewMap[currentPath] || !!getProjectIdFromPath(currentPath);
    if (currentUserId && !isKnownRoute) {
      const nextUserType = authSession?.user_type || "user";
      const nextRouteMap =
        nextUserType === "team_admin" ? viewRouteMap : memberViewRouteMap;
      window.history.replaceState(null, "", nextRouteMap.workspace);
    }
  }, [hydrateAuthSession]);

  useEffect(() => {
    if (isLogin && isTeamAdmin && teamCode) {
      refreshDashboardData();
    }
  }, [isLogin, isTeamAdmin, refreshDashboardData, teamCode]);

  useEffect(() => {
    if (isLogin && !isTeamAdmin && userId) {
      refreshDashboardData();
    }
  }, [isLogin, isTeamAdmin, refreshDashboardData, userId]);

  const fetchUnreadMessageCount = useCallback(async (options: { silent?: boolean } = {}) => {
    if (!isLogin || !userId) {
      return;
    }

    try {
      const { data } = await createApi().post("/message/unreadCount", {});
      if (data.code === 0) {
        setUnreadMessageCount(data.data?.count || 0);
        if (data.data?.weekly_status) {
          setWeeklyStatus(data.data.weekly_status);
        }
        if (data.data?.weekly_ranking) {
          setWeeklyRanking(data.data.weekly_ranking);
        }
        if (activeView === "projectFlow" && flowProjectId) {
          fetchUnreadTaskCommentMessages(flowProjectId);
        }
        const profile = data.data?.profile;
        if (profile?.user) {
          const nextUser = profile.user;
          setUserMail(nextUser.mail || "");
          setUserPhone(nextUser.phone || "");
          setUserAvatar(nextUser.avatar || "");
          setNickname(nextUser.nickname || "");
          updateDepartmentState(nextUser.department || []);
          setUserRole(nextUser.role || 0);
          setTeamCode(nextUser.team_code || nextUser.link_code || "");
          setUserType(profile.user_type || "user");
          saveAuthStorage({
            user: nextUser,
            user_type: profile.user_type || "user",
          });
        }
      } else if (!options.silent) {
        message.error(data.message || "获取未读消息失败");
      }
    } catch {
      if (!options.silent) {
        message.error("获取未读消息失败");
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeView, flowProjectId, isLogin, updateDepartmentState, userId]);

  async function fetchMessages() {
    if (!isLogin || !userId) {
      return;
    }

    setMessageLoading(true);
    try {
      const { data } = await createApi().post("/message/list", {
        pageIndex: 0,
        pageSize: 100,
      });
      if (data.code !== 0) {
        message.error(data.message || "获取消息失败");
        return;
      }
      setMessageList(data.data?.list || []);
    } catch {
      message.error("获取消息失败");
    } finally {
      setMessageLoading(false);
    }
  }

  async function fetchProjectAnnouncements(
    projectId: string,
    options: { silent?: boolean; showLoading?: boolean } = {}
  ) {
    if (!isLogin || !userId || !projectId) {
      return;
    }

    if (options.showLoading !== false) {
      setProjectAnnouncementLoading(true);
    }
    try {
      const { data } = await createApi().post("/message/list", {
        project_id: projectId,
        type: "project.announcement",
        pageIndex: 0,
        pageSize: 100,
      });
      if (data.code !== 0) {
        if (!options.silent) {
          message.error(data.message || "获取项目公告失败");
        }
        return;
      }
      setProjectAnnouncementList(data.data?.list || []);
    } catch {
      if (!options.silent) {
        message.error("获取项目公告失败");
      }
    } finally {
      if (options.showLoading !== false) {
        setProjectAnnouncementLoading(false);
      }
    }
  }

  async function createProjectAnnouncement(values: ProjectAnnouncementFormValues) {
    await runMutation("announcement:create", () =>
      performCreateProjectAnnouncement(values)
    );
  }

  async function performCreateProjectAnnouncement(
    values: ProjectAnnouncementFormValues
  ) {
    if (!flowProject || !userId) {
      return;
    }
    const receivers = announcementReceivers;
    if (!receivers.length) {
      message.warning("请选择查收人员");
      return;
    }
    if (
      !getTaskContentText(announcementContentHtml) &&
      !getTaskImageCount(announcementContentHtml)
    ) {
      message.warning("请输入公告内容");
      return;
    }

    try {
      const { data } = await createApi().post("/message/create", {
        type: "project.announcement",
        title: "项目公告",
        content: announcementContentHtml,
        sender_id: userId,
        sender_type: isTeamAdmin ? "team_admin" : "user",
        receiver_scope: "users",
        receivers,
        team_code: flowProject.team_code,
        project_id: flowProject.project_id,
        target_type: "project",
        target_id: flowProject.project_id,
        action_required: values.action_required !== false,
      });
      if (data.code !== 0) {
        message.error(data.message || "发布公告失败");
        return;
      }
      announcementForm.resetFields();
      closeAnnouncementModal();
      message.success("项目公告已发布");
      await fetchProjectAnnouncements(flowProject.project_id);
      await fetchProjectLogs(flowProject.project_id);
      await fetchUnreadMessageCount({ silent: true });
    } catch {
      message.error("发布公告失败");
    }
  }

  useEffect(() => {
    if (!isLogin || !userId) {
      setUnreadMessageCount(0);
      setMessageList([]);
      setWeeklyStatus({
        title: "本周任务进度",
        text: "登录后同步本周任务状态。",
        completed: 0,
        pending: 0,
        due_tasks: [],
      });
      setWeeklyRanking(null);
      return;
    }

    let timer: number | undefined;
    let stopped = false;

    const poll = async () => {
      if (stopped) {
        return;
      }
      if (document.visibilityState === "visible") {
        await fetchUnreadMessageCount({ silent: true });
      }
      if (!stopped) {
        timer = window.setTimeout(poll, getNextMessagePollDelay());
      }
    };

    fetchUnreadMessageCount({ silent: true });
    timer = window.setTimeout(poll, getNextMessagePollDelay());

    const refreshWhenVisible = () => {
      if (document.visibilityState === "visible") {
        fetchUnreadMessageCount({ silent: true });
      }
    };
    document.addEventListener("visibilitychange", refreshWhenVisible);

    return () => {
      stopped = true;
      document.removeEventListener("visibilitychange", refreshWhenVisible);
      if (timer) {
        window.clearTimeout(timer);
      }
    };
  }, [fetchUnreadMessageCount, isLogin, userId]);

  useEffect(() => {
    const shouldPollDashboard =
      activeView === "workspace" || activeView === "board";
    if (!isLogin || !userId || !shouldPollDashboard) {
      return;
    }

    let timer: number | undefined;
    let stopped = false;

    const poll = async () => {
      if (stopped) {
        return;
      }
      if (document.visibilityState === "visible") {
        await refreshDashboardData({ silent: true });
      }
      if (!stopped) {
        timer = window.setTimeout(poll, getNextDashboardPollDelay());
      }
    };

    refreshDashboardData({ silent: true });
    timer = window.setTimeout(poll, getNextDashboardPollDelay());

    const refreshWhenVisible = () => {
      if (document.visibilityState === "visible") {
        refreshDashboardData({ silent: true });
      }
    };
    document.addEventListener("visibilitychange", refreshWhenVisible);

    return () => {
      stopped = true;
      document.removeEventListener("visibilitychange", refreshWhenVisible);
      if (timer) {
        window.clearTimeout(timer);
      }
    };
  }, [activeView, isLogin, refreshDashboardData, userId]);

  useEffect(() => {
    const syncRoute = () => {
      const path = window.location.pathname;
      setActiveView(getViewFromPath(path));
      setFlowProjectId(getProjectIdFromPath(path));
    };
    window.addEventListener("popstate", syncRoute);
    return () => window.removeEventListener("popstate", syncRoute);
  }, []);

  useEffect(() => {
    const taskFlowElements = Array.from(
      document.querySelectorAll<HTMLDivElement>(".projectMemberTaskFlow")
    );
    const handleTaskFlowWheel = (event: WheelEvent) => {
      event.preventDefault();
      event.stopPropagation();
      const target = event.currentTarget as HTMLDivElement;
      target.scrollLeft +=
        Math.abs(event.deltaY) > Math.abs(event.deltaX)
          ? event.deltaY
          : event.deltaX;
    };

    taskFlowElements.forEach((element) => {
      element.addEventListener("wheel", handleTaskFlowWheel, {
        passive: false,
      });
    });

    return () => {
      taskFlowElements.forEach((element) => {
        element.removeEventListener("wheel", handleTaskFlowWheel);
      });
    };
  });

  useEffect(() => {
    if (activeView !== "projectFlow") {
      setProjectLogList([]);
      setProjectAnnouncementList([]);
      setIsProjectFlowFullscreen(false);
      setIsProjectFlowArrangeMode(false);
      setFlowProjectSnapshot(null);
      setFlowTaskSnapshot([]);
      setProjectFlowLoadFailed(false);
      setSelectedTaskNode(null);
      setCommentingTaskNode(null);
      resetTaskCommentEditor();
      setProjectFlowPanelTab("announcements");
      setIsProjectFlowPanelCollapsed(false);
      setActiveAttachmentFolderId("");
      return;
    }

    if (flowProjectId) {
      setSelectedTaskNode(null);
      setCommentingTaskNode(null);
      resetTaskCommentEditor();
      setProjectFlowPanelTab("announcements");
      setActiveAttachmentFolderId("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeView, flowProjectId]);

  useEffect(() => {
    activeProjectPhaseRef.current = activeProjectPhase;
  }, [activeProjectPhase]);

  useEffect(() => {
    if (isCurrentProjectPhaseEditable) {
      return;
    }
    setLocalProjectFlowDraggingNode();
    projectFlowDraggingRef.current = false;
    projectFlowDraggingNodeIdsRef.current.clear();
    clearProjectFlowDragClaim();
    setIsProjectFlowArrangeMode(false);
    if (projectFlowSaveTimer.current) {
      window.clearTimeout(projectFlowSaveTimer.current);
      projectFlowSaveTimer.current = undefined;
    }
    projectFlowPendingSyncNodeIdsRef.current.clear();
  }, [isCurrentProjectPhaseEditable]);

  useEffect(() => {
    if (activeView !== "projectFlow" || !flowProjectId || !isLogin || !userId) {
      return;
    }
    fetchProjectFlowSnapshot(flowProjectId, {
      silent: false,
      showLoading: true,
    });
    fetchUnreadTaskCommentMessages(flowProjectId);
    // Entering a project flow must load the authoritative project snapshot once.
    // Later timer refreshes intentionally avoid rebuilding the graph.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeProjectPhase, activeView, flowProjectId, isLogin, userId]);

  useEffect(() => {
    if (activeView !== "projectFlow" || !flowProjectId || !isLogin || !userId) {
      return;
    }

    let timer: number | undefined;
    let stopped = false;

    const poll = async () => {
      if (stopped) {
        return;
      }
      if (document.visibilityState === "visible") {
        await refreshProjectFlowPanelData(
          flowProjectId,
          projectFlowPanelTab,
          {
            silent: true,
            showLoading: false,
          },
          {
            refreshSnapshot: false,
          }
        );
      }
      if (!stopped) {
        timer = window.setTimeout(poll, getNextProjectFlowPollDelay());
      }
    };

    poll();

    const refreshWhenVisible = () => {
      if (document.visibilityState === "visible") {
        if (timer) {
          window.clearTimeout(timer);
        }
        poll();
      }
    };
    document.addEventListener("visibilitychange", refreshWhenVisible);

    return () => {
      stopped = true;
      document.removeEventListener("visibilitychange", refreshWhenVisible);
      if (timer) {
        window.clearTimeout(timer);
      }
    };
    // The effect intentionally restarts on tab change so switching tabs refreshes once and resets the timer.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    activeView,
    flowProjectId,
    isLogin,
    isTeamAdmin,
    projectFlowPanelTab,
    userId,
    userRole,
  ]);

  useEffect(() => {
    if (projectFlowPanelTab === "members" && !canManageFlowProjectMembers) {
      setProjectFlowPanelTab("announcements");
    }
  }, [canManageFlowProjectMembers, projectFlowPanelTab]);

  useEffect(() => {
    projectFlowPanelTabRef.current = projectFlowPanelTab;
  }, [projectFlowPanelTab]);

  useEffect(() => {
    const authSession = getAuthSession();
    const token = authSession?.access_token || "";
    const canStartCollaboration =
      isActiveFlowProjectParticipant &&
      canStartProjectFlowCollaboration(
        flowProjectId,
        flowProjectSnapshot?.project_id
      );
    if (
      activeView !== "projectFlow" ||
      !isLogin ||
      !token ||
      !canStartCollaboration
    ) {
      projectFlowCollabProviderRef.current?.destroy();
      projectFlowCollabDocRef.current?.destroy();
      if (projectFlowCollabSyncTimerRef.current) {
        window.clearTimeout(projectFlowCollabSyncTimerRef.current);
      }
      if (projectFlowRemoteApplyTimerRef.current) {
        window.clearTimeout(projectFlowRemoteApplyTimerRef.current);
      }
      if (projectFlowGraphRefreshTimerRef.current) {
        window.clearTimeout(projectFlowGraphRefreshTimerRef.current);
      }
      projectFlowCollabProviderRef.current = null;
      projectFlowCollabDocRef.current = null;
      projectFlowCollabPositionsRef.current = {};
      projectFlowPendingSyncNodeIdsRef.current.clear();
      projectFlowPendingGraphRefreshRef.current = false;
      projectFlowPendingGraphProjectIdRef.current = "";
      projectFlowGraphVersionRef.current = 0;
      projectFlowPendingGraphVersionRef.current = 0;
      projectFlowCollabSyncTimerRef.current = undefined;
      projectFlowRemoteApplyTimerRef.current = undefined;
      projectFlowGraphRefreshTimerRef.current = undefined;
      setProjectFlowCollabStatus("idle");
      setProjectFlowCollaborators([]);
      projectFlowRemoteNodeLocksRef.current = {};
      setProjectFlowRemoteNodeLocks({});
      return;
    }

    let disposed = false;
    let cleanup: (() => void) | null = null;
    setProjectFlowCollabStatus("connecting");

    Promise.resolve().then(() => {
        if (disposed) {
          return;
        }
        const YModule = require("yjs");
        const websocketModule = require("y-websocket");
        const doc = new YModule.Doc();
        const provider = new websocketModule.WebsocketProvider(
          `${getProjectFlowCollabServerUrl()}/project-flow-collaboration`,
          `${flowProjectId}/${activeProjectPhase}`,
          doc,
          {
            params: { token },
          }
        );
        const positionMap = doc.getMap("nodePositions");
        const graphEventsMap = doc.getMap("graphEvents");
        projectFlowCollabDocRef.current = doc;
        projectFlowCollabProviderRef.current = provider;

        const updateCollaborators = () => {
          const awarenessStates = Array.from(
            provider.awareness.getStates().entries()
          ) as Array<[number, any]>;
          const collaborators = awarenessStates
            .filter(([clientId]) => clientId !== doc.clientID)
            .map(([clientId, state]: [number, any]) => ({
              clientId,
              name: state?.user?.name || state?.user?.userId || "协作者",
              userId: state?.user?.userId || "",
            }));
          setProjectFlowCollaborators(collaborators);
          const nodeLocks = getRemoteProjectFlowNodeLocks(
            awarenessStates,
            doc.clientID
          );
          let lostDraggingOwnership = false;
          projectFlowDraggingNodeIdsRef.current.forEach((nodeId) => {
            const remoteLock = nodeLocks[nodeId];
            if (!remoteLock) return;
            if (remoteLock.clientId > doc.clientID) {
              delete nodeLocks[nodeId];
              return;
            }
            projectFlowDraggingNodeIdsRef.current.delete(nodeId);
            projectFlowPendingSyncNodeIdsRef.current.delete(nodeId);
            lostDraggingOwnership = true;
          });
          if (lostDraggingOwnership) {
            projectFlowDraggingRef.current = false;
            clearProjectFlowDragClaim();
            provider.awareness.setLocalStateField("dragging", null);
            message.warning("该节点已由另一位协作者先开始移动");
          }
          projectFlowRemoteNodeLocksRef.current = nodeLocks;
          setProjectFlowRemoteNodeLocks(nodeLocks);
        };

        const applyRemotePositions = (event: any) => {
          if (event.transaction.origin === "local-layout") {
            return;
          }
          const nextPositions = { ...projectFlowCollabPositionsRef.current };
          event.keysChanged.forEach((nodeId: string) => {
            const position = normalizeNodePosition(positionMap.get(nodeId));
            if (position) {
              nextPositions[nodeId] = position;
            } else {
              delete nextPositions[nodeId];
            }
          });
          projectFlowCollabPositionsRef.current = nextPositions;
          if (projectFlowRemoteApplyTimerRef.current) {
            return;
          }
          projectFlowRemoteApplyTimerRef.current = window.setTimeout(() => {
            projectFlowRemoteApplyTimerRef.current = undefined;
            const latestPositions = projectFlowCollabPositionsRef.current;
            projectFlowCollabApplyingRef.current = true;
            setProjectFlowNodes((currentNodes) => {
              const movedNodes = currentNodes.map((node) => {
                const position = latestPositions[node.id];
                if (
                  !isProjectFlowTaskLayoutNode(node) ||
                  !position ||
                  projectFlowDraggingNodeIdsRef.current.has(node.id)
                ) {
                  return node;
                }
                return {
                  ...node,
                  position,
                };
              });
              const nextNodes = movedNodes;
              projectFlowNodesRef.current = nextNodes;
              return nextNodes;
            });
            window.setTimeout(() => {
              projectFlowCollabApplyingRef.current = false;
            }, 0);
          }, 80);
        };

        const applyRemoteGraphEvent = async (event: any) => {
          if (event.transaction.origin === "local-graph-event") {
            return;
          }
          if (!event.keysChanged?.has?.("latest")) {
            return;
          }
          const graphEvent = graphEventsMap.get("latest") as
            | { at?: number; by?: string; reason?: string; version?: number; task_id?: string }
            | undefined;
          if (!graphEvent?.at) {
            return;
          }
          if (shouldRefreshUnreadTaskComments(graphEvent.reason)) {
            void fetchUnreadTaskCommentMessages(flowProjectId);
          }
          if (graphEvent.reason?.startsWith("task.subtask.") && graphEvent.task_id) {
            const selected = selectedTaskNodeRef.current;
            const editing = editingTaskNodeRef.current;
            if (selected?.id === graphEvent.task_id) {
              void fetchTaskSubtasks(selected, "detail");
            }
            if (editing?.id === graphEvent.task_id) {
              void fetchTaskSubtasks(editing, "edit");
            }
          }
          const version = Number(graphEvent.version || 0);
          if (version && version <= projectFlowGraphVersionRef.current) {
            return;
          }
          if (version) {
            projectFlowPendingGraphVersionRef.current = Math.max(
              projectFlowPendingGraphVersionRef.current,
              version
            );
          }
          queueProjectFlowGraphRefresh(flowProjectId);
        };

        provider.on("status", ({ status }: { status: string }) => {
          setProjectFlowCollabStatus(
            status === "connected" ? "connected" : "disconnected"
          );
        });
        provider.on("connection-error", () => {
          setProjectFlowCollabStatus("disconnected");
        });
        provider.on("sync", () => {
          const initialGraphEvent = graphEventsMap.get("latest") as
            | { version?: number }
            | undefined;
          const roomGraphVersion = Number(initialGraphEvent?.version || 0);
          if (roomGraphVersion > projectFlowGraphVersionRef.current) {
            projectFlowPendingGraphVersionRef.current = Math.max(
              projectFlowPendingGraphVersionRef.current,
              roomGraphVersion
            );
            queueProjectFlowGraphRefresh(flowProjectId);
          }
          const remotePositions: Record<string, { x: number; y: number }> = {};
          Array.from(positionMap.entries()).forEach(([nodeId, value]: any) => {
            const position = normalizeNodePosition(value);
            if (position) {
              remotePositions[nodeId] = position;
            }
          });
          projectFlowCollabPositionsRef.current = remotePositions;
          if (Object.keys(remotePositions).length) {
            setProjectFlowNodes((currentNodes) => {
              const nextNodes = applyProjectFlowCollabPositions(currentNodes);
              projectFlowNodesRef.current = nextNodes;
              return nextNodes;
            });
          }
          setProjectFlowSaveStatus("saved");
        });
        provider.awareness.setLocalStateField("user", {
          userId,
          name: nickname || userId,
          phase: activeProjectPhase,
        });
        provider.awareness.on("change", updateCollaborators);
        positionMap.observe(applyRemotePositions);
        graphEventsMap.observe(applyRemoteGraphEvent);

        cleanup = () => {
          positionMap.unobserve(applyRemotePositions);
          graphEventsMap.unobserve(applyRemoteGraphEvent);
          provider.awareness.off("change", updateCollaborators);
          if (projectFlowCollabSyncTimerRef.current) {
            window.clearTimeout(projectFlowCollabSyncTimerRef.current);
          }
          if (projectFlowRemoteApplyTimerRef.current) {
            window.clearTimeout(projectFlowRemoteApplyTimerRef.current);
          }
          if (projectFlowGraphRefreshTimerRef.current) {
            window.clearTimeout(projectFlowGraphRefreshTimerRef.current);
          }
          provider.destroy();
          doc.destroy();
          if (projectFlowCollabProviderRef.current === provider) {
            projectFlowCollabProviderRef.current = null;
            projectFlowCollabDocRef.current = null;
            projectFlowCollabPositionsRef.current = {};
            projectFlowPendingSyncNodeIdsRef.current.clear();
            projectFlowPendingGraphRefreshRef.current = false;
            projectFlowPendingGraphProjectIdRef.current = "";
            projectFlowGraphVersionRef.current = 0;
            projectFlowPendingGraphVersionRef.current = 0;
            projectFlowCollabSyncTimerRef.current = undefined;
            projectFlowRemoteApplyTimerRef.current = undefined;
            projectFlowGraphRefreshTimerRef.current = undefined;
            setProjectFlowCollabStatus("idle");
            setProjectFlowCollaborators([]);
      projectFlowRemoteNodeLocksRef.current = {};
      clearProjectFlowDragClaim();
      setProjectFlowRemoteNodeLocks({});
          }
        };
      }
    );

    return () => {
      disposed = true;
      cleanup?.();
    };
    // Collaboration room is scoped by current project and logged-in identity.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    activeProjectPhase,
    activeView,
    flowProjectId,
    flowProjectSnapshot?.project_id,
    isActiveFlowProjectParticipant,
    isLogin,
    nickname,
    userId,
  ]);

  useEffect(() => {
    selectedTaskNodeRef.current = selectedTaskNode;
  }, [selectedTaskNode]);

  useEffect(() => {
    editingTaskNodeRef.current = editingTaskNode;
  }, [editingTaskNode]);

  useEffect(() => {
    if (!selectedTaskNode) {
      return;
    }
    const refreshedTask = findTaskNodeByMessageTarget(selectedTaskNode.id);
    if (refreshedTask && refreshedTask !== selectedTaskNode) {
      setSelectedTaskNode(refreshedTask);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    flowTaskSnapshot,
    selectedTaskNode,
    selectedTaskNode?.id,
  ]);

  useEffect(() => {
    if (
      activeView !== "projectFlow" ||
      !pendingTaskCommentFocus ||
      pendingTaskCommentFocus.projectId !== flowProjectId ||
      !flowTaskSnapshot.length
    ) {
      return;
    }
    const targetTask = findTaskNodeByMessageTarget(
      pendingTaskCommentFocus.taskId,
      pendingTaskCommentFocus.parentTaskId
    );
    if (!targetTask) {
      return;
    }
    openTaskDetailModal(
      targetTask,
      pendingTaskCommentFocus.subtaskId,
      pendingTaskCommentFocus.contentItemId
    );
    setPendingTaskCommentFocus(null);
    message.success(
      pendingTaskCommentFocus.contentItemId
        ? "已定位到任务内容项"
        : pendingTaskCommentFocus.subtaskId
        ? "已定位到子任务"
        : "已定位到批注节点"
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    activeView,
    flowProjectId,
    flowTaskSnapshot,
    pendingTaskCommentFocus,
  ]);

  useEffect(() => {
    return () => {
      if (projectFlowSaveTimer.current) {
        window.clearTimeout(projectFlowSaveTimer.current);
      }
    };
  }, []);

  function changeView(view: Exclude<DashboardView, "projectFlow">) {
    setActiveView(view);
    setFlowProjectId("");
    window.history.pushState(null, "", currentRouteMap[view]);
  }

  function openProjectFlow(
    project: ApiProjectItem,
    phase: ProjectPhase = "initiation"
  ) {
    const targetPhase = project.template === "single" ? "initiation" : phase;
    activeProjectPhaseRef.current = targetPhase;
    setActiveProjectPhase(targetPhase);
    setFlowProjectId(project.project_id);
    setActiveView("projectFlow");
    window.history.pushState(
      null,
      "",
      `/projects/${encodeURIComponent(project.project_id)}`
    );
  }

  async function changeProjectPhase(phase: ProjectPhase) {
    if (phase === activeProjectPhase) {
      return;
    }
    flushProjectFlowPendingPositionSync();
    if (projectFlowSaveTimer.current) {
      window.clearTimeout(projectFlowSaveTimer.current);
      projectFlowSaveTimer.current = undefined;
    }
    setLocalProjectFlowDraggingNode();
    setIsProjectFlowArrangeMode(false);
    setSelectedTaskNode(null);
    setSelectedTaskSubtasks([]);
    setCommentingTaskNode(null);
    resetTaskCommentEditor();
    projectFlowNodesRef.current = [];
    projectFlowCollabPositionsRef.current = {};
    setProjectFlowNodes([]);
    setProjectFlowEdges([]);
    setFlowTaskSnapshot([]);
    activeProjectPhaseRef.current = phase;
    setActiveProjectPhase(phase);
  }

  function updateProjectPhasePermissionState(
    projectId: string,
    phase: ProjectPhase,
    editable: boolean
  ) {
    const updateProject = (project: ApiProjectItem) => ({
      ...project,
      phase_permissions: {
        ...project.phase_permissions,
        [phase]: editable,
      },
      updated_at: Date.now(),
    });
    setApiProjects((prev) =>
      prev.map((project) =>
        project.project_id === projectId ? updateProject(project) : project
      )
    );
    setFlowProjectSnapshot((prev) =>
      prev?.project_id === projectId ? updateProject(prev) : prev
    );
  }

  function changeProjectPhaseEditable(
    phase: ProjectPhase,
    editable: boolean
  ) {
    if (!flowProject || !canManageProjectPhases) {
      return;
    }
    setPendingProjectPhasePermission({
      projectId: flowProject.project_id,
      phase,
      editable,
    });
  }

  async function confirmProjectPhaseEditable() {
    if (!pendingProjectPhasePermission || projectPhaseUpdating) {
      return;
    }
    const lockKey = `phase:editable:${pendingProjectPhasePermission.projectId}:${pendingProjectPhasePermission.phase}`;
    await runMutation(lockKey, performConfirmProjectPhaseEditable);
  }

  async function performConfirmProjectPhaseEditable() {
    if (!pendingProjectPhasePermission) {
      return;
    }
    const { projectId, phase, editable } = pendingProjectPhasePermission;
    const phaseLabel = PROJECT_PHASE_LABELS[phase];
    setProjectPhaseUpdating(true);
    try {
      const { data } = await createApi().post("/project/phase/editable", {
        project_id: projectId,
        operator: userId,
        phase,
        editable,
      });
      if (data.code !== 0) {
        message.error(data.message || "修改阶段编辑权限失败");
        return;
      }
      updateProjectPhasePermissionState(projectId, phase, editable);
      if (phase === activeProjectPhase && !editable) {
        setIsProjectFlowArrangeMode(false);
      }
      setPendingProjectPhasePermission(null);
      await fetchProjectLogs(projectId, {
        silent: true,
        showLoading: false,
      });
      message.success(`${phaseLabel}已${editable ? "开放编辑" : "设为只读"}`);
    } catch {
      message.error("修改阶段编辑权限失败");
    } finally {
      setProjectPhaseUpdating(false);
    }
  }

  function openProjectFlowById(
    projectId?: string,
    focus?: {
      taskId?: string;
      parentTaskId?: string;
      subtaskId?: string;
      contentItemId?: string;
      commentId?: string;
      phase?: ProjectPhase;
    }
  ) {
    if (!projectId) {
      message.warning("消息缺少项目 ID，无法跳转");
      return;
    }
    if (focus?.taskId) {
      setPendingTaskCommentFocus({
        projectId,
        taskId: focus.taskId,
        phase: focus.phase,
        parentTaskId: focus.parentTaskId,
        subtaskId: focus.subtaskId,
        contentItemId: focus.contentItemId,
        commentId: focus.commentId,
      });
    }
    const project = overviewProjects.find((item) => item.project_id === projectId);
    if (project) {
      openProjectFlow(project, focus?.phase || "initiation");
      return;
    }
    const targetPhase = focus?.phase || "initiation";
    activeProjectPhaseRef.current = targetPhase;
    setActiveProjectPhase(targetPhase);
    setFlowProjectId(projectId);
    setActiveView("projectFlow");
    window.history.pushState(null, "", `/projects/${encodeURIComponent(projectId)}`);
  }

  function backToProjectBoard() {
    setIsProjectRequirementOpen(false);
    setFlowProjectId("");
    setActiveView("board");
    window.history.pushState(null, "", viewRouteMap.board);
  }

  function quit(e: React.MouseEvent<HTMLElement>) {
    e.preventDefault();
    clearAuthStorage();
    setIsLogin(false);
    setUserId("");
    window.history.pushState(null, "", "/");
  }

  function copyUserId(e: React.MouseEvent<HTMLElement>) {
    e.preventDefault();
    copyText(userId);
  }

  function copyText(value: string) {
    if (!value) {
      message.warning("暂无可复制内容");
      return;
    }
    try {
      if (navigator.clipboard?.writeText) {
        navigator.clipboard.writeText(value);
        message.success("复制成功");
        return;
      }
      const tempTextArea = document.createElement("textarea");
      tempTextArea.value = value;
      document.body.appendChild(tempTextArea);
      tempTextArea.select();
      tempTextArea.setSelectionRange(0, 99999);
      const successful = document.execCommand("copy");
      document.body.removeChild(tempTextArea);
      successful ? message.success("复制成功") : message.error("复制失败");
    } catch {
      message.error("复制失败，请手动复制");
    }
  }

  function formatMessageTime(timestamp?: number) {
    if (!timestamp) {
      return "未知时间";
    }
    return new Intl.DateTimeFormat("zh-CN", {
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    }).format(new Date(timestamp));
  }

  function getMessageTypeText(type?: string) {
    const typeMap: Record<string, string> = {
      "project.invite": "项目通知",
      "project.announcement": "项目公告",
      "project.deleted": "项目删除",
      "project.member.removed": "成员变更",
      "project.member.left": "成员变更",
      "task.confirm": "任务确认",
      "task.assigned": "任务指派",
      "task.comment": "节点批注",
      "task.content.due": "到期提醒",
      "task.status.change": "任务动态",
      "system.notice": "系统通知",
    };
    return type ? typeMap[type] || "消息" : "消息";
  }

  function getMessageProjectName(detail?: ApiMessageItem["message"]) {
    const projectId = detail?.payload?.project_id || detail?.project_id;
    if (!projectId) {
      return "";
    }
    return (
      detail?.payload?.project_name ||
      overviewProjects.find((project) => project.project_id === projectId)
        ?.project_name ||
      "未命名项目"
    );
  }

  function getMessageProjectActionText(detail?: ApiMessageItem["message"]) {
    return getTaskMessageActionText(detail);
  }

  function getProjectAnnouncementOptions(project: ApiProjectItem) {
    const receiverIds = Array.from(
      new Set([
        project.creator,
        ...project.members.map((member) => member.user_id),
      ].filter(Boolean))
    );
    return receiverIds.map((receiverId) => ({
      label: getMemberDisplayName(receiverId) || receiverId,
      value: receiverId,
    }));
  }

  function getProjectAnnouncementUnreadCount() {
    return projectAnnouncementList.filter((item) => !item.read_at).length;
  }

  function findTaskNodeByMessageTarget(
    taskId?: string,
    _parentTaskId?: string,
    tasks = flowTaskSnapshot
  ): ApiTaskItem | null {
    if (!taskId) {
      return null;
    }
    return tasks.find((task) => task.id === taskId) || null;
  }

  function getTaskCommentMessageTaskId(item: ApiMessageItem) {
    return (
      item.message?.payload?.task_id ||
      item.message?.task_id ||
      item.message?.target_id ||
      ""
    );
  }

  function buildUnreadTaskCommentMap(items: ApiMessageItem[]) {
    return items.reduce<Record<string, ApiMessageItem[]>>((result, item) => {
      const taskId = getTaskCommentMessageTaskId(item);
      if (!taskId || item.read_at) {
        return result;
      }
      result[taskId] = [...(result[taskId] || []), item];
      return result;
    }, {});
  }

  async function fetchUnreadTaskCommentMessages(projectId = flowProjectId) {
    if (!projectId || !userId) {
      setUnreadTaskCommentMap({});
      return;
    }
    try {
      const { data } = await createApi().post("/message/list", {
        project_id: projectId,
        type: "task.comment",
        unread: true,
        pageIndex: 0,
        pageSize: 100,
      });
      if (data.code === 0) {
        setUnreadTaskCommentMap(buildUnreadTaskCommentMap(data.data?.list || []));
      }
    } catch {
      // 批注提醒不阻断节点图主流程。
    }
  }

  async function markTaskCommentMessagesRead(taskId?: string) {
    if (!taskId || !unreadTaskCommentMap[taskId]?.length) {
      return;
    }
    const messages = unreadTaskCommentMap[taskId];
    setUnreadTaskCommentMap((prev) => {
      const next = { ...prev };
      delete next[taskId];
      return next;
    });
    setUnreadMessageCount((prev) => Math.max(prev - messages.length, 0));
    await Promise.all(
      messages.map((item) =>
        createApi()
          .post("/message/read", { message_id: item.message_id })
          .catch(() => undefined)
      )
    );
    setMessageList((prev) =>
      prev.map((item) =>
        messages.some((messageItem) => messageItem.message_id === item.message_id)
          ? { ...item, read_at: item.read_at || Date.now() }
          : item
      )
    );
  }

  async function openMessageDrawer() {
    setIsMessageDrawerOpen(true);
    await fetchMessages();
    await fetchUnreadMessageCount({ silent: true });
  }

  async function markMessageRead(item: ApiMessageItem) {
    if (item.read_at) {
      return;
    }

    try {
      const { data } = await createApi().post("/message/read", {
        message_id: item.message_id,
      });
      if (data.code !== 0) {
        message.error(data.message || "标记消息已读失败");
        return;
      }
      const readAt = Date.now();
      setMessageList((prev) =>
        prev.map((messageItem) =>
          messageItem.message_id === item.message_id
            ? { ...messageItem, read_at: readAt }
            : messageItem
        )
      );
      setProjectAnnouncementList((prev) =>
        prev.map((messageItem) =>
          messageItem.message_id === item.message_id
            ? { ...messageItem, read_at: readAt }
            : messageItem
        )
      );
      if (item.message?.type === "task.comment") {
        const taskId = getTaskCommentMessageTaskId(item);
        if (taskId) {
          setUnreadTaskCommentMap((prev) => {
            const nextItems = (prev[taskId] || []).filter(
              (messageItem) => messageItem.message_id !== item.message_id
            );
            const next = { ...prev };
            if (nextItems.length) {
              next[taskId] = nextItems;
            } else {
              delete next[taskId];
            }
            return next;
          });
        }
      }
      setUnreadMessageCount((prev) => Math.max(prev - 1, 0));
    } catch {
      message.error("标记消息已读失败");
    }
  }

  async function openMessageDetail(item: ApiMessageItem) {
    setSelectedMessage(item);
    if (!item.read_at) {
      await markMessageRead(item);
      setSelectedMessage((prev) =>
        prev?.message_id === item.message_id
          ? { ...prev, read_at: Date.now() }
          : prev
      );
    }
  }

  async function markAllMessagesRead() {
    try {
      const { data } = await createApi().post("/message/readAll", {});
      if (data.code !== 0) {
        message.error(data.message || "全部标记已读失败");
        return;
      }
      const readAt = Date.now();
      setMessageList((prev) =>
        prev.map((item) => ({ ...item, read_at: item.read_at || readAt }))
      );
      setUnreadTaskCommentMap({});
      setUnreadMessageCount(0);
      message.success("已全部标记为已读");
    } catch {
      message.error("全部标记已读失败");
    }
  }

  const avatarUploadProps: UploadProps = {
    accept: "image/*",
    maxCount: 1,
    showUploadList: false,
    beforeUpload: (file) => {
      const reader = new FileReader();
      reader.onload = () => setProjectAvatar(String(reader.result || ""));
      reader.readAsDataURL(file);
      return false;
    },
  };

  function getProjectFallbackColor(projectName: string) {
    const colors = ["#1f4fd8", "#0f766e", "#b45309", "#be123c", "#6d28d9"];
    const index = projectName.charCodeAt(0) % colors.length;
    return colors[index];
  }

  function getTextAvatar(text: string) {
    const value = text.trim() || userId || "用";
    return {
      initial: value.slice(0, 1).toUpperCase(),
      background: getProjectFallbackColor(value),
    };
  }

  function getMemberDisplayName(username: string) {
    const projectProfile =
      flowProject?.member_profiles?.find((item) => item.username === username) ||
      overviewProjects
        .flatMap((project) => project.member_profiles || [])
        .find((item) => item.username === username);
    if (projectProfile?.nickname) {
      return projectProfile.nickname;
    }
    const member = overviewTeamMembers.find((item) => item.username === username);
    if (member?.nickname) {
      return member.nickname;
    }
    if (username === userId && nickname) {
      return nickname;
    }
    return username;
  }

  function runMutation<T>(key: string, action: () => Promise<T>) {
    return mutationLockRef.current!.run(key, action);
  }

  function isMutationPending(key: string) {
    return mutationPendingKeys.has(key);
  }

  function getTaskAssigneeNames(assignees: string[] = []) {
    return uniqueAssignees(assignees).map(getMemberDisplayName);
  }

  function getProjectMemberNames(project: ApiProjectItem) {
    return project.members
      .map((member) => getMemberDisplayName(member.user_id))
      .filter(Boolean)
      .join("、");
  }

  function getCurrentTask(tasks: ApiTaskItem[]) {
    return getFocusedReadyTask(tasks);
  }

  function getTaskCurrentLabel(status: ApiTaskItem["status"]) {
    if (status === "discarded") {
      return "已废弃";
    }
    if (status === "doing") {
      return "当前执行";
    }
    if (status === "blocked") {
      return "当前阻塞";
    }
    if (status === "review") {
      return "等待验收";
    }
    return "待开始";
  }

  function getTaskStatusClass(status: ApiTaskItem["status"]) {
    return `projectTaskBlock--${status}`;
  }

  function getTaskStatusText(status: ApiTaskItem["status"]) {
    const statusMap: Record<ApiTaskItem["status"], string> = {
      todo: "待处理",
      doing: "进行中",
      blocked: "阻塞",
      review: "评审中",
      done: "已完成",
      discarded: "已废弃",
    };
    return statusMap[status];
  }

  function getTaskStatusColor(status: ApiTaskItem["status"]) {
    const colorMap: Record<ApiTaskItem["status"], string> = {
      todo: "blue",
      doing: "cyan",
      blocked: "red",
      review: "gold",
      done: "green",
      discarded: "default",
    };
    return colorMap[status];
  }

  function formatTaskDueTime(timestamp?: number) {
    if (!timestamp) {
      return "";
    }
    return new Intl.DateTimeFormat("zh-CN", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).format(new Date(timestamp));
  }

  function formatFileSize(size?: number) {
    if (!size) {
      return "0 B";
    }
    if (size < 1024) {
      return `${size} B`;
    }
    if (size < 1024 * 1024) {
      return `${(size / 1024).toFixed(1)} KB`;
    }
    return `${(size / 1024 / 1024).toFixed(1)} MB`;
  }

  function getTaskContentText(content?: string) {
    if (!content) {
      return "";
    }
    const wrapper = document.createElement("div");
    wrapper.innerHTML = content;
    return (wrapper.textContent || "").replace(/\s+/g, " ").trim();
  }

  function getTaskImageCount(content?: string) {
    if (!content) {
      return 0;
    }
    const wrapper = document.createElement("div");
    wrapper.innerHTML = content;
    return wrapper.querySelectorAll("img").length;
  }

  function handleTaskDetailContentClick(event: React.MouseEvent<HTMLDivElement>) {
    const target = event.target as HTMLElement;
    if (target.tagName.toLowerCase() !== "img") {
      return;
    }
    const image = target as HTMLImageElement;
    if (image.src) {
      setPreviewImage(image.src);
    }
  }

  function isTaskFinished(task: ApiTaskItem) {
    return task.status === "done";
  }

  function flattenTaskModules(tasks: ApiTaskItem[]) {
    return tasks;
  }

  function getProjectStepCurrent(project: ApiProjectItem) {
    if (project.template === "single") {
      return project.status === "done" ? 1 : 0;
    }
    return getProjectProgressStep(
      project.phase_summaries,
      project.status === "done"
    );
  }

  function getProjectStepStatuses(
    project: ApiProjectItem
  ): ProjectProgressStepStatus[] {
    if (project.template === "single") {
      const summary = project.task_summary || {
        total: 0,
        completed: 0,
        pending: 0,
      };
      const tasksComplete = summary.total > 0 && summary.pending === 0;
      return [
        tasksComplete ? "finish" : "process",
        project.status === "done"
          ? "finish"
          : tasksComplete
          ? "process"
          : "wait",
      ];
    }
    return getProjectProgressStepStatuses(
      project.phase_summaries,
      project.status === "done"
    );
  }

  function sortProjectsForBoard(projects: ApiProjectItem[]) {
    return [...projects].sort((prev, next) => {
      const prevDone = prev.status === "done";
      const nextDone = next.status === "done";
      if (prevDone !== nextDone) {
        return prevDone ? 1 : -1;
      }
      return (next.updated_at || next.created_at || 0) - (prev.updated_at || prev.created_at || 0);
    });
  }

  function canArchiveProject(project: ApiProjectItem) {
    if (project.status === "done") {
      return false;
    }
    if (project.template === "single") {
      return Boolean(
        project.task_summary &&
          project.task_summary.total > 0 &&
          project.task_summary.pending === 0
      );
    }
    return canArchiveByPhaseSummaries(project.phase_summaries);
  }

  function canUnarchiveProject(project: ApiProjectItem) {
    return project.status === "done";
  }

  function canDeleteProject(project: ApiProjectItem) {
    if (isTeamAdmin) {
      return true;
    }
    return project.creator === userId;
  }

  function canEditProject(project: ApiProjectItem) {
    if (isTeamAdmin || project.creator === userId) {
      return true;
    }
    return (
      userRole === 1 &&
      project.members.some((member) => member.user_id === userId)
    );
  }

  function canManageTaskAssignee(_assignees: string[]) {
    return isCurrentProjectPhaseEditable;
  }

  function getProjectMemberSummary(project: ApiProjectItem) {
    const names = project.members
      .filter((member) => member.user_id !== project.creator)
      .map((member) => getMemberDisplayName(member.user_id));
    return names.length ? names.join("、") : "暂无参与成员";
  }

  const projectFlowData = (() => {
    if (!flowProject) {
      return {
        nodes: [],
        edges: [],
        memberCount: 0,
        taskMemberCount: 0,
        taskCount: 0,
        completedCount: 0,
        pendingCount: 0,
        signature: "",
      };
    }

    const projectTaskSource =
      flowProjectSnapshot?.project_id === flowProjectId ? flowTaskSnapshot : [];
    const projectTasks = projectTaskSource
      .filter(
        (task) =>
          task.project_id === flowProject.project_id &&
          task.phase === activeProjectPhase
      )
      .sort((prev, next) => (prev.created_at || 0) - (next.created_at || 0));
    const projectTaskModules = flattenTaskModules(projectTasks);
    const effectiveProjectTaskModules = projectTaskModules.filter(
      (task) => task.status !== "discarded"
    );
    const taskMemberCount = new Set(
      effectiveProjectTaskModules.flatMap((task) => task.assignees).filter(Boolean)
    ).size;

    const nodes: any[] = [];
    const edges: any[] = [];
    const taskStartY = 190;
    const activePhaseLayout = flowProject.flow_layouts[activeProjectPhase];
    const canUseSavedLayout =
      activePhaseLayout.version === PROJECT_FLOW_LAYOUT_VERSION;
    const savedNodePositions = canUseSavedLayout
      ? activePhaseLayout.nodes
      : {};

    nodes.push({
      id: "project-root",
      type: "projectNode",
      position: savedNodePositions["project-root"] || { x: 0, y: 0 },
      data: {
        name: flowProject.project_name,
        phaseLabel: activeProjectViewLabel,
        taskCount: effectiveProjectTaskModules.length,
      },
      sourcePosition: FlowPosition.Bottom,
    });

    const readyTaskIds = new Set(
      getReadyTasks(projectTasks).map((task) => task.id).filter(Boolean)
    );
    const taskMap = new Map(
      projectTasks.filter((task) => task.id).map((task) => [task.id!, task])
    );
    const depthCache = new Map<string, number>();
    const getTaskDepth = (task: ApiTaskItem): number => {
      if (!task.id) return 0;
      if (depthCache.has(task.id)) return depthCache.get(task.id)!;
      const predecessors = (task.predecessor_ids || [])
        .map((id) => taskMap.get(id))
        .filter(Boolean) as ApiTaskItem[];
      const depth = predecessors.length
        ? Math.max(...predecessors.map(getTaskDepth)) + 1
        : 0;
      depthCache.set(task.id, depth);
      return depth;
    };
    const levelCounts = new Map<number, number>();
    const depthHeights = new Map<number, number>();
    projectTasks.forEach((task) => {
      const depth = getTaskDepth(task);
      const assigneeRows = Math.max(
        1,
        Math.ceil(uniqueAssignees(task.assignees).length / 3)
      );
      const estimatedHeight = 184 + assigneeRows * 25;
      depthHeights.set(
        depth,
        Math.max(depthHeights.get(depth) || 0, estimatedHeight)
      );
    });
    const depthPositions = new Map<number, number>();
    let nextDepthY = taskStartY;
    const maxDepth = Math.max(0, ...Array.from(depthHeights.keys()));
    for (let depth = 0; depth <= maxDepth; depth += 1) {
      depthPositions.set(depth, nextDepthY);
      nextDepthY += (depthHeights.get(depth) || 210) + 90;
    }

    projectTasks.forEach((task) => {
        const taskNodeId = `task-${task.id || task.title}`;
        const depth = getTaskDepth(task);
        const levelIndex = levelCounts.get(depth) || 0;
        levelCounts.set(depth, levelIndex + 1);
        const taskPosition = savedNodePositions[taskNodeId] || {
          x: levelIndex * 390,
          y: depthPositions.get(depth) || taskStartY,
        };
        const isCurrentTask = !!task.id && readyTaskIds.has(task.id);
        const overdueDays = getTaskOverdueDays(task);
        const assignees = uniqueAssignees(task.assignees)
          .map((assigneeId) => ({
            userId: assigneeId,
            name: getMemberDisplayName(assigneeId),
            isSelf: assigneeId === userId,
          }))
          .sort(
            (previous, next) => Number(next.isSelf) - Number(previous.isSelf)
          );
        const remoteLock = projectFlowRemoteNodeLocks[taskNodeId];
        nodes.push({
          id: taskNodeId,
          type: "taskNode",
          position: taskPosition,
          draggable: !remoteLock,
          data: {
            title: task.title,
            status: task.status,
            priority: task.priority || "medium",
            dueTimeText: formatTaskDueTime(task.due_time),
            contentSummary: getTaskContentText(task.content).slice(0, 80),
            imageCount: getTaskImageCount(task.content),
            commentCount: task.comments?.length || 0,
            unreadCommentCount: unreadTaskCommentMap[task.id || task.title]?.length || 0,
            assignees,
            isAssignedToCurrentUser: assignees.some(
              (assignee) => assignee.isSelf
            ),
            canManage: isCurrentProjectPhaseEditable,
            canComment: isCurrentProjectPhaseEditable,
            isCurrent: isCurrentTask,
            isOverdue: overdueDays > 0,
            overdueDays,
            currentLabel: getTaskCurrentLabel(task.status),
            lockedBy: remoteLock?.name || "",
            onSelect: () => {
              openTaskDetailModal(task);
            },
            onComment: () => {
              openTaskCommentModal(task);
            },
            onAction: (action: "edit" | "delete") => {
              if (action === "delete") {
                setConfirmingTaskDelete(task);
                return;
              }
              openTaskEditModal(task);
            },
          },
          targetPosition: FlowPosition.Top,
          sourcePosition: FlowPosition.Bottom,
        });

    });

    projectTasks.forEach((task) => {
      if (!task.id) return;
      const target = `task-${task.id}`;
      const sources = task.predecessor_ids?.length
        ? task.predecessor_ids.map((id) => `task-${id}`)
        : ["project-root"];
      sources.forEach((source, index) => {
        const animated = readyTaskIds.has(task.id!);
        edges.push({
          id: `edge-${source}-${target}-${index}`,
          source,
          target,
          animated,
          markerEnd: { type: FlowMarkerType.ArrowClosed },
          style: {
            stroke: animated
              ? task.status === "blocked" ? "#f87171" : "#67e8f9"
              : "rgba(96, 165, 250, 0.52)",
            strokeWidth: animated ? 3.4 : 1.6,
          },
        });
      });
    });

    return {
      nodes,
      edges,
      memberCount: taskMemberCount,
      taskMemberCount,
      taskCount: effectiveProjectTaskModules.length,
      completedCount: effectiveProjectTaskModules.filter(isTaskFinished).length,
      pendingCount: effectiveProjectTaskModules.filter(
        (task) => !isTaskFinished(task)
      ).length,
      signature: [
        flowProject.project_id,
        activeProjectPhase,
        flowProject.project_name,
        flowProject.updated_at || 0,
        activePhaseLayout.version,
        activePhaseLayout.updated_at || 0,
        Object.entries(unreadTaskCommentMap)
          .sort(([prevTaskId], [nextTaskId]) =>
            prevTaskId.localeCompare(nextTaskId)
          )
          .map(([taskId, items]) => `${taskId}:${items.length}`)
          .join(","),
        Object.entries(projectFlowRemoteNodeLocks)
          .sort(([left], [right]) => left.localeCompare(right))
          .map(([nodeId, lock]) => `${nodeId}:${lock.clientId}:${lock.name}`)
          .join(","),
        ...projectTasks.map((task) =>
          [
            task.id || task.title,
            getTaskAssigneeKey(task.assignees),
            (task.predecessor_ids || []).join(","),
            task.status,
            task.priority || "medium",
            task.due_time || 0,
            getTaskOverdueDays(task),
            task.updated_at || 0,
          ].join(":")
        ),
      ].join("|"),
    };
  })();

  useEffect(() => {
    if (activeView !== "projectFlow" || !flowProject) {
      return;
    }
    if (isProjectFlowArrangeMode && projectFlowDraggingRef.current) {
      return;
    }
    const nextNodes = applyProjectFlowCollabPositions(projectFlowData.nodes);
    projectFlowNodesRef.current = nextNodes;
    setProjectFlowNodes(nextNodes);
    setProjectFlowEdges(projectFlowData.edges);
    setProjectFlowRenderMeta({
      memberCount: projectFlowData.memberCount,
      taskMemberCount: projectFlowData.taskMemberCount,
      taskCount: projectFlowData.taskCount,
    });
    setProjectFlowSaveStatus("idle");
    // The flow graph snapshot is keyed by a compact signature; nodes/edges are recreated every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    activeView,
    flowProject?.project_id,
    projectFlowData.signature,
  ]);

  useEffect(() => {
    if (activeView !== "projectFlow") {
      return;
    }
    const fitTimer = window.setTimeout(() => {
      projectFlowInstanceRef.current?.fitView?.({
        padding: 0.18,
        duration: 240,
      });
    }, 120);
    return () => window.clearTimeout(fitTimer);
  }, [
    activeView,
    isProjectFlowFullscreen,
    projectFlowRenderMeta.memberCount,
    projectFlowRenderMeta.taskCount,
    projectFlowNodes.length,
    projectFlowEdges.length,
  ]);

  useEffect(() => {
    if (activeView !== "projectFlow" || !projectFlowCanvasRef.current) {
      return;
    }
    const observer = new ResizeObserver(() => {
      window.setTimeout(() => {
        projectFlowInstanceRef.current?.fitView?.({
          padding: 0.18,
          duration: 180,
        });
      }, 80);
    });
    observer.observe(projectFlowCanvasRef.current);
    return () => observer.disconnect();
  }, [activeView]);

  function getProjectName(projectId: string) {
    const project = overviewProjects.find((item) => item.project_id === projectId);
    return project?.project_name || "未归属项目";
  }

  function getWeekStartTime() {
    const current = new Date();
    const day = current.getDay() || 7;
    current.setDate(current.getDate() - day + 1);
    current.setHours(0, 0, 0, 0);
    return current.getTime();
  }

  function getActiveProjectIds(projects = apiProjects) {
    return projects
      .filter((project) => project.status !== "done")
      .map((project) => project.project_id)
      .filter(Boolean);
  }

  async function fetchWeekPlanTasks() {
    if (!isLogin || !userId || weekPlanLoading) {
      return;
    }
    const activeProjectIds = getActiveProjectIds();
    if (!activeProjectIds.length) {
      setWeekPlanTasks([]);
      setWeekPlanLoaded(true);
      return;
    }

    setWeekPlanLoading(true);
    try {
      const api = createApi();
      const { data } = await api.post("/task/list", {
        project_ids: activeProjectIds,
        pageIndex: 0,
        pageSize: 1000,
      });
      if (data.code !== 0) {
        message.error(data.message || "获取本周计划失败");
        return;
      }

      setWeekPlanTasks(data.data?.list || []);
      setWeekPlanLoaded(true);
    } catch {
      message.error("获取本周计划失败");
    } finally {
      setWeekPlanLoading(false);
    }
  }

  function handleWeekPlanOpenChange(open: boolean) {
    if (open) {
      fetchWeekPlanTasks();
    }
  }

  function getMemberCalendarRange() {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const startTime = today.getTime();
    const endTime = startTime + 30 * 24 * 60 * 60 * 1000;
    return { startTime, endTime };
  }

  async function fetchMemberCalendarTasks(memberId: string) {
    if (!memberId) {
      return;
    }
    const calendarProjectIds = getActiveProjectIds();
    if (!calendarProjectIds.length) {
      setMemberCalendarTasks((prev) => ({
        ...prev,
        [memberId]: [],
      }));
      return;
    }
    setMemberCalendarLoading(true);
    try {
      const { data } = await createApi().post("/task/list", {
        project_ids: calendarProjectIds,
        pageIndex: 0,
        pageSize: 1000,
      });
      if (data.code !== 0) {
        message.error(data.message || "获取成员任务日历失败");
        return;
      }
      setMemberCalendarTasks((prev) => ({
        ...prev,
        [memberId]: data.data?.list || [],
      }));
    } catch {
      message.error("获取成员任务日历失败");
    } finally {
      setMemberCalendarLoading(false);
    }
  }

  function openMemberCalendar(member: TeamMember) {
    setSelectedMemberCalendar(member);
    fetchMemberCalendarTasks(member.username);
  }

  function getWeekPlanGroups() {
    const weekStart = getWeekStartTime();
    const weekEnd = weekStart + 7 * 24 * 60 * 60 * 1000;
    const teamProjectIds = new Set(apiProjects.map((project) => project.project_id));
    const isNormalMember = !isTeamAdmin && userRole !== 1;
    let weekTasks: WeekPlanDisplayItem[];

    const contentSelections = isNormalMember
      ? selectMemberWeeklyContentItems(
          weekPlanTasks,
          teamProjectIds,
          userId,
          weekStart,
          weekEnd
        )
      : selectProjectWeeklyContentItems(
          weekPlanTasks,
          teamProjectIds,
          weekStart,
          weekEnd
        );
    weekTasks = contentSelections
        .map(({ task, subtask, contentItem }): WeekPlanDisplayItem => {
          const text = getTaskContentText(contentItem.content);
          return {
            id: `${task.id || task.project_id}-${subtask.id || subtask.title}-${
              contentItem.item_id
            }`,
            project_id: task.project_id,
            title:
              text ||
              (getTaskImageCount(contentItem.content)
                ? "图片内容"
                : "未填写内容"),
            context: `${
              PROJECT_PHASE_LABELS[task.phase || "initiation"]
            } · ${subtask.title || "未命名子任务"}`,
            assignees: Array.from(contentItem.assignees),
            start_time: contentItem.start_time,
            due_time: contentItem.due_time,
            priority: contentItem.priority,
            status: contentItem.completed ? "done" : "todo",
            is_content_item: true,
          };
        })
        .sort(
          (prev, next) =>
            Number(prev.status === "done") - Number(next.status === "done") ||
            (prev.start_time || 0) - (next.start_time || 0) ||
            (prev.due_time || 0) - (next.due_time || 0)
        );

    return Array.from({ length: 7 }).map((_, index) => {
      const date = new Date(weekStart + index * 24 * 60 * 60 * 1000);
      const dateStart = date.getTime();
      const dateEnd = dateStart + 24 * 60 * 60 * 1000;
      return {
        key: date.toISOString(),
        label: new Intl.DateTimeFormat("zh-CN", {
          month: "2-digit",
          day: "2-digit",
          weekday: "short",
        }).format(date),
        tasks: weekTasks.filter((task) => {
          return isContentItemActiveInRange(
            {
              start_time: task.start_time || 0,
              due_time: task.due_time || 0,
            },
            dateStart,
            dateEnd
          );
        }),
      };
    });
  }

  function getMemberMonthTaskGroups(memberId?: string) {
    const { startTime, endTime } = getMemberCalendarRange();
    const projectIds = new Set(getActiveProjectIds());
    const contentItems: MemberCalendarDisplayItem[] = memberId
      ? selectMemberCalendarContentItems(
          memberCalendarTasks[memberId] || [],
          projectIds,
          memberId,
          startTime,
          endTime
        )
          .map(({ task, subtask, contentItem, participantRole }) => {
            const contentText = getTaskContentText(contentItem.content);
            const state: MemberCalendarDisplayItem["state"] = contentItem.completed
              ? "done"
              : contentItem.due_time < Date.now()
              ? "overdue"
              : "pending";
            return {
              id: `${task.id || task.project_id}-${subtask.id || subtask.title}-${
                contentItem.item_id
              }`,
              project_id: task.project_id,
              phase: task.phase || "initiation",
              subtask_title: subtask.title,
              title:
                contentText ||
                (getTaskImageCount(contentItem.content)
                  ? "图片内容"
                  : "未填写内容"),
              start_time: contentItem.start_time,
              due_time: contentItem.due_time,
              priority: contentItem.priority,
              participant_role: participantRole,
              state,
            };
          })
          .sort(
            (prev, next) =>
              Number(prev.state === "done") - Number(next.state === "done") ||
              prev.start_time - next.start_time ||
              prev.due_time - next.due_time
          )
      : [];

    return Array.from({ length: 30 }).map((_, index) => {
      const date = new Date(startTime + index * 24 * 60 * 60 * 1000);
      const dateStart = date.getTime();
      const dateEnd = dateStart + 24 * 60 * 60 * 1000;
      const tasks = contentItems.filter((contentItem) =>
        isContentItemActiveInRange(
          contentItem,
          dateStart,
          dateEnd
        )
      );
      return {
        key: date.toISOString(),
        day: date.getDate(),
        label: new Intl.DateTimeFormat("zh-CN", {
          month: "2-digit",
          day: "2-digit",
          weekday: "short",
        }).format(date),
        tasks,
        isToday: index === 0,
      };
    });
  }

  function renderMemberCalendarDayTasks(tasks: MemberCalendarDisplayItem[]) {
    if (!tasks.length) {
      return <div className="memberCalendarTooltip__empty">当天暂无任务内容</div>;
    }
    return (
      <div className="memberCalendarTooltip">
        {tasks.map((task) => (
          <div
            className={`memberCalendarTooltip__task is-${task.state}`}
            key={task.id}
          >
            <div>
              <strong title={task.title}>{task.title}</strong>
              <span>
                {getProjectName(task.project_id)} ·{" "}
                {PROJECT_PHASE_LABELS[task.phase]} · {task.subtask_title}
              </span>
              <small>
                {task.participant_role === "assignee" ? "负责人" : "审核人"} ·{" "}
                {formatTaskDueTime(task.start_time)} 至{" "}
                {formatTaskDueTime(task.due_time)}
              </small>
            </div>
            <div className="memberCalendarTooltip__badges">
              <TaskPriorityTag priority={task.priority} />
              <span
                className={`memberCalendarStatus memberCalendarStatus--${task.state}`}
              >
                {task.state === "done"
                  ? "已完成"
                  : task.state === "overdue"
                  ? "已逾期"
                  : "未完成"}
              </span>
            </div>
          </div>
        ))}
      </div>
    );
  }

  function closeProjectModal() {
    setIsProjectModalOpen(false);
    setEditingProject(null);
    setProjectAvatar("");
    setProjectRequirementHtml("");
    projectForm.resetFields();
  }

  function openProjectModal() {
    setEditingProject(null);
    projectForm.resetFields();
    projectForm.setFieldsValue({ template: "phased" });
    setProjectAvatar("");
    setProjectRequirementHtml("");
    setIsProjectModalOpen(true);
  }

  function openProjectEditModal(project: ApiProjectItem) {
    if (!canEditProject(project)) {
      message.warning("你没有权限编辑该项目");
      return;
    }
    setEditingProject(project);
    setProjectAvatar(project.avatar || "");
    setProjectRequirementHtml(
      normalizeProjectRequirementMarkdown(project.remark)
    );
    projectForm.setFieldsValue({
      name: project.project_name,
      template: project.template,
      members: project.members
        .map((member) => member.user_id)
        .filter(
          (memberId) =>
            memberId !== project.creator &&
            memberId !== project.team_admin_id
        ),
    });
    setIsProjectModalOpen(true);
  }

  function getProjectMemberSelectOptions() {
    const optionMap = new Map(
      projectMemberOptions.map((option) => [option.value, option])
    );
    (editingProject?.members || []).forEach((member) => {
      if (!optionMap.has(member.user_id)) {
        const displayName =
          getMemberDisplayName(member.user_id) || member.user_id;
        optionMap.set(member.user_id, {
          label: `${displayName}（${member.user_id}）`,
          value: member.user_id,
        });
      }
    });
    return Array.from(optionMap.values()).filter(
      (option) =>
        option.value !== editingProject?.creator &&
        option.value !== editingProject?.team_admin_id
    );
  }

  function selectAllProjectMembers() {
    projectForm.setFieldsValue({
      members: getProjectMemberSelectOptions().map((option) => option.value),
    });
  }

  function getProjectTaskAssigneeOptions(project: ApiProjectItem) {
    return buildProjectTaskParticipantOptions(project, teamMembers);
  }

  function applyAutoAddedProjectMembers(
    projectId: string,
    addedMembers?: ApiProjectMember[]
  ) {
    if (!addedMembers?.length) return;
    const mergeMembers = (members: ApiProjectMember[]) => {
      const memberMap = new Map(
        members.map((member) => [member.user_id, member])
      );
      addedMembers.forEach((member) => memberMap.set(member.user_id, member));
      return Array.from(memberMap.values());
    };
    setApiProjects((current) =>
      current.map((project) =>
        project.project_id === projectId
          ? { ...project, members: mergeMembers(project.members) }
          : project
      )
    );
    setFlowProjectSnapshot((current) =>
      current?.project_id === projectId
        ? { ...current, members: mergeMembers(current.members) }
        : current
    );
  }

  async function fetchTaskSubtasks(
    task: ApiTaskItem,
    target: "detail" | "edit"
  ) {
    if (!task.id) return [];
    const setLoading =
      target === "detail"
        ? setSelectedTaskSubtasksLoading
        : setEditingTaskSubtasksLoading;
    const setList =
      target === "detail" ? setSelectedTaskSubtasks : setEditingTaskSubtasks;
    setLoading(true);
    try {
      const { data } = await createApi().post("/task/subtask/list", {
        task_id: task.id,
        project_id: task.project_id,
      });
      if (data.code !== 0) {
        message.error(data.message || "获取子任务失败");
        return [];
      }
      const list = (data.data?.list || []) as TaskSubtaskItem[];
      setList(list);
      return list;
    } catch (error: any) {
      message.error(error?.response?.data?.message || "获取子任务失败");
      return [];
    } finally {
      setLoading(false);
    }
  }

  function openTaskDetailModal(
    task: ApiTaskItem,
    focusedSubtaskId = "",
    focusedContentItemId = ""
  ) {
    setSelectedTaskNode(task);
    setSelectedTaskSubtasks([]);
    setFocusedTaskSubtaskId(focusedSubtaskId);
    setFocusedTaskContentItemId(focusedContentItemId);
    resetTaskCommentEditor();
    markTaskCommentMessagesRead(task.id || task.title);
    void fetchTaskSubtasks(task, "detail");
  }

  function applySubtaskParentUpdate(parentTask?: ApiTaskItem) {
    if (!parentTask?.id) return;
    const replace = (task: ApiTaskItem) =>
      task.id === parentTask.id ? { ...task, ...parentTask } : task;
    setFlowTaskSnapshot((current) => current.map(replace));
    setTeamTasks((current) => current.map(replace));
    setWeekPlanTasks((current) => current.map(replace));
    setMemberCalendarTasks((current) =>
      Object.fromEntries(
        Object.entries(current).map(([memberId, tasks]) => [
          memberId,
          tasks.map(replace),
        ])
      )
    );
    setProjectTaskOverviewTasks((current) => ({
      ...current,
      [parentTask.phase]: (current[parentTask.phase] || []).map(replace),
    }));
    setSelectedTaskNode((current) =>
      current?.id === parentTask.id ? { ...current, ...parentTask } : current
    );
    setEditingTaskNode((current) =>
      current?.id === parentTask.id ? { ...current, ...parentTask } : current
    );
    setCommentingTaskNode((current) =>
      current?.id === parentTask.id ? { ...current, ...parentTask } : current
    );
    if (editingTaskNodeRef.current?.id === parentTask.id) {
      taskEditorForm.setFieldsValue({ status: parentTask.status });
    }
  }

  function applyTaskSubtaskUpdate(subtask?: TaskSubtaskItem) {
    if (!subtask?.id) return;
    const replace = (item: TaskSubtaskItem) =>
      item.id === subtask.id ? { ...item, ...subtask } : item;
    setSelectedTaskSubtasks((current) => current.map(replace));
    setEditingTaskSubtasks((current) => current.map(replace));
  }

  function openTaskCreateModal(
    project: ApiProjectItem,
    connection: {
      predecessor_ids: string[];
      position?: { x: number; y: number };
    } = { predecessor_ids: [] }
  ) {
    if (!canManageTaskAssignee([])) {
      message.warning("当前阶段为只读状态");
      return;
    }
    setEditingTaskNode(null);
    setCreatingTaskProject({ project });
    setCreatingTaskSubtasks([]);
    setTaskSubtaskEditing(false);
    setPendingTaskConnection(connection);
    window.requestAnimationFrame(() => {
      taskEditorForm.setFieldsValue({
        title: "",
        due_time: undefined,
        priority: "medium",
        status: "todo",
      });
    });
    setTaskTagInput("");
    setTaskDraftTags([]);
  }

  function closeTaskCreateModal() {
    setCreatingTaskProject(null);
    setCreatingTaskSubtasks([]);
    setTaskSubtaskEditing(false);
    setPendingTaskConnection(null);
    taskEditorForm.resetFields();
    setTaskTagInput("");
    setTaskDraftTags([]);
  }

  function changeProjectFlowViewMode(mode: ProjectFlowViewMode) {
    setLocalProjectFlowDraggingNode();
    setIsProjectFlowArrangeMode(false);
    setProjectFlowRenderMode(mode);
  }

  function openTaskEditModal(task: ApiTaskItem) {
    if (!task.id) {
      message.warning("任务节点缺少 ID，无法编辑");
      return;
    }
    if (!canManageTaskAssignee(task.assignees)) {
      message.warning("当前阶段为只读状态");
      return;
    }
    setCreatingTaskProject(null);
    setCreatingTaskSubtasks([]);
    setEditingTaskNode(task);
    setEditingTaskSubtasks([]);
    setTaskSubtaskEditing(false);
    window.requestAnimationFrame(() => {
      taskEditorForm.setFieldsValue({
        title: task.title,
        due_time: task.due_time ? moment(task.due_time) : undefined,
        priority: task.priority || "medium",
        status: task.status || "todo",
      });
    });
    void fetchTaskSubtasks(task, "edit");
    setTaskTagInput("");
    setTaskDraftTags(task.tags || []);
  }

  function closeTaskEditModal() {
    setEditingTaskNode(null);
    setEditingTaskSubtasks([]);
    setTaskSubtaskEditing(false);
    taskEditorForm.resetFields();
    setTaskTagInput("");
    setTaskDraftTags([]);
  }

  function syncCreatingTaskStatus(subtasks: TaskSubtaskItem[]) {
    if (!subtasks.length) {
      taskEditorForm.setFieldsValue({ status: "todo" });
      return;
    }
    if (subtasks.every((subtask) => subtask.status === "done")) {
      taskEditorForm.setFieldsValue({ status: "done" });
      return;
    }
    if (taskEditorForm.getFieldValue("status") === "done") {
      taskEditorForm.setFieldsValue({ status: "doing" });
    }
  }

  async function createTaskDraftSubtask(draft: TaskSubtaskDraft) {
    const now = Date.now() + taskDraftSubtaskIdRef.current++;
    const next = [
      ...creatingTaskSubtasks,
      {
        id: `draft-${now}`,
        parent_task_id: "",
        project_id: creatingTaskProject?.project.project_id || "",
        phase: activeProjectPhase,
        creator: userId,
        status: "todo" as const,
        revision: 0,
        created_at: now,
        updated_at: now,
        ...draft,
      },
    ];
    setCreatingTaskSubtasks(next);
    syncCreatingTaskStatus(next);
    return true;
  }

  async function updateTaskDraftSubtask(
    subtask: TaskSubtaskItem,
    draft: TaskSubtaskDraft
  ) {
    const next = creatingTaskSubtasks.map((item) =>
      item.id === subtask.id ? { ...item, ...draft, updated_at: Date.now() } : item
    );
    setCreatingTaskSubtasks(next);
    syncCreatingTaskStatus(next);
    return true;
  }

  async function deleteTaskDraftSubtask(subtask: TaskSubtaskItem) {
    const next = creatingTaskSubtasks.filter((item) => item.id !== subtask.id);
    setCreatingTaskSubtasks(next);
    syncCreatingTaskStatus(next);
    return true;
  }

  function getTaskEditorStatusOptions(subtasks: TaskSubtaskItem[]) {
    const completed = subtasks.filter((subtask) => subtask.status === "done").length;
    const allCompleted = subtasks.length > 0 && completed === subtasks.length;
    const hasIncomplete = subtasks.length > 0 && completed < subtasks.length;
    return [
      { label: "待处理", value: "todo", disabled: allCompleted },
      { label: "进行中", value: "doing", disabled: allCompleted },
      { label: "阻塞", value: "blocked", disabled: allCompleted },
      { label: "评审中", value: "review", disabled: allCompleted },
      { label: "已完成", value: "done", disabled: hasIncomplete },
      ...(!creatingTaskProject
        ? [
            {
              label: "废弃",
              value: "discarded",
              disabled: editingTaskNode?.status === "discarded",
            },
          ]
        : []),
    ];
  }

  function getDueDelayDays(previousDue?: number, nextDue?: number) {
    if (!previousDue || !nextDue) {
      return 0;
    }
    const normalizeDate = (value: number) => {
      const date = new Date(value);
      date.setHours(0, 0, 0, 0);
      return date.getTime();
    };
    const previousDay = normalizeDate(previousDue);
    const nextDay = normalizeDate(nextDue);
    if (nextDay <= previousDay) {
      return 0;
    }
    const dayMs = 24 * 60 * 60 * 1000;
    return Math.max(1, Math.round((nextDay - previousDay) / dayMs));
  }

  function openCascadeDelayConfirm(options: {
    days: number;
    scope: "main";
    taskTitle: string;
    onSave: (cascade: boolean) => Promise<boolean>;
  }) {
    setCascadeDelayConfirm(options);
  }

  function openAnnouncementModal() {
    if (!flowProject) {
      return;
    }
    announcementForm.setFieldsValue({ action_required: true });
    setAnnouncementContentHtml("");
    setAnnouncementReceivers([]);
    setAnnouncementReceiverPicker(undefined);
    setAnnouncementReceiverSelectKey((prev) => prev + 1);
    setIsAnnouncementModalOpen(true);
    window.requestAnimationFrame(() => {
      if (announcementContentEditorRef.current) {
        announcementContentEditorRef.current.innerHTML = "";
      }
    });
  }

  function closeAnnouncementModal() {
    setIsAnnouncementModalOpen(false);
    announcementForm.resetFields();
    setAnnouncementContentHtml("");
    setAnnouncementReceivers([]);
    setAnnouncementReceiverPicker(undefined);
    setAnnouncementReceiverSelectKey((prev) => prev + 1);
    if (announcementContentEditorRef.current) {
      announcementContentEditorRef.current.innerHTML = "";
    }
  }

  function handleAnnouncementContentInput() {
    setAnnouncementContentHtml(
      announcementContentEditorRef.current?.innerHTML || ""
    );
  }

  function appendAnnouncementContentImage(src: string) {
    if (!announcementContentEditorRef.current) {
      return;
    }
    const img = document.createElement("img");
    img.src = src;
    img.alt = "公告图片";
    announcementContentEditorRef.current.appendChild(img);
    announcementContentEditorRef.current.appendChild(document.createElement("br"));
    setAnnouncementContentHtml(announcementContentEditorRef.current.innerHTML);
  }

  function handleAnnouncementContentPaste(
    event: React.ClipboardEvent<HTMLDivElement>
  ) {
    const files = Array.from(event.clipboardData.files || []);
    const imageFiles = files.filter((file) => file.type.startsWith("image/"));
    if (!imageFiles.length) {
      return;
    }
    event.preventDefault();
    imageFiles.forEach((file) => {
      const reader = new FileReader();
      reader.onload = () =>
        appendAnnouncementContentImage(String(reader.result || ""));
      reader.readAsDataURL(file);
    });
  }

  function resetTaskCommentEditor() {
    setTaskCommentHtml("");
    setTaskCommentSubtaskIds([]);
    setEditingTaskComment(null);
    if (taskCommentEditorRef.current) {
      taskCommentEditorRef.current.innerHTML = "";
    }
  }

  function closeTaskCommentModal() {
    setCommentingTaskNode(null);
    resetTaskCommentEditor();
  }

  function openTaskCommentModal(
    task: ApiTaskItem,
    comment?: ApiTaskComment,
    initialSubtaskIds: string[] = []
  ) {
    setCommentingTaskNode(task);
    if (selectedTaskNode?.id !== task.id) {
      setSelectedTaskSubtasks([]);
    }
    void fetchTaskSubtasks(task, "detail");
    if (comment) {
      setEditingTaskComment(comment);
      setTaskCommentHtml(comment.content);
      setTaskCommentSubtaskIds(comment.subtask_ids || []);
      window.requestAnimationFrame(() => {
        if (taskCommentEditorRef.current) {
          taskCommentEditorRef.current.innerHTML = comment.content;
        }
      });
      return;
    }
    resetTaskCommentEditor();
    setTaskCommentSubtaskIds(initialSubtaskIds);
    window.setTimeout(() => {
      taskCommentEditorRef.current?.focus();
    }, 80);
  }

  function handleTaskCommentInput() {
    setTaskCommentHtml(taskCommentEditorRef.current?.innerHTML || "");
  }

  function appendTaskCommentImage(src: string) {
    if (!taskCommentEditorRef.current) {
      return;
    }
    const img = document.createElement("img");
    img.src = src;
    img.alt = "批注图片";
    taskCommentEditorRef.current.appendChild(img);
    taskCommentEditorRef.current.appendChild(document.createElement("br"));
    setTaskCommentHtml(taskCommentEditorRef.current.innerHTML);
  }

  function handleTaskCommentPaste(event: React.ClipboardEvent<HTMLDivElement>) {
    const files = Array.from(event.clipboardData.files || []);
    const imageFiles = files.filter((file) => file.type.startsWith("image/"));
    if (!imageFiles.length) {
      return;
    }
    event.preventDefault();
    imageFiles.forEach((file) => {
      const reader = new FileReader();
      reader.onload = () => appendTaskCommentImage(String(reader.result || ""));
      reader.readAsDataURL(file);
    });
  }

  function canCommentSelectedTask() {
    return isCurrentProjectPhaseEditable;
  }

  function editTaskFromDetail(task: ApiTaskItem) {
    openTaskEditModal(task);
  }

  function deleteTaskFromDetail(task: ApiTaskItem) {
    setConfirmingTaskDelete(task);
  }

  function updateTaskCommentsInState(
    taskId: string,
    comments: ApiTaskComment[],
    _parentTaskId?: string
  ) {
    const applyComments = (task: ApiTaskItem) => {
      return task.id === taskId ? { ...task, comments, updated_at: Date.now() } : task;
    };
    setTeamTasks((prev) => prev.map(applyComments));
    setFlowTaskSnapshot((prev) => prev.map(applyComments));
    setSelectedTaskNode((prev) =>
      prev && prev.id === taskId ? { ...prev, comments, updated_at: Date.now() } : prev
    );
    setCommentingTaskNode((prev) =>
      prev && prev.id === taskId ? { ...prev, comments, updated_at: Date.now() } : prev
    );
  }

  function updateTaskCommentInState(
    taskId: string,
    commentId: string,
    updateComment: (comment: ApiTaskComment) => ApiTaskComment
  ) {
    const applyCommentUpdate = (task: ApiTaskItem) => {
      if (task.id !== taskId) return task;
      return {
        ...task,
        comments: (task.comments || []).map((comment) =>
          comment.comment_id === commentId
            ? updateComment(comment)
            : comment
        ),
        updated_at: Date.now(),
      };
    };
    setTeamTasks((prev) => prev.map(applyCommentUpdate));
    setFlowTaskSnapshot((prev) => prev.map(applyCommentUpdate));
    setSelectedTaskNode((prev) =>
      prev?.id === taskId ? applyCommentUpdate(prev) : prev
    );
    setCommentingTaskNode((prev) =>
      prev?.id === taskId ? applyCommentUpdate(prev) : prev
    );
  }

  async function submitTaskComment() {
    if (!commentingTaskNode?.id) {
      message.warning("请先选择任务节点");
      return;
    }
    if (!canCommentSelectedTask()) {
      message.warning("你不是该项目成员，不能批注");
      return;
    }
    if (!getTaskContentText(taskCommentHtml) && !getTaskImageCount(taskCommentHtml)) {
      message.warning("请输入批注内容");
      return;
    }

    await runMutation(`task:comment:${commentingTaskNode.id}`, () =>
      performSubmitTaskComment()
    );
  }

  async function performSubmitTaskComment() {
    if (!commentingTaskNode?.id) {
      return;
    }

    const taskId = commentingTaskNode.id;
    const baseParams = {
      task_id: taskId,
      project_id: commentingTaskNode.project_id,
      operator: userId,
      content: taskCommentHtml,
      subtask_ids: taskCommentSubtaskIds,
    };
    try {
      if (editingTaskComment) {
        const { data } = await createApi().post("/task/comment/update", {
          ...baseParams,
          comment_id: editingTaskComment.comment_id,
        });
        if (data.code !== 0) {
          message.error(data.message || "更新批注失败");
          return;
        }
        const comments = (commentingTaskNode.comments || []).map((comment) =>
          comment.comment_id === editingTaskComment.comment_id
            ? {
                ...comment,
                content: taskCommentHtml,
                subtask_ids: taskCommentSubtaskIds,
                updated_at: Date.now(),
              }
            : comment
        );
        updateTaskCommentsInState(taskId, comments);
        message.success("批注已更新");
        closeTaskCommentModal();
      } else {
        const { data } = await createApi().post("/task/comment/add", baseParams);
        if (data.code !== 0) {
          message.error(data.message || "新增批注失败");
          return;
        }
        const comments = [
          ...(commentingTaskNode.comments || []),
          data.data?.comment,
        ].filter(Boolean);
        updateTaskCommentsInState(taskId, comments);
        message.success("批注已发布");
        closeTaskCommentModal();
      }
      await fetchProjectLogs(commentingTaskNode.project_id);
    } catch {
      message.error(editingTaskComment ? "更新批注失败" : "新增批注失败");
    }
  }

  async function deleteTaskComment(
    comment: ApiTaskComment,
    taskOverride?: ApiTaskItem
  ) {
    const targetTask = taskOverride || commentingTaskNode;
    if (!targetTask?.id) {
      return;
    }
    if (comment.author !== userId) {
      message.warning("只能删除自己的批注");
      return;
    }
    await runMutation(`task:comment:delete:${comment.comment_id}`, () =>
      performDeleteTaskComment(comment, targetTask)
    );
  }

  async function performDeleteTaskComment(
    comment: ApiTaskComment,
    targetTask: ApiTaskItem
  ) {
    try {
      const { data } = await createApi().post("/task/comment/delete", {
        task_id: targetTask.id,
        project_id: targetTask.project_id,
        operator: userId,
        comment_id: comment.comment_id,
      });
      if (data.code !== 0) {
        message.error(data.message || "删除批注失败");
        return;
      }
      updateTaskCommentsInState(
        targetTask.id,
        (targetTask.comments || []).filter(
          (item) => item.comment_id !== comment.comment_id
        )
      );
      if (editingTaskComment?.comment_id === comment.comment_id) {
        resetTaskCommentEditor();
      }
      await fetchProjectLogs(targetTask.project_id);
      message.success("批注已删除");
    } catch {
      message.error("删除批注失败");
    }
  }

  async function addTaskCommentReply(
    task: ApiTaskItem,
    comment: ApiTaskComment,
    content: string
  ) {
    if (!task.id) return false;
    const result = await runMutation(
      `task:comment:reply:add:${comment.comment_id}`,
      async () => {
        try {
          const { data } = await createApi().post("/task/comment/reply/add", {
            task_id: task.id,
            project_id: task.project_id,
            operator: userId,
            comment_id: comment.comment_id,
            content,
          });
          if (data.code !== 0 || !data.data?.reply) {
            message.error(data.message || "回复批注失败");
            return false;
          }
          const reply = data.data.reply as ApiTaskCommentReply;
          updateTaskCommentInState(task.id!, comment.comment_id, (current) => ({
            ...current,
            replies: [...(current.replies || []), reply],
          }));
          await fetchProjectLogs(task.project_id);
          message.success("回复已发送");
          return true;
        } catch {
          message.error("回复批注失败");
          return false;
        }
      }
    );
    return result.executed ? result.value === true : false;
  }

  async function updateTaskCommentReply(
    task: ApiTaskItem,
    comment: ApiTaskComment,
    reply: ApiTaskCommentReply,
    content: string
  ) {
    if (!task.id) return false;
    const result = await runMutation(
      `task:comment:reply:update:${reply.reply_id}`,
      async () => {
        try {
          const { data } = await createApi().post("/task/comment/reply/update", {
            task_id: task.id,
            project_id: task.project_id,
            operator: userId,
            comment_id: comment.comment_id,
            reply_id: reply.reply_id,
            content,
          });
          if (data.code !== 0) {
            message.error(data.message || "更新回复失败");
            return false;
          }
          const updatedReply = (data.data?.reply || {
            ...reply,
            content,
            updated_at: Date.now(),
          }) as ApiTaskCommentReply;
          updateTaskCommentInState(task.id!, comment.comment_id, (current) => ({
            ...current,
            replies: (current.replies || []).map((item) =>
              item.reply_id === reply.reply_id ? updatedReply : item
            ),
          }));
          await fetchProjectLogs(task.project_id);
          message.success("回复已更新");
          return true;
        } catch {
          message.error("更新回复失败");
          return false;
        }
      }
    );
    return result.executed ? result.value === true : false;
  }

  async function deleteTaskCommentReply(
    task: ApiTaskItem,
    comment: ApiTaskComment,
    reply: ApiTaskCommentReply
  ) {
    if (!task.id) return false;
    const result = await runMutation(
      `task:comment:reply:delete:${reply.reply_id}`,
      async () => {
        try {
          const { data } = await createApi().post("/task/comment/reply/delete", {
            task_id: task.id,
            project_id: task.project_id,
            operator: userId,
            comment_id: comment.comment_id,
            reply_id: reply.reply_id,
          });
          if (data.code !== 0) {
            message.error(data.message || "删除回复失败");
            return false;
          }
          updateTaskCommentInState(task.id!, comment.comment_id, (current) => ({
            ...current,
            replies: (current.replies || []).filter(
              (item) => item.reply_id !== reply.reply_id
            ),
          }));
          await fetchProjectLogs(task.project_id);
          message.success("回复已删除");
          return true;
        } catch {
          message.error("删除回复失败");
          return false;
        }
      }
    );
    return result.executed ? result.value === true : false;
  }

  function addAnnouncementReceiver(receiverId?: string) {
    if (!receiverId) {
      return;
    }
    setAnnouncementReceivers((prev) =>
      prev.includes(receiverId) ? prev : [...prev, receiverId]
    );
    setAnnouncementReceiverPicker(undefined);
    setAnnouncementReceiverSelectKey((prev) => prev + 1);
    window.setTimeout(() => setAnnouncementReceiverPicker(undefined), 0);
  }

  function removeAnnouncementReceiver(receiverId: string) {
    setAnnouncementReceivers((prev) => prev.filter((item) => item !== receiverId));
  }

  function selectAllAnnouncementReceivers() {
    if (!flowProject) {
      return;
    }
    setAnnouncementReceivers(
      getProjectAnnouncementOptions(flowProject).map((item) => item.value)
    );
  }

  function addTaskDraftTag() {
    const nextTag = taskTagInput.trim();
    if (!nextTag) {
      return;
    }
    setTaskDraftTags((prev) =>
      prev.includes(nextTag) ? prev : [...prev, nextTag]
    );
    setTaskTagInput("");
  }

  function removeTaskDraftTag(tag: string) {
    setTaskDraftTags((prev) => prev.filter((item) => item !== tag));
  }

  async function refreshTaskList() {
    if (activeView === "projectFlow" && flowProjectId) {
      await fetchProjectFlowSnapshot(flowProjectId, {
        silent: true,
        showLoading: false,
      });
      return;
    }

    const params =
      isTeamAdmin || userRole === 1
        ? { pageIndex: 0, pageSize: 300 }
        : { assignee_id: userId, pageIndex: 0, pageSize: 300 };
    const { data } = await createApi().post("/task/list", params);
    if (data.code === 0) {
      const nextTasks = data.data?.list || [];
      setTeamTasks(nextTasks);
    }
  }

  async function fetchProjectFlowSnapshot(
    projectId: string,
    options: { silent?: boolean; showLoading?: boolean } = {}
  ) {
    if (!projectId || !userId) {
      return { success: false, graphVersion: 0 };
    }
    const requestId = ++projectFlowSnapshotRequestRef.current;
    const showLoading = options.showLoading !== false;
    if (showLoading) {
      projectFlowLoadingRequestRef.current = requestId;
      setProjectFlowLoading(true);
    }
    setProjectFlowLoadFailed(false);
    const requestedPhase = activeProjectPhaseRef.current;
    try {
      const api = createApi();
      const snapshot = await loadStableProjectFlowSnapshot({
        loadProject: async () => {
          const { data } = await api.post("/project/detail", {
            project_id: projectId,
          });
          if (data.code !== 0 || !data.data) {
            throw new Error(data.message || "项目不存在或无权访问");
          }
          return normalizeProjectPhaseRuntimeFields(
            data.data as ApiProjectItem
          );
        },
        loadTasks: async () => {
          const { data } = await api.post(
            "/task/list",
            getProjectTaskListBody(projectId, requestedPhase)
          );
          if (data.code !== 0) {
            throw new Error(data.message || "任务节点加载失败");
          }
          return (data.data?.list || []) as ApiTaskItem[];
        },
        getVersion: (project) => project.flow_graph_versions[requestedPhase],
        maxAttempts: 2,
      });

      if (
        requestId !== projectFlowSnapshotRequestRef.current ||
        flowProjectId !== projectId ||
        activeProjectPhaseRef.current !== requestedPhase
      ) {
        return { success: false, graphVersion: 0 };
      }

      const nextProject = snapshot.project;
      const nextTasks = snapshot.tasks;
      setFlowProjectSnapshot(nextProject);
      setApiProjects((prev) => {
        const exists = prev.some(
          (project) => project.project_id === nextProject.project_id
        );
        return exists
          ? prev.map((project) =>
              project.project_id === nextProject.project_id
                ? { ...project, ...nextProject }
                : project
            )
          : [nextProject, ...prev];
      });
      setFlowTaskSnapshot(nextTasks);
      setSelectedTaskNode((current) =>
        current?.id
          ? nextTasks.find((task) => task.id === current.id) || null
          : current
      );
      setCommentingTaskNode((current) =>
        current?.id
          ? nextTasks.find((task) => task.id === current.id) || null
          : current
      );
      setTeamTasks((prev) => [
        ...prev.filter(
          (task) =>
            task.project_id !== projectId || task.phase !== requestedPhase
        ),
        ...nextTasks,
      ]);
      return {
        success: true,
        graphVersion: snapshot.version,
      };
    } catch {
      if (requestId !== projectFlowSnapshotRequestRef.current) {
        return { success: false, graphVersion: 0 };
      }
      setProjectFlowLoadFailed(true);
      if (!options.silent) {
        message.error("节点图数据加载失败");
      }
      return { success: false, graphVersion: 0 };
    } finally {
      if (
        shouldFinishProjectFlowLoading(
          showLoading,
          requestId,
          projectFlowLoadingRequestRef.current
        )
      ) {
        setProjectFlowLoading(false);
      }
    }
  }

  async function openProjectTaskOverview() {
    if (!flowProject) return;
    const projectId = flowProject.project_id;
    setProjectTaskOverviewTasks(
      PROJECT_PHASES.reduce((result, phase) => {
        result[phase] = [];
        return result;
      }, {} as Record<ProjectPhase, ApiTaskItem[]>)
    );
    setIsProjectTaskOverviewOpen(true);
    setProjectTaskOverviewLoading(true);
    try {
      const api = createApi();
      const phaseResults = await Promise.all(
        PROJECT_PHASES.map(async (phase) => {
          const { data } = await api.post(
            "/task/list",
            getProjectTaskListBody(projectId, phase)
          );
          if (data.code !== 0) {
            throw new Error(data.message || `${PROJECT_PHASE_LABELS[phase]}任务加载失败`);
          }
          return [phase, (data.data?.list || []) as ApiTaskItem[]] as const;
        })
      );
      if (flowProjectId !== projectId) return;
      setProjectTaskOverviewTasks(
        phaseResults.reduce((result, [phase, tasks]) => {
          result[phase] = tasks;
          return result;
        }, {} as Record<ProjectPhase, ApiTaskItem[]>)
      );
    } catch (error: any) {
      message.error(error?.message || "项目任务总览加载失败");
    } finally {
      if (flowProjectId === projectId) {
        setProjectTaskOverviewLoading(false);
      }
    }
  }

  function openOverviewPhase(phase: ProjectPhase) {
    setIsProjectTaskOverviewOpen(false);
    if (phase !== activeProjectPhaseRef.current) {
      void changeProjectPhase(phase);
    }
  }

  function openOverviewTask(task: ProjectTaskOverviewTask) {
    setIsProjectTaskOverviewOpen(false);
    if (task.phase !== activeProjectPhaseRef.current) {
      void changeProjectPhase(task.phase);
    }
    openTaskDetailModal(task as ApiTaskItem);
  }

  async function fetchProjectLogs(
    projectId: string,
    options: { silent?: boolean; showLoading?: boolean } = {}
  ) {
    if (!projectId) {
      setProjectLogList([]);
      return;
    }
    if (options.showLoading !== false) {
      setProjectLogLoading(true);
    }
    try {
      const { data } = await createApi().post("/project/log/list", {
        project_id: projectId,
        pageIndex: 0,
        pageSize: 100,
      });
      if (data.code !== 0) {
        if (!options.silent) {
          message.error(data.message || "获取项目日志失败");
        }
        return;
      }
      setProjectLogList(data.data?.list || []);
    } catch {
      if (!options.silent) {
        message.error("获取项目日志失败");
      }
    } finally {
      if (options.showLoading !== false) {
        setProjectLogLoading(false);
      }
    }
  }

  async function refreshProjectFlowPanelData(
    projectId: string,
    tab: ProjectFlowPanelTab,
    options: { silent?: boolean; showLoading?: boolean } = {},
    refreshOptions: { refreshSnapshot?: boolean } = {}
  ) {
    if (!projectId || projectFlowAutoRefreshRef.current) {
      return;
    }

    projectFlowAutoRefreshRef.current = true;
    try {
      if (refreshOptions.refreshSnapshot !== false) {
        await fetchProjectFlowSnapshot(projectId, options);
      } else {
        const { data } = await createApi().post("/project/detail", {
          project_id: projectId,
        });
        if (data.code === 0 && data.data) {
          const nextProject = data.data;
          const authoritativeVersion =
            nextProject.flow_graph_versions[activeProjectPhaseRef.current];
          if (authoritativeVersion > projectFlowGraphVersionRef.current) {
            projectFlowPendingGraphVersionRef.current = Math.max(
              projectFlowPendingGraphVersionRef.current,
              authoritativeVersion
            );
            queueProjectFlowGraphRefresh(projectId);
          }
          setFlowProjectSnapshot(nextProject);
          setApiProjects((prev) =>
            prev.map((project) =>
              project.project_id === projectId
                ? { ...project, ...nextProject }
                : project
            )
          );
        }
      }
      if (tab === "announcements") {
        await fetchProjectAnnouncements(projectId, options);
      }
      if (tab === "logs") {
        await fetchProjectLogs(projectId, options);
      }
    } finally {
      projectFlowAutoRefreshRef.current = false;
    }
  }

  async function uploadProjectAttachment(file: File) {
    await runMutation(
      `attachment:upload:${file.name}:${file.size}:${file.lastModified}`,
      () => performUploadProjectAttachment(file)
    );
  }

  async function performUploadProjectAttachment(file: File) {
    if (!flowProject || !userId) {
      return false;
    }
    if (file.size > 50 * 1024 * 1024) {
      message.warning("单个附件不能超过 50MB");
      return false;
    }
    try {
      const formData = new FormData();
      formData.append("project_id", flowProject.project_id);
      formData.append("operator", userId);
      formData.append("file", file);
      const { data } = await createApi().post(
        "/project/attachment/upload",
        formData
      );
      if (data.code !== 0) {
        message.error(data.message || "上传附件失败");
        return false;
      }
      const detailRes = await createApi().post("/project/detail", {
        project_id: flowProject.project_id,
      });
      const nextProject =
        detailRes.data.code === 0 && detailRes.data.data
          ? detailRes.data.data
          : {
              ...flowProject,
              attachments: [
                ...(flowProject.attachments || []),
                data.data.attachment,
              ],
            };
      setApiProjects((prev) =>
        prev.map((project) =>
          project.project_id === flowProject.project_id
            ? { ...project, ...nextProject }
            : project
        )
      );
      setFlowProjectSnapshot((prev) =>
        prev && prev.project_id === flowProject.project_id
          ? { ...prev, ...nextProject }
          : prev
      );
      await fetchProjectLogs(flowProject.project_id);
      message.success("项目附件已上传");
    } catch {
      message.error("上传附件失败");
    }
    return false;
  }

  async function removeProjectAttachment(attachmentId: string) {
    await runMutation(`attachment:remove:${attachmentId}`, () =>
      performRemoveProjectAttachment(attachmentId)
    );
  }

  async function performRemoveProjectAttachment(attachmentId: string) {
    if (!flowProject || !userId) {
      return;
    }
    try {
      const { data } = await createApi().post("/project/attachment/delete", {
        project_id: flowProject.project_id,
        attachment_id: attachmentId,
        operator: userId,
      });
      if (data.code !== 0) {
        message.error(data.message || "删除附件失败");
        return;
      }
      const detailRes = await createApi().post("/project/detail", {
        project_id: flowProject.project_id,
      });
      const nextProject =
        detailRes.data.code === 0 && detailRes.data.data
          ? detailRes.data.data
          : {
              ...flowProject,
              attachments: (flowProject.attachments || []).filter(
                (item) => item.attachment_id !== attachmentId
              ),
            };
      setApiProjects((prev) =>
        prev.map((project) =>
          project.project_id === flowProject.project_id
            ? { ...project, ...nextProject }
            : project
        )
      );
      setFlowProjectSnapshot((prev) =>
        prev && prev.project_id === flowProject.project_id
          ? { ...prev, ...nextProject }
          : prev
      );
      await fetchProjectLogs(flowProject.project_id);
      message.success("项目附件已移除");
    } catch {
      message.error("删除附件失败");
    }
  }

  function canRemoveProjectMember(member: ApiProjectMember) {
    if (!flowProject || !userId) {
      return false;
    }
    if (
      member.user_id === flowProject.creator ||
      member.user_id === flowProject.team_admin_id
    ) {
      return false;
    }
    return canManageFlowProjectMembers;
  }

  async function removeProjectMember(member: ApiProjectMember) {
    await runMutation(`project:member:remove:${member.user_id}`, () =>
      performRemoveProjectMember(member)
    );
  }

  async function performRemoveProjectMember(member: ApiProjectMember) {
    if (!flowProject || !userId) {
      return;
    }
    try {
      const { data } = await createApi().post("/project/member/remove", {
        project_id: flowProject.project_id,
        user_id: member.user_id,
        operator: userId,
      });
      if (data.code !== 0) {
        message.error(data.message || "移除项目成员失败");
        return;
      }
      const nextProject = {
        ...flowProject,
        members: flowProject.members.filter(
          (item) => item.user_id !== member.user_id
        ),
      };
      setApiProjects((prev) =>
        prev.map((project) =>
          project.project_id === flowProject.project_id
            ? { ...project, members: nextProject.members }
            : project
        )
      );
      setFlowProjectSnapshot((prev) =>
        prev && prev.project_id === flowProject.project_id
          ? { ...prev, members: nextProject.members }
          : prev
      );
      await fetchProjectLogs(flowProject.project_id);
      message.success("项目成员已移除，历史任务节点已保留");
    } catch {
      message.error("移除项目成员失败");
    }
  }

  function openProjectMemberAddModal() {
    if (!canManageFlowProjectMembers) {
      message.warning("只有团队管理员和项目创建人可以新增项目成员");
      return;
    }
    projectMemberAddForm.resetFields();
    setIsProjectMemberAddModalOpen(true);
  }

  function closeProjectMemberAddModal() {
    setIsProjectMemberAddModalOpen(false);
    projectMemberAddForm.resetFields();
  }

  async function addProjectMember(values: ProjectMemberAddFormValues) {
    await runMutation("project:member:add", () => performAddProjectMember(values));
  }

  async function performAddProjectMember(values: ProjectMemberAddFormValues) {
    if (!flowProject || !userId || !values.user_id) {
      return;
    }
    try {
      const { data } = await createApi().post("/project/member/add", {
        project_id: flowProject.project_id,
        user_id: values.user_id,
        operator: userId,
        role: "member",
      });
      if (data.code !== 0) {
        message.error(data.message || "新增项目成员失败");
        return;
      }
      const nextMember: ApiProjectMember = {
        user_id: values.user_id,
        role: "member",
        joined_at: Date.now(),
      };
      const nextMembers = [...flowProject.members, nextMember];
      setApiProjects((prev) =>
        prev.map((project) =>
          project.project_id === flowProject.project_id
            ? { ...project, members: nextMembers }
            : project
        )
      );
      setFlowProjectSnapshot((prev) =>
        prev && prev.project_id === flowProject.project_id
          ? { ...prev, members: nextMembers }
          : prev
      );
      await fetchProjectLogs(flowProject.project_id);
      message.success("项目成员已新增");
      closeProjectMemberAddModal();
    } catch {
      message.error("新增项目成员失败");
    }
  }

  function downloadProjectAttachment(attachment: ApiProjectAttachment) {
    const link = document.createElement("a");
    link.href = attachment.url.startsWith("http")
      ? attachment.url
      : `${baseURL}${attachment.url}`;
    link.download = attachment.name;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  const projectAttachmentUploadProps: UploadProps = {
    multiple: true,
    showUploadList: false,
    beforeUpload: (file) => uploadProjectAttachment(file),
  };

  function getProjectLogActionText(action: string) {
    const actionMap: Record<string, string> = {
      "project.create": "创建项目",
      "project.update": "更新项目",
      "project.delete": "删除项目",
      "project.archive": "归档项目",
      "project.unarchive": "撤销归档",
      "project.phase.editable": "阶段编辑权限变更",
      "task.create": "创建任务节点",
      "task.update": "更新任务节点",
      "task.discard": "废弃任务节点",
      "task.restore": "恢复任务节点",
      "task.delete": "删除任务节点",
      "task.subtask.create": "新增子任务",
      "task.subtask.update": "更新子任务",
      "task.subtask.delete": "删除子任务",
      "task.subtask.handoff": "转交子任务",
      "task.subtask.review.submit": "提交子任务审批",
      "task.subtask.complete": "完成子任务",
      "task.subtask.review.approve": "通过子任务审批",
      "task.subtask.review.reject": "驳回子任务审批",
      "task.status.change": "变更任务状态",
      "task.comment.create": "新增节点批注",
      "task.comment.update": "编辑节点批注",
      "task.comment.delete": "删除节点批注",
      "task.comment.reply.create": "回复节点批注",
      "task.comment.reply.update": "编辑批注回复",
      "task.comment.reply.delete": "删除批注回复",
      "member.add": "添加成员",
      "member.update": "更新成员",
      "member.remove": "移除成员",
      "attachment.add": "添加附件",
      "attachment.remove": "移除附件",
      "announcement.create": "发布公告",
    };
    return actionMap[action] || "项目动态";
  }

  function getProjectLogActionTone(action: string) {
    if (action === "task.restore") {
      return "create";
    }
    if (action === "task.discard") {
      return "delete";
    }
    if (action === "task.subtask.review.approve") {
      return "create";
    }
    if (action === "task.subtask.review.reject") {
      return "delete";
    }
    if (
      action.endsWith(".create") ||
      action.endsWith(".add") ||
      action === "announcement.create"
    ) {
      return "create";
    }
    if (action.endsWith(".delete") || action.endsWith(".remove")) {
      return "delete";
    }
    return "update";
  }

  function getProjectLogTargetText(targetType: ApiProjectLogItem["target_type"]) {
    const targetMap: Record<ApiProjectLogItem["target_type"], string> = {
      project: "项目",
      task: "任务节点",
      subtask: "子任务",
      member: "成员",
      attachment: "附件",
      announcement: "公告",
    };
    return targetMap[targetType] || targetType;
  }

  function getProjectLogReceiverNames(value: any) {
    const receiverIds = (Array.isArray(value) ? value : [value])
      .map((item) => String(item || "").trim())
      .filter(Boolean);
    if (!receiverIds.length) {
      return "空";
    }
    return receiverIds.map((receiverId) => getMemberDisplayName(receiverId)).join("、");
  }

  function getProjectLogSummary(log: ApiProjectLogItem) {
    const after = log.detail?.after || {};
    const before = log.detail?.before || {};
    const operatorName = getMemberDisplayName(log.operator) || log.operator;
    if (log.action === "task.discard") {
      return `${operatorName} 废弃了任务节点「${after.title || before.title || log.target_id}」`;
    }
    if (log.action === "task.restore") {
      return `${operatorName} 恢复了任务节点「${after.title || before.title || log.target_id}」`;
    }
    if (log.action === "project.phase.editable") {
      return `${operatorName} 将${
        log.detail?.phase_label ||
        (log.detail?.phase
          ? PROJECT_PHASE_LABELS[log.detail.phase]
          : "项目阶段")
      }设为${after.editable ? "编辑开放" : "只读"}`;
    }
    if (log.target_type === "announcement") {
      const receiverNames = after.recipient_ids
        ? `，查收人员：${getProjectLogReceiverNames(after.recipient_ids)}`
        : "";
      return `${operatorName} 发布了项目公告，${after.recipient_count || 0} 人查收${receiverNames}`;
    }
    if (log.target_type === "attachment") {
      const afterAttachments = after.attachments || [];
      const beforeAttachments = before.attachments || [];
      const attachment =
        after.attachment ||
        before.attachment ||
        afterAttachments.find(
          (afterItem: ApiProjectAttachment) =>
            !beforeAttachments.some(
              (beforeItem: ApiProjectAttachment) =>
                beforeItem.attachment_id === afterItem.attachment_id
            )
        ) ||
        beforeAttachments.find(
          (beforeItem: ApiProjectAttachment) =>
            !afterAttachments.some(
              (afterItem: ApiProjectAttachment) =>
                afterItem.attachment_id === beforeItem.attachment_id
            )
        );
      return `${operatorName} ${log.action === "attachment.remove" ? "移除了" : "上传了"}附件「${
        attachment?.name || log.target_id
      }」`;
    }
    if (log.target_type === "member") {
      const memberId = after.user_id || before.user_id || log.target_id;
      const memberName = getMemberDisplayName(memberId) || memberId;
      return `${operatorName} ${log.action === "member.remove" ? "移除了" : "添加了"}成员「${memberName}」`;
    }
    if (log.action.startsWith("task.comment.reply.")) {
      const snapshot = { ...before, ...after };
      const actionText =
        log.action === "task.comment.reply.delete"
          ? "删除了"
          : log.action === "task.comment.reply.update"
          ? "编辑了"
          : "回复了";
      return `${operatorName} ${actionText}节点「${
        snapshot.title || log.target_id
      }」中的一条批注回复`;
    }
    if (log.action.startsWith("task.comment.")) {
      const assigneeName = getTaskAssigneeNames(
        after.assignees || before.assignees || []
      ).join("、") || "成员";
      const actionText =
        log.action === "task.comment.delete"
          ? "删除了"
          : log.action === "task.comment.update"
          ? "编辑了"
          : "新增了";
      return `${operatorName} ${actionText} ${assigneeName} 的节点「${
        after.title || before.title || log.target_id
      }」批注`;
    }
    if (log.action.startsWith("task.subtask.")) {
      const snapshot = { ...before, ...after };
      const subtaskTitle = snapshot.title || log.target_id;
      const parentTitle = snapshot.parent_title || log.target_id;
      if (log.action === "task.subtask.handoff") {
        const transfereeNames = (snapshot.transferees || [])
          .map(getMemberDisplayName)
          .join("、");
        return `${operatorName} 将「${parentTitle}」中的子任务「${subtaskTitle}」转交给 ${transfereeNames}`;
      }
      if (log.action === "task.subtask.review.submit") {
        const reviewerNames = (snapshot.reviewers || [])
          .map(getMemberDisplayName)
          .join("、");
        return `${operatorName} 将「${parentTitle}」中的子任务「${subtaskTitle}」提交给 ${reviewerNames} 审核`;
      }
      if (log.action === "task.subtask.complete") {
        return `${operatorName} 直接完成了「${parentTitle}」中的子任务「${subtaskTitle}」`;
      }
      if (log.action === "task.subtask.review.approve") {
        return `${operatorName} 通过了「${parentTitle}」中的子任务「${subtaskTitle}」`;
      }
      if (log.action === "task.subtask.review.reject") {
        return `${operatorName} 驳回了「${parentTitle}」中的子任务「${subtaskTitle}」，原因：${
          snapshot.rejection_reason || "未填写"
        }`;
      }
      const actionText =
        log.action === "task.subtask.delete"
          ? "删除了"
          : log.action === "task.subtask.update"
          ? "更新了"
          : "新增了";
      const assigneeName = (snapshot.assignees || []).length
        ? snapshot.assignees.map(getMemberDisplayName).join("、")
        : "未分配成员";
      return `${operatorName} 为「${parentTitle}」${actionText}${assigneeName}负责的子任务「${subtaskTitle}」`;
    }
    if (after.title) {
      const assigneeName = getTaskAssigneeNames(
        after.assignees || before.assignees || []
      ).join("、") || "成员";
      return `${operatorName} 更新了 ${assigneeName} 的节点「${after.title}」`;
    }
    if (after.project_name) {
      return `${operatorName} 更新了项目「${after.project_name}」`;
    }
    if (after.status || before.status) {
      return `${operatorName} 将状态从 ${getTaskStatusText(before.status)} 改为 ${getTaskStatusText(after.status)}`;
    }
    return log.target_id;
  }

  function formatProjectLogValue(key: string, value: any) {
    if (value === undefined || value === null || value === "") {
      return "未设置";
    }
    if (key.endsWith("_at") || key === "created_at" || key === "updated_at") {
      return typeof value === "number" && value > 0
        ? formatMessageTime(value)
        : "未设置";
    }
    if (key === "status") {
      return getTaskStatusText(value);
    }
    if (key === "phase") {
      return flowProject?.template === "single"
        ? "任务视图"
        : PROJECT_PHASE_LABELS[value as ProjectPhase] || String(value);
    }
    if (key === "phase_label" && flowProject?.template === "single") {
      return "任务视图";
    }
    if (key === "editable") {
      return value ? "编辑开放" : "只读";
    }
    if (key === "priority") {
      return flowTaskPriorityText[value as NonNullable<ApiTaskItem["priority"]>] || value;
    }
    if (key === "due_time" || key === "start_time") {
      return typeof value === "number" && value > 0
        ? formatTaskDueTime(value)
        : "未设置";
    }
    if (
      [
        "announcement_content",
        "comment_content",
        "reply_content",
        "content",
      ].includes(key)
    ) {
      const html = String(value || "");
      const text = getTaskContentText(html);
      const imageCount = getTaskImageCount(html);
      if (text && imageCount) {
        return `${text}（含 ${imageCount} 张图片）`;
      }
      if (text) {
        return text;
      }
      return imageCount ? `含 ${imageCount} 张图片` : "未设置";
    }
    if (key === "recipient_ids") {
      return getProjectLogReceiverNames(value);
    }
    if (key === "role") {
      const roleMap: Record<string, string> = {
        manager: "项目管理员",
        member: "项目成员",
        developer: "开发成员",
        tester: "测试成员",
        viewer: "观察者",
      };
      return roleMap[value] || String(value);
    }
    if (key === "size" && typeof value === "number") {
      return formatFileSize(value);
    }
    if (key === "folder_id") {
      const folderMap: Record<string, string> = {
        images: "图片",
        documents: "文档",
        archives: "压缩包",
        others: "其他",
      };
      return folderMap[value] || String(value);
    }
    if (key === "assignees") {
      return (
        getTaskAssigneeNames(Array.isArray(value) ? value : []).join("、") ||
        "未设置"
      );
    }
    if (key === "reviewers") {
      return (
        (Array.isArray(value) ? value : [])
          .map(getMemberDisplayName)
          .join("、") || "无需审核"
      );
    }
    if (key === "transferees") {
      return (
        (Array.isArray(value) ? value : [])
          .map(getMemberDisplayName)
          .join("、") || "无需转交"
      );
    }
    if (key === "subtask_status") {
      const statusMap: Record<string, string> = {
        todo: "待完成",
        transfer: "待转交",
        review: "待审批",
        rejected: "已驳回",
        done: "已完成",
      };
      return statusMap[value] || String(value);
    }
    if (key === "workflow_action") {
      const actionMap: Record<string, string> = {
        handoff: "转交处理",
        submit: "提交审批",
        complete: "确认完成",
        approve: "审核通过",
        reject: "退回修改",
      };
      return actionMap[value] || String(value);
    }
    if (key === "completed") {
      return value ? "已完成" : "未完成";
    }
    if (key === "parent_status") {
      return getTaskStatusText(value);
    }
    if (key === "subtask_summary" && typeof value === "object") {
      return `${value.completed || 0} / ${value.total || 0} 已完成，进度 ${value.progress || 0}%`;
    }
    if (key === "content_items" && Array.isArray(value)) {
      if (!value.length) return "无内容项";
      return value
        .map((item, index) => {
          const html = String(item?.content || "");
          const text = getTaskContentText(html);
          const imageCount = getTaskImageCount(html);
          const content = text
            ? text.length > 96
              ? `${text.slice(0, 96)}...`
              : text
            : imageCount
            ? `图片内容（${imageCount} 张）`
            : "未填写内容";
          const dueTime = Number(item?.due_time);
          const dueText = dueTime > 0 ? formatTaskDueTime(dueTime) : "未设置";
          const startTime = Number(item?.start_time);
          const startText =
            startTime > 0 ? formatTaskDueTime(startTime) : "未设置";
          const priorityText =
            flowTaskPriorityText[item?.priority as NonNullable<
              ApiTaskItem["priority"]
            >] || "未设置";
          return `${index + 1}. ${
            item?.completed ? "已完成" : "待完成"
          } · ${priorityText} · ${startText} 至 ${dueText} · ${content}`;
        })
        .join("\n");
    }
    if (
      [
        "creator",
        "operator",
        "sender_id",
        "uploaded_by",
        "user_id",
        "comment_author",
        "reply_author",
      ].includes(key)
    ) {
      return getMemberDisplayName(value) || value;
    }
    if (key === "system") {
      return value ? "系统默认" : "自定义";
    }
    if (key === "deleted") {
      return value ? "已删除" : "未删除";
    }
    if (key === "action_required") {
      return value ? "需要查收" : "无需查收";
    }
    if (key === "receiver_scope") {
      const scopeMap: Record<string, string> = {
        all: "全部用户",
        team: "团队成员",
        users: "指定人员",
        project_members: "项目成员",
      };
      return scopeMap[value] || String(value);
    }
    if (key === "action_status") {
      const statusMap: Record<string, string> = {
        pending: "待处理",
        accepted: "已确认",
        rejected: "已拒绝",
        done: "已完成",
      };
      return statusMap[value] || String(value);
    }
    if (Array.isArray(value)) {
      return value.length ? value.join("、") : "空";
    }
    if (typeof value === "object") {
      return JSON.stringify(value, null, 2);
    }
    return String(value);
  }

  function getProjectLogFieldText(key: string) {
    const fieldMap: Record<string, string> = {
      title: "节点标题",
      project_name: "项目名称",
      project_id: "项目 ID",
      creator: "创建人",
      team_code: "团队邀请码",
      normalized_project_name: "标准化项目名",
      status: "状态",
      priority: "优先级",
      start_time: "预期开始时间",
      due_time: "预期完结时间",
      end_time: "实际完成时间",
      assignees: "负责人",
      transferees: "子任务转让人",
      user_id: "成员",
      role: "角色",
      joined_at: "加入时间",
      name: "名称",
      remark: "项目需求",
      avatar: "项目头像",
      attachment_id: "附件 ID",
      folder_id: "附件分类",
      size: "文件大小",
      type: "文件类型",
      url: "附件地址",
      uploaded_by: "上传人",
      uploaded_at: "上传时间",
      system: "文件夹类型",
      announcement: "公告",
      announcement_content: "公告内容",
      recipient_count: "查收人数",
      recipient_ids: "查收人员",
      comment: "批注",
      comment_id: "批注 ID",
      comment_author: "批注人",
      comment_content: "批注内容",
      reply_id: "回复 ID",
      reply_author: "回复人",
      reply_content: "回复内容",
      content: "节点内容",
      content_items: "子任务内容清单",
      parent_task_id: "主任务 ID",
      parent_title: "主任务名称",
      reviewers: "子任务审核人",
      subtask_ids: "关联子任务",
      subtask_status: "子任务状态",
      rejection_reason: "驳回原因",
      transferred_at: "转交时间",
      submitted_at: "提交审批时间",
      reviewed_at: "审核时间",
      workflow_action: "审批操作",
      discard_reason: "废弃原因",
      parent_status: "主任务状态",
      subtask_summary: "子任务进度摘要",
      revision: "数据版本",
      tags: "标签",
      attachment_count: "附件数量",
      deleted: "是否删除",
      sender_id: "发送人",
      receiver_scope: "接收范围",
      action_required: "是否需要查收",
      action_status: "处理状态",
      phase: "所属阶段",
      phase_label: "阶段名称",
      editable: "允许编辑",
    };
    return fieldMap[key] || key;
  }

  function getProjectLogDetailRows(log: ApiProjectLogItem | null) {
    if (!log) {
      return [];
    }
    const before = log.detail?.before || {};
    const after = log.detail?.after || {};
    const keys = Array.from(
      new Set([...Object.keys(before), ...Object.keys(after)])
    ).filter(
      (key) =>
        !["attachments", "comments"].includes(key) &&
        JSON.stringify(before[key]) !== JSON.stringify(after[key])
    );
    if (log.detail?.phase && !keys.includes("phase")) {
      const rows = [
        {
          key: "所属阶段",
          before: log.detail.phase_label || PROJECT_PHASE_LABELS[log.detail.phase],
          after: log.detail.phase_label || PROJECT_PHASE_LABELS[log.detail.phase],
        },
        ...keys.map((key) => ({
          key: getProjectLogFieldText(key),
          before: formatProjectLogValue(key, before[key]),
          after: formatProjectLogValue(key, after[key]),
        })),
      ];
      if (log.detail?.discard_reason) {
        rows.push({
          key: "废弃原因",
          before: "未设置",
          after: log.detail.discard_reason,
        });
      }
      return rows;
    }
    if (!keys.length && (Object.keys(before).length || Object.keys(after).length)) {
      return [
        {
          key: "记录",
          before: before.title || before.name || before.user_id || "-",
          after: after.title || after.name || after.user_id || "-",
        },
      ];
    }
    return keys.map((key) => ({
      key: getProjectLogFieldText(key),
      before: formatProjectLogValue(key, before[key]),
      after: formatProjectLogValue(key, after[key]),
    }));
  }

  function openDepartmentModal(departmentName?: string) {
    setEditingDepartment(departmentName || "");
    if (departmentName) {
      departmentEditForm.setFieldsValue({ name: departmentName });
    } else {
      departmentForm.resetFields();
    }
    setIsDepartmentModalOpen(true);
  }

  function closeDepartmentModal() {
    setIsDepartmentModalOpen(false);
    setEditingDepartment("");
    departmentForm.resetFields();
    departmentEditForm.resetFields();
  }

  function syncDepartmentList(nextDepartment: string[]) {
    setTeamDepartmentOptions(nextDepartment);
    updateDepartmentState(nextDepartment);
    const authSession = getAuthSession();
    saveAuthStorage({
      user: {
        ...(authSession?.user || {}),
        department: nextDepartment,
      },
    });
  }

  async function refreshTeamMembers() {
    if (!teamCode) {
      return;
    }

    const api = createApi();
    const memberRes = await api.post("/user/list", {
      team_code: teamCode,
      pageIndex: 0,
      pageSize: 1000,
    });
    if (memberRes.data.code === 0) {
      setTeamMembers(memberRes.data.data?.list || []);
    }
  }

  async function createDepartment(values: DepartmentFormValues) {
    await runMutation("department:create", () => performCreateDepartment(values));
  }

  async function performCreateDepartment(values: DepartmentFormValues) {
    const name = values.name.trim();
    if (!name) {
      message.warning("请输入部门名称");
      return;
    }

    try {
      const { data } = await createApi().post("/user/department/add", { name });
      if (data.code !== 0) {
        message.error(data.message || "新增部门失败");
        return;
      }
      syncDepartmentList(data.data?.list || []);
      message.success("部门新增成功");
      closeDepartmentModal();
    } catch {
      message.error("新增部门失败");
    }
  }

  async function updateDepartment(values: DepartmentEditFormValues) {
    await runMutation(`department:update:${editingDepartment}`, () =>
      performUpdateDepartment(values)
    );
  }

  async function performUpdateDepartment(values: DepartmentEditFormValues) {
    const name = values.name.trim();
    if (!editingDepartment || !name) {
      message.warning("请输入部门名称");
      return;
    }

    try {
      const { data } = await createApi().post("/user/department/update", {
        old_name: editingDepartment,
        new_name: name,
      });
      if (data.code !== 0) {
        message.error(data.message || "更新部门失败");
        return;
      }
      syncDepartmentList(data.data?.list || []);
      await refreshTeamMembers();
      message.success("部门更新成功");
      closeDepartmentModal();
    } catch {
      message.error("更新部门失败");
    }
  }

  async function deleteDepartment(departmentName: string) {
    await runMutation(`department:delete:${departmentName}`, () =>
      performDeleteDepartment(departmentName)
    );
  }

  async function performDeleteDepartment(departmentName: string) {
    if (!departmentName || departmentDeleting) {
      return;
    }
    setDepartmentDeleting(true);
    try {
      const { data } = await createApi().post("/user/department/delete", {
        name: departmentName,
      });
      if (data.code !== 0) {
        message.error(data.message || "删除部门失败");
        return;
      }
      syncDepartmentList(data.data?.list || []);
      await refreshTeamMembers();
      setDeletingDepartment("");
      message.success("部门删除成功");
    } catch {
      message.error("删除部门失败");
    } finally {
      setDepartmentDeleting(false);
    }
  }

  function openMemberEditModal(member: TeamMember) {
    setEditingMember(member);
    memberEditForm.setFieldsValue({
      department: member.department || [],
      role: member.role || 0,
    });
  }

  function closeMemberEditModal() {
    setEditingMember(null);
    memberEditForm.resetFields();
  }

  async function deleteTeamMember(member: TeamMember) {
    await runMutation(`team-member:delete:${member.username}`, () =>
      performDeleteTeamMember(member)
    );
  }

  async function performDeleteTeamMember(member: TeamMember) {
    if (!member.username) {
      message.error("成员不存在");
      return;
    }
    try {
      const { data } = await createApi().post("/user/member/delete", {
        username: member.username,
      });
      if (data.code !== 0) {
        message.error(data.message || "删除成员失败");
        return;
      }
      setTeamMembers((prev) =>
        prev.filter((item) => item.username !== member.username)
      );
      setApiProjects((prev) =>
        prev.map((project) => ({
          ...project,
          members: (project.members || []).filter(
            (item) => item.user_id !== member.username
          ),
        }))
      );
      message.success("成员已删除");
    } catch {
      message.error("删除成员失败");
    }
  }

  function openProfileModal() {
    setIsProfileModalOpen(true);
  }

  function closeProfileModal() {
    setIsProfileModalOpen(false);
  }

  function openPasswordModal() {
    passwordForm.resetFields();
    setIsPasswordModalOpen(true);
  }

  function closePasswordModal() {
    setIsPasswordModalOpen(false);
    passwordForm.resetFields();
  }

  async function submitProfile(
    values: ProfileEditorValues,
    avatarFile?: File,
    removeAvatar?: boolean
  ) {
    await runMutation("profile:update", () =>
      performSubmitProfile(values, avatarFile, removeAvatar)
    );
  }

  async function performSubmitProfile(
    values: ProfileEditorValues,
    avatarFile?: File,
    removeAvatar?: boolean
  ) {
    const nextNickname = values.nickname.trim();
    const nextPhone = values.phone.trim();
    const nextMail = values.mail.trim().toLowerCase();
    if (!nextNickname) {
      message.warning("请输入昵称");
      return;
    }
    if (!nextPhone) {
      message.warning("请输入手机号");
      return;
    }
    if (!nextMail) {
      message.warning("请输入邮箱");
      return;
    }

    try {
      const formData = new FormData();
      formData.append("nickname", nextNickname);
      formData.append("phone", nextPhone);
      formData.append("mail", nextMail);
      if (avatarFile) {
        formData.append("avatar", avatarFile);
      } else if (removeAvatar) {
        formData.append("remove_avatar", "true");
      }
      const { data } = await createApi().post("/user/profile/update", formData);
      if (data.code !== 0) {
        message.error(data.message || "更新个人资料失败");
        return;
      }
      const savedNickname = data.data?.nickname || nextNickname;
      const savedPhone = data.data?.phone || nextPhone;
      const savedMail = data.data?.mail || nextMail;
      const savedAvatar =
        data.data?.avatar !== undefined ? data.data.avatar : userAvatar;
      setNickname(savedNickname);
      setUserPhone(savedPhone);
      setUserMail(savedMail);
      setUserAvatar(savedAvatar);
      const authSession = getAuthSession();
      saveAuthStorage({
        user: {
          ...(authSession?.user || {}),
          nickname: savedNickname,
          phone: savedPhone,
          mail: savedMail,
          avatar: savedAvatar,
        },
      });
      message.success("个人资料已更新");
      closeProfileModal();
    } catch {
      message.error("更新个人资料失败");
    }
  }

  async function submitPassword(values: PasswordFormValues) {
    await runMutation("password:update", () => performSubmitPassword(values));
  }

  async function performSubmitPassword(values: PasswordFormValues) {
    const oldPassword = values.old_password.trim();
    const newPassword = values.new_password.trim();
    const confirmPassword = values.confirm_password.trim();
    if (newPassword !== confirmPassword) {
      message.warning("两次输入的新密码不一致");
      return;
    }

    try {
      const { data } = await createApi().post("/user/profile/password", {
        old_password: oldPassword,
        new_password: newPassword,
      });
      if (data.code !== 0) {
        message.error(data.message || "修改密码失败");
        return;
      }
      closePasswordModal();
      clearAuthStorage();
      hydrateAuthSession(null);
      setActiveView("workspace");
      setFlowProjectId("");
      window.history.replaceState(null, "", memberViewRouteMap.workspace);
      message.success("密码已修改，请重新登录");
    } catch {
      message.error("修改密码失败");
    }
  }

  async function submitMemberEdit(values: MemberEditFormValues) {
    await runMutation(`team-member:update:${editingMember?.username}`, () =>
      performSubmitMemberEdit(values)
    );
  }

  async function performSubmitMemberEdit(values: MemberEditFormValues) {
    if (!editingMember?.username) {
      message.error("成员不存在");
      return;
    }
    try {
      const nextDepartment = values.department || [];
      const nextRole = values.role || 0;
      const { data } = await createApi().post("/user/member/update", {
        username: editingMember.username,
        department: nextDepartment,
        role: nextRole,
      });
      if (data.code !== 0) {
        message.error(data.message || "更新成员信息失败");
        return;
      }

      setTeamMembers((prev) =>
        prev.map((item) =>
          item.username === editingMember.username
            ? { ...item, department: nextDepartment, role: nextRole }
            : item
        )
      );
      message.success("成员信息已更新");
      closeMemberEditModal();
    } catch {
      message.error("更新成员信息失败");
    }
  }

  async function createProject(values: ProjectFormValues) {
    const valuesWithRequirement = {
      ...values,
      remark: projectRequirementHtml.trim(),
    };
    if (editingProject) {
      await runMutation(`project:update:${editingProject.project_id}`, () =>
        performUpdateProject(editingProject, valuesWithRequirement)
      );
      return;
    }
    await runMutation("project:create", () =>
      performCreateProject(valuesWithRequirement)
    );
  }

  async function performUpdateProject(
    project: ApiProjectItem,
    values: ProjectFormValues & { remark?: string },
    memberRemovalConfirmed = false
  ) {
    if (!canEditProject(project)) {
      message.warning("你没有权限编辑该项目");
      return;
    }
    const projectName = values.name.trim();
    if (!projectName) {
      message.warning("请输入项目名");
      return;
    }
    const nextRemark = String(values.remark || "").trim();
    const selectedMemberIds = Array.from(
      new Set(
        (values.members || []).filter(
          (memberId) =>
            memberId !== project.creator &&
            memberId !== project.team_admin_id
        )
      )
    );
    const currentMemberIds = project.members
      .map((member) => member.user_id)
      .filter(
        (memberId) =>
          memberId !== project.creator &&
          memberId !== project.team_admin_id
      );
    const memberSelectionChanged =
      selectedMemberIds.length !== currentMemberIds.length ||
      selectedMemberIds.some(
        (memberId) => !currentMemberIds.includes(memberId)
      );
    const removedMemberIds = currentMemberIds.filter(
      (memberId) => !selectedMemberIds.includes(memberId)
    );
    const updateData: {
      project_name?: string;
      avatar?: string;
      remark?: string;
    } = {};
    if (projectName !== project.project_name) {
      updateData.project_name = projectName;
    }
    if (projectAvatar !== (project.avatar || "")) {
      updateData.avatar = projectAvatar;
    }
    if (nextRemark !== (project.remark || "")) {
      updateData.remark = nextRemark;
    }
    if (!Object.keys(updateData).length && !memberSelectionChanged) {
      message.info("项目资料没有发生变化");
      return;
    }
    if (removedMemberIds.length && !memberRemovalConfirmed) {
      Modal.confirm({
        centered: true,
        className: "projectMemberBatchConfirm",
        title: "确认移除项目成员",
        content: (
          <div className="projectMemberBatchConfirm__content">
            <p>
              将从项目「{project.project_name}」移除{" "}
              <strong>{removedMemberIds.length}</strong> 名成员：
            </p>
            <div>
              {removedMemberIds.map((memberId) => (
                <Tag key={memberId}>
                  {getMemberDisplayName(memberId) || memberId}
                </Tag>
              ))}
            </div>
            <small>
              移除后将立即收回项目访问权限，但该成员创建和参与的历史任务仍会保留。
            </small>
          </div>
        ),
        okText: "确认移除并保存",
        cancelText: "返回检查",
        maskClosable: false,
        keyboard: false,
        okButtonProps: { danger: true },
        onOk: () =>
          runMutation(`project:update:${project.project_id}`, () =>
            performUpdateProject(project, values, true)
          ),
      });
      return;
    }

    try {
      const { data } = await createApi().post("/project/update", {
        project_id: project.project_id,
        operator: userId,
        data: updateData,
        ...(memberSelectionChanged ? { member_ids: selectedMemberIds } : {}),
      });
      if (data.code !== 0) {
        message.error(data.message || "项目资料更新失败");
        return;
      }
      const currentMemberMap = new Map(
        project.members.map((member) => [member.user_id, member])
      );
      const nextMembers = memberSelectionChanged
        ? [
            currentMemberMap.get(project.creator) || {
              user_id: project.creator,
              role: "manager",
              joined_at: project.created_at || Date.now(),
            },
            ...(project.team_admin_id &&
            project.team_admin_id !== project.creator &&
            currentMemberMap.has(project.team_admin_id)
              ? [currentMemberMap.get(project.team_admin_id)!]
              : []),
            ...selectedMemberIds.map(
              (memberId): ApiProjectMember =>
                currentMemberMap.get(memberId) || {
                  user_id: memberId,
                  role: "member",
                  joined_at: Date.now(),
                }
            ),
          ]
        : project.members;
      const updatedProject = {
        ...project,
        ...updateData,
        members: nextMembers,
        updated_at: Date.now(),
      };
      setApiProjects((prev) =>
        prev.map((item) =>
          item.project_id === project.project_id
            ? {
                ...item,
                ...updateData,
                members: nextMembers,
                updated_at: updatedProject.updated_at,
              }
            : item
        )
      );
      setFlowProjectSnapshot((prev) =>
        prev?.project_id === project.project_id
          ? {
              ...prev,
              ...updateData,
              members: nextMembers,
              updated_at: updatedProject.updated_at,
            }
          : prev
      );
      if (flowProjectId === project.project_id) {
        await fetchProjectLogs(project.project_id);
      }
      message.success("项目资料已更新");
      closeProjectModal();
    } catch {
      message.error("项目资料更新失败");
    }
  }

  async function saveFlowProjectRequirement(requirement: string) {
    if (!flowProject || !canEditProject(flowProject)) {
      message.warning("你没有权限编辑该项目需求");
      return false;
    }
    const nextRequirement = requirement.trim();
    if (
      nextRequirement ===
      normalizeProjectRequirementMarkdown(flowProject.remark).trim()
    ) {
      message.info("项目需求没有发生变化");
      return true;
    }
    const mutationKey = `project:requirement:${flowProject.project_id}`;
    const result = await runMutation(mutationKey, async () => {
      try {
        const { data } = await createApi().post("/project/update", {
          project_id: flowProject.project_id,
          operator: userId,
          data: { remark: nextRequirement },
        });
        if (data.code !== 0) {
          message.error(data.message || "项目需求保存失败");
          return false;
        }
        const updatedAt = Date.now();
        setApiProjects((prev) =>
          prev.map((project) =>
            project.project_id === flowProject.project_id
              ? { ...project, remark: nextRequirement, updated_at: updatedAt }
              : project
          )
        );
        setFlowProjectSnapshot((prev) =>
          prev?.project_id === flowProject.project_id
            ? { ...prev, remark: nextRequirement, updated_at: updatedAt }
            : prev
        );
        await fetchProjectLogs(flowProject.project_id);
        message.success("项目需求已保存");
        return true;
      } catch {
        message.error("项目需求保存失败");
        return false;
      }
    });
    return result.executed && result.value === true;
  }

  async function performCreateProject(
    values: ProjectFormValues & { remark?: string }
  ) {
    const projectName = values.name.trim();
    if (!projectName) {
      message.warning("请输入项目名");
      return;
    }
    const selectedMembers = values.members || [];
    const joinedAt = Date.now();
    const creatorMember: ApiProjectMember = {
      user_id: userId,
      role: "manager",
      joined_at: joinedAt,
    };
    const members: ApiProjectMember[] = selectedMembers.map((memberId) => ({
      user_id: memberId,
      role: "member",
      joined_at: joinedAt,
    }));
    const projectMembers = [
      creatorMember,
      ...members.filter((member) => member.user_id !== userId),
    ];

    try {
      const { data } = await createApi().post("/project/add", {
        project_name: projectName,
        template: values.template,
        creator: userId,
        team_code: teamCode,
        members: projectMembers,
        avatar: projectAvatar,
        status: "planning",
        remark: values.remark,
      });
      if (data.code !== 0) {
        message.error(data.message || "项目创建失败");
        return;
      }
      if (!data.data?.project_id) {
        message.error("项目创建失败，未返回项目 ID");
        return;
      }

      setApiProjects((prev) => [
        {
          project_id: data.data.project_id,
          project_name: projectName,
          template: values.template,
          creator: userId,
          team_code: teamCode,
          team_admin_id: isTeamAdmin ? userId : undefined,
          members: projectMembers,
          avatar: projectAvatar,
          status: "planning",
          task_summary: { total: 0, completed: 0, pending: 0 },
          phase_permissions: {
            initiation: true,
            development: false,
            promotion: false,
            production: false,
          },
          phase_summaries: createEmptyPhaseSummaries(),
          flow_layouts: {
            initiation: { version: 0, nodes: {} },
            development: { version: 0, nodes: {} },
            promotion: { version: 0, nodes: {} },
            production: { version: 0, nodes: {} },
          },
          flow_graph_versions: {
            initiation: 0,
            development: 0,
            promotion: 0,
            production: 0,
          },
          remark: values.remark,
        },
        ...prev,
      ]);
      message.success("项目创建成功");
      closeProjectModal();
    } catch {
      message.error("项目创建失败");
    }
  }

  async function createTaskNode(values: TaskCreateFormValues) {
    await runMutation("task:create", () => performCreateTaskNode(values));
  }

  async function performCreateTaskNode(values: TaskCreateFormValues) {
    if (!creatingTaskProject) {
      return;
    }
    if (!canManageTaskAssignee([])) {
      message.warning("当前阶段为只读状态");
      return;
    }
    const title = values.title.trim();
    if (!title) {
      message.warning("请输入任务节点标题");
      return;
    }
    const dueTimeDate = values.due_time?.toDate?.();
    if (dueTimeDate) {
      dueTimeDate.setHours(0, 0, 0, 0);
    }
    if (
      !(dueTimeDate instanceof Date) ||
      !Number.isFinite(dueTimeDate.getTime())
    ) {
      message.warning("请选择预期完结时间");
      return;
    }

    try {
      const { data } = await createApi().post("/task/add", {
        project_id: creatingTaskProject.project.project_id,
        phase: activeProjectPhase,
        creator: userId,
        predecessor_ids: pendingTaskConnection?.predecessor_ids || [],
        title,
        priority: values.priority || "medium",
        status: values.status || "todo",
        due_time:
          dueTimeDate instanceof Date && Number.isFinite(dueTimeDate.getTime())
            ? dueTimeDate.getTime()
            : 0,
        tags: taskDraftTags,
        attachments: [],
        subtasks: creatingTaskSubtasks.map((subtask) => ({
          title: subtask.title,
          transferees: subtask.transferees || [],
          reviewers: subtask.reviewers,
          content_items: subtask.content_items,
        })),
      });
      if (data.code !== 0) {
        message.error(data.message || "新增任务节点失败");
        return;
      }
      applyAutoAddedProjectMembers(
        creatingTaskProject.project.project_id,
        data.data?.added_members
      );
      const taskId = data.data?.task_id;
      if (taskId && pendingTaskConnection?.position) {
        const nodeId = `task-${taskId}`;
        const position = pendingTaskConnection.position;
        projectFlowCollabPositionsRef.current[nodeId] = position;
        const doc = projectFlowCollabDocRef.current;
        if (doc) {
          doc.transact(() => {
            doc.getMap("nodePositions").set(nodeId, position);
          }, "local-layout");
        }
      }
      await refreshTaskList();
      await fetchProjectLogs(creatingTaskProject.project.project_id);
      message.success("任务节点已新增");
      closeTaskCreateModal();
    } catch (error: any) {
      const status = error?.response?.status;
      if (status === 413) {
        message.error("新增任务节点失败：粘贴图片过大，请压缩后重试");
        return;
      }
      message.error(error?.response?.data?.message || "新增任务节点失败");
    }
  }

  async function createTaskSubtask(draft: TaskSubtaskDraft) {
    if (!editingTaskNode?.id) return false;
    const parentTask = editingTaskNode;
    const result = await runMutation(`subtask:create:${parentTask.id}`, async () => {
      try {
        const { data } = await createApi().post("/task/subtask/add", {
          task_id: parentTask.id,
          project_id: parentTask.project_id,
          operator: userId,
          ...draft,
        });
        if (data.code !== 0) {
          message.error(data.message || "新增子任务失败");
          await refreshTaskSubtaskSnapshots(parentTask);
          return false;
        }
        applyAutoAddedProjectMembers(
          parentTask.project_id,
          data.data?.added_members
        );
        applySubtaskParentUpdate(data.data?.parent_task);
        await fetchTaskSubtasks(parentTask, "edit");
        if (selectedTaskNode?.id === parentTask.id) {
          await fetchTaskSubtasks(parentTask, "detail");
        }
        await fetchProjectLogs(parentTask.project_id);
        message.success("子任务已新增");
        return true;
      } catch (error: any) {
        if (error?.response?.status === 413) {
          message.error("新增子任务失败：粘贴图片过大，请压缩后重试");
          await refreshTaskSubtaskSnapshots(parentTask);
          return false;
        }
        message.error(error?.response?.data?.message || "新增子任务失败");
        await refreshTaskSubtaskSnapshots(parentTask);
        return false;
      }
    });
    return Boolean(result.executed && result.value);
  }

  function getSubtaskContentSummary(
    contentItems: TaskSubtaskItem["content_items"]
  ) {
    const completed = contentItems.filter((item) => item.completed).length;
    return `内容 ${completed} / ${contentItems.length} 已完成`;
  }

  async function updateTaskSubtask(
    subtask: TaskSubtaskItem,
    draft: TaskSubtaskDraft
  ) {
    const parentTask = getSubtaskParentTask(subtask);
    if (!parentTask?.id) return false;
    const result = await runMutation(`subtask:update:${subtask.id}`, async () => {
      try {
        const { data } = await createApi().post("/task/subtask/update", {
          subtask_id: subtask.id,
          task_id: parentTask.id,
          project_id: parentTask.project_id,
          operator: userId,
          expected_revision: subtask.revision,
          ...draft,
        });
        if (data.code !== 0) {
          message.error(data.message || "更新子任务失败");
          await refreshTaskSubtaskSnapshots(parentTask);
          return false;
        }
        if (data.data?.unchanged) {
          message.info("子任务未发生变化");
          return true;
        }
        applyAutoAddedProjectMembers(
          parentTask.project_id,
          data.data?.added_members
        );
        applySubtaskParentUpdate(data.data?.parent_task);
        await refreshTaskSubtaskSnapshots(parentTask);
        await fetchProjectLogs(parentTask.project_id);
        message.success("子任务已更新");
        return true;
      } catch (error: any) {
        const current = error?.response?.data?.data?.current as
          | TaskSubtaskItem
          | undefined;
        if (error?.response?.status === 409 && current) {
          Modal.confirm({
            centered: true,
            className: "taskSubtaskConflictModal",
            title: "子任务已被其他成员修改",
            content: (
              <div className="taskSubtaskConflictModal__compare">
                <div><span>服务器最新</span><strong>{current.title}</strong><p>{getTaskSubtaskContentAssignees(current).map(getMemberDisplayName).join("、")} · 转让人 {(current.transferees || []).length ? (current.transferees || []).map(getMemberDisplayName).join("、") : "无需转交"} · 审核人 {current.reviewers.length ? current.reviewers.map(getMemberDisplayName).join("、") : "无需审核"}</p><em>{getSubtaskContentSummary(current.content_items)}</em></div>
                <div><span>我的修改</span><strong>{draft.title}</strong><p>{getTaskSubtaskContentAssignees(draft).map(getMemberDisplayName).join("、")} · 转让人 {draft.transferees.length ? draft.transferees.map(getMemberDisplayName).join("、") : "无需转交"} · 审核人 {draft.reviewers.length ? draft.reviewers.map(getMemberDisplayName).join("、") : "无需审核"}</p><em>{getSubtaskContentSummary(draft.content_items)}</em></div>
              </div>
            ),
            cancelText: "载入最新版本",
            okText: "确认保留我的修改",
            onCancel: () => refreshTaskSubtaskSnapshots(parentTask),
            onOk: () => updateTaskSubtask(current, draft),
          });
          return false;
        }
        if (error?.response?.status === 413) {
          message.error("更新子任务失败：粘贴图片过大，请压缩后重试");
          await refreshTaskSubtaskSnapshots(parentTask);
          return false;
        }
        message.error(error?.response?.data?.message || "更新子任务失败");
        await refreshTaskSubtaskSnapshots(parentTask);
        return false;
      }
    });
    return Boolean(result.executed && result.value);
  }

  async function deleteTaskSubtask(subtask: TaskSubtaskItem) {
    if (!editingTaskNode?.id) return false;
    const parentTask = editingTaskNode;
    const result = await runMutation(`subtask:delete:${subtask.id}`, async () => {
      try {
        const { data } = await createApi().post("/task/subtask/delete", {
          subtask_id: subtask.id,
          task_id: parentTask.id,
          project_id: parentTask.project_id,
          operator: userId,
          expected_revision: subtask.revision,
        });
        if (data.code !== 0) {
          message.error(data.message || "删除子任务失败");
          await refreshTaskSubtaskSnapshots(parentTask);
          return false;
        }
        applySubtaskParentUpdate(data.data?.parent_task);
        await fetchTaskSubtasks(parentTask, "edit");
        if (selectedTaskNode?.id === parentTask.id) {
          await fetchTaskSubtasks(parentTask, "detail");
        }
        await fetchProjectLogs(parentTask.project_id);
        message.success("子任务已删除");
        return true;
      } catch (error: any) {
        if (error?.response?.status === 409) {
          message.warning("该子任务刚刚被其他成员修改，请查看最新内容后重新删除");
          await refreshTaskSubtaskSnapshots(parentTask);
          return false;
        }
        message.error(error?.response?.data?.message || "删除子任务失败");
        await refreshTaskSubtaskSnapshots(parentTask);
        return false;
      }
    });
    return Boolean(result.executed && result.value);
  }

  function getSubtaskParentTask(subtask: TaskSubtaskItem) {
    return [editingTaskNode, selectedTaskNode, ...flowTaskSnapshot, ...teamTasks]
      .filter(Boolean)
      .find((task) => task?.id === subtask.parent_task_id) as
      | ApiTaskItem
      | undefined;
  }

  async function refreshTaskSubtaskSnapshots(parentTask: ApiTaskItem) {
    const refreshes: Promise<unknown>[] = [refreshTaskList()];
    if (editingTaskNode?.id === parentTask.id) {
      refreshes.push(fetchTaskSubtasks(parentTask, "edit"));
    }
    if (selectedTaskNode?.id === parentTask.id) {
      refreshes.push(fetchTaskSubtasks(parentTask, "detail"));
    }
    await Promise.allSettled(refreshes);
  }

  async function refreshSubtaskWorkflowViews(
    parentTask: ApiTaskItem,
    parentUpdate?: ApiTaskItem,
    subtaskUpdate?: TaskSubtaskItem
  ) {
    applySubtaskParentUpdate(parentUpdate);
    applyTaskSubtaskUpdate(subtaskUpdate);
    const refreshes: Promise<unknown>[] = [refreshTaskList()];
    if (editingTaskNode?.id === parentTask.id) {
      refreshes.push(fetchTaskSubtasks(parentTask, "edit"));
    }
    if (selectedTaskNode?.id === parentTask.id) {
      refreshes.push(fetchTaskSubtasks(parentTask, "detail"));
    }
    refreshes.push(fetchProjectLogs(parentTask.project_id));
    await Promise.all(refreshes);
  }

  async function mutateTaskSubtaskWorkflow(
    subtask: TaskSubtaskItem,
    action: "handoff" | "submit-review" | "complete" | "approve" | "reject",
    rejectionReason = ""
  ) {
    const parentTask = getSubtaskParentTask(subtask);
    if (!parentTask?.id) {
      message.error("主任务不存在，请刷新页面后重试");
      return false;
    }
    const actionText =
      action === "handoff"
        ? "子任务已转交"
        : action === "submit-review"
        ? "提交审批"
        : action === "complete"
        ? "子任务已完成"
        : action === "approve"
        ? "审批通过"
        : "审批驳回";
    const result = await runMutation(
      `subtask:${action}:${subtask.id}`,
      async () => {
        try {
          const { data } = await createApi().post(`/task/subtask/${action}`, {
            subtask_id: subtask.id,
            task_id: parentTask.id,
            project_id: parentTask.project_id,
            operator: userId,
            expected_revision: subtask.revision,
            ...(action === "reject"
              ? { rejection_reason: rejectionReason }
              : {}),
          });
          if (data.code !== 0) {
            message.error(data.message || `${actionText}失败`);
            await refreshTaskSubtaskSnapshots(parentTask);
            return false;
          }
          await refreshSubtaskWorkflowViews(
            parentTask,
            data.data?.parent_task,
            data.data?.subtask
          );
          message.success(actionText);
          return true;
        } catch (error: any) {
          if (error?.response?.status === 409) {
            message.warning("子任务已被其他成员更新，已载入最新状态");
            await refreshSubtaskWorkflowViews(parentTask);
            return false;
          }
          message.error(error?.response?.data?.message || `${actionText}失败`);
          await refreshTaskSubtaskSnapshots(parentTask);
          return false;
        }
      }
    );
    return Boolean(result.executed && result.value);
  }

  function handoffTaskSubtask(subtask: TaskSubtaskItem) {
    return mutateTaskSubtaskWorkflow(subtask, "handoff");
  }

  function submitTaskSubtaskReview(subtask: TaskSubtaskItem) {
    return mutateTaskSubtaskWorkflow(subtask, "submit-review");
  }

  function completeTaskSubtask(subtask: TaskSubtaskItem) {
    return mutateTaskSubtaskWorkflow(subtask, "complete");
  }

  function approveTaskSubtask(subtask: TaskSubtaskItem) {
    return mutateTaskSubtaskWorkflow(subtask, "approve");
  }

  function rejectTaskSubtask(subtask: TaskSubtaskItem, reason: string) {
    return mutateTaskSubtaskWorkflow(subtask, "reject", reason);
  }

  async function submitTaskUpdate(
    baseTask: ApiTaskItem,
    expectedTask: ApiTaskItem,
    draft: TaskUpdateDraft,
    cascade: boolean,
    discardReason = ""
  ) {
    if (!expectedTask.id) return false;
    try {
      const delayDays = cascade
        ? getDueDelayDays(expectedTask.due_time, draft.due_time)
        : 0;
      const { data } = await createApi().post("/task/update", {
        task_id: expectedTask.id,
        project_id: expectedTask.project_id,
        operator: userId,
        expected_revision: expectedTask.revision,
        ...draft,
        discard_reason: discardReason,
        cascade_delay_days: delayDays,
      });
      if (data.code !== 0) {
        message.error(data.message || "更新任务节点失败");
        return false;
      }
      if (data.data?.unchanged) {
        message.info("任务节点未发生变化");
        setTaskConflict(null);
        closeTaskEditModal();
        return true;
      }
      await refreshTaskList();
      await fetchProjectLogs(expectedTask.project_id);
      const cascadeCount = data.data?.cascade_count || 0;
      message.success(
        cascadeCount
          ? `任务节点已更新，已顺延 ${cascadeCount} 个后续节点`
          : "任务节点已更新"
      );
      setTaskConflict(null);
      closeTaskEditModal();
      return true;
    } catch (error: any) {
      const status = error?.response?.status;
      if (status === 409 && error?.response?.data?.data?.current) {
        setTaskConflict({
          baseTask,
          serverTask: error.response.data.data.current,
          draft,
          cascade,
          discardReason,
        });
        return false;
      }
      if (status === 413) {
        message.error("更新任务节点失败：粘贴图片过大，请压缩后重试");
        return false;
      }
      message.error(error?.response?.data?.message || "更新任务节点失败");
      return false;
    }
  }

  async function updateTaskNode(values: TaskCreateFormValues) {
    await runMutation(`task:update:${editingTaskNode?.id}`, () =>
      performUpdateTaskNode(values, false)
    );
  }

  async function performUpdateTaskNode(
    values: TaskCreateFormValues,
    lifecycleConfirmed: boolean,
    discardReason = ""
  ) {
    if (!editingTaskNode?.id) return;
    if (!canManageTaskAssignee(editingTaskNode.assignees)) {
      message.warning("当前阶段为只读状态");
      return;
    }
    const title = values.title.trim();
    if (!title) {
      message.warning("请输入任务节点标题");
      return;
    }
    const dueTimeDate = values.due_time?.toDate?.();
    if (dueTimeDate) dueTimeDate.setHours(0, 0, 0, 0);
    const nextDueTime =
      dueTimeDate instanceof Date && Number.isFinite(dueTimeDate.getTime())
        ? dueTimeDate.getTime()
        : 0;
    const draft: TaskUpdateDraft = {
      title,
      assignees: editingTaskNode.subtask_summary.assignees,
      content: "",
      priority: values.priority || "medium",
      status: values.status || "todo",
      due_time: nextDueTime,
      tags: taskDraftTags,
      attachments: editingTaskNode.attachments || [],
    };
    const isDiscarding =
      editingTaskNode.status !== "discarded" && draft.status === "discarded";
    const isRestoring =
      editingTaskNode.status === "discarded" && draft.status !== "discarded";
    if ((isDiscarding || isRestoring) && !lifecycleConfirmed) {
      setTaskDiscardReason("");
      setPendingTaskLifecycle({
        kind: isDiscarding ? "discard" : "restore",
        values,
      });
      return;
    }
    const delayDays = getDueDelayDays(editingTaskNode.due_time, nextDueTime);
    const saveTaskNode = (cascade: boolean) =>
      submitTaskUpdate(
        editingTaskNode,
        editingTaskNode,
        draft,
        cascade,
        discardReason
      );
    if (!isDiscarding && !isRestoring && delayDays > 0) {
      openCascadeDelayConfirm({
        days: delayDays,
        scope: "main",
        taskTitle: editingTaskNode.title,
        onSave: saveTaskNode,
      });
      return;
    }
    await saveTaskNode(false);
  }

  async function confirmTaskConflictMine() {
    await runMutation(`task:conflict:${taskConflict?.serverTask.id}`, () =>
      performConfirmTaskConflictMine()
    );
  }

  async function performConfirmTaskConflictMine() {
    if (!taskConflict) return;
    setTaskConflictSaving(true);
    try {
      await submitTaskUpdate(
        taskConflict.baseTask,
        taskConflict.serverTask,
        taskConflict.draft,
        taskConflict.cascade,
        taskConflict.discardReason
      );
    } finally {
      setTaskConflictSaving(false);
    }
  }

  async function deleteTaskNode(task: ApiTaskItem) {
    await runMutation(`task:delete:${task.id}`, () => performDeleteTaskNode(task));
  }

  async function performDeleteTaskNode(task: ApiTaskItem) {
    if (!task.id) {
      return;
    }
    try {
      const { data } = await createApi().post("/task/delete", {
        task_id: task.id,
        project_id: task.project_id,
        operator: userId,
        expected_revision: task.revision,
      });
      if (data.code !== 0) {
        message.error(data.message || "删除任务节点失败");
        return;
      }
      setTeamTasks((prev) => prev.filter((item) => item.id !== task.id));
      setFlowTaskSnapshot((prev) => prev.filter((item) => item.id !== task.id));
      setSelectedTaskNode((prev) => (prev?.id === task.id ? null : prev));
      setCommentingTaskNode((prev) => (prev?.id === task.id ? null : prev));
      projectFlowCollabDocRef.current
        ?.getMap("nodePositions")
        .delete(`task-${task.id}`);
      await fetchProjectLogs(task.project_id);
      message.success("任务节点已删除");
    } catch (error: any) {
      if (error?.response?.status === 409) {
        message.warning("该任务刚刚被其他成员修改，请查看最新内容后重新确认删除");
        await refreshTaskList();
        return;
      }
      message.error(error?.response?.data?.message || "删除任务节点失败");
    }
  }

  async function archiveProject(project: ApiProjectItem) {
    await runMutation(`project:archive:${project.project_id}`, () =>
      performArchiveProject(project)
    );
  }

  async function performArchiveProject(project: ApiProjectItem) {
    if (!canArchiveProject(project)) {
      return;
    }

    try {
      const { data } = await createApi().post("/project/update", {
        project_id: project.project_id,
        operator: userId,
        data: { status: "done" },
      });
      if (data.code !== 0) {
        message.error(data.message || "项目归档失败");
        return;
      }
      setApiProjects((prev) =>
        prev.map((item) =>
          item.project_id === project.project_id
            ? { ...item, status: "done", updated_at: Date.now() }
            : item
        )
      );
      message.success("项目已归档");
    } catch {
      message.error("项目归档失败");
    }
  }

  async function unarchiveProject(project: ApiProjectItem) {
    await runMutation(`project:unarchive:${project.project_id}`, () =>
      performUnarchiveProject(project)
    );
  }

  async function performUnarchiveProject(project: ApiProjectItem) {
    if (!canUnarchiveProject(project)) {
      return;
    }

    try {
      const { data } = await createApi().post("/project/update", {
        project_id: project.project_id,
        operator: userId,
        data: { status: "active" },
      });
      if (data.code !== 0) {
        message.error(data.message || "撤销归档失败");
        return;
      }
      setApiProjects((prev) =>
        prev.map((item) =>
          item.project_id === project.project_id
            ? { ...item, status: "active", updated_at: Date.now() }
            : item
        )
      );
      const taskRes = await createApi().post("/task/list", {
        project_id: project.project_id,
        pageIndex: 0,
        pageSize: 1000,
      });
      if (taskRes.data.code === 0) {
        const projectTasks = taskRes.data.data?.list || [];
        setTeamTasks((prev) => [
          ...prev.filter((task) => task.project_id !== project.project_id),
          ...projectTasks,
        ]);
      }
      message.success("项目已撤销归档");
    } catch {
      message.error("撤销归档失败");
    }
  }

  async function deleteProject(project: ApiProjectItem) {
    await runMutation(`project:delete:${project.project_id}`, () =>
      performDeleteProject(project)
    );
  }

  async function performDeleteProject(project: ApiProjectItem) {
    if (!canDeleteProject(project)) {
      message.warning("你没有权限删除该项目");
      return;
    }

    try {
      const { data } = await createApi().post("/project/delete", {
        project_id: project.project_id,
        operator: userId,
      });
      if (data.code !== 0) {
        message.error(data.message || "删除项目失败");
        return;
      }
      setApiProjects((prev) =>
        prev.filter((item) => item.project_id !== project.project_id)
      );
      setTeamTasks((prev) =>
        prev.filter((task) => task.project_id !== project.project_id)
      );
      if (flowProjectId === project.project_id) {
        setFlowProjectId("");
        setFlowProjectSnapshot(null);
        setFlowTaskSnapshot([]);
        setActiveView("board");
        const boardRouteMap = isTeamAdmin ? viewRouteMap : memberViewRouteMap;
        window.history.pushState(null, "", boardRouteMap.board);
      }
      message.success("项目已删除");
    } catch {
      message.error("删除项目失败");
    }
  }

  const items: MenuProps["items"] = [
    {
      label: (
        <button onClick={(e) => copyUserId(e)} type="button">
          复制账号
        </button>
      ),
      key: "1",
    },
    {
      label: (
        <button onClick={(e) => quit(e)} type="button">
          退出登录
        </button>
      ),
      key: "2",
    },
  ];

  const weekPlanGroups = getWeekPlanGroups();
  const weekPlanTaskCount = new Set(
    weekPlanGroups.flatMap((group) => group.tasks.map((task) => task.id))
  ).size;
  const weekPlanContent = (
    <div className="weekPlanPopover">
      <div className="weekPlanPopover__header">
        <strong>本周任务计划</strong>
        <span>
          {weekPlanLoading
            ? "加载中"
            : isTeamAdmin
            ? `${weekPlanTaskCount} 项团队任务内容`
            : userRole !== 1 && weekPlanLoaded
            ? `${weekPlanTaskCount} 项任务内容`
            : weekPlanLoaded
            ? `${weekPlanTaskCount} 项本周任务内容`
            : "打开后加载"}
        </span>
      </div>
      <div className="weekPlanList">
        {weekPlanLoading ? (
          <div className="weekPlanDay">
            <p>本周计划加载中...</p>
          </div>
        ) : weekPlanLoaded ? (
          weekPlanGroups.map((group) => (
            <div className="weekPlanDay" key={group.key}>
              <div className="weekPlanDay__date">
                <span>{group.label}</span>
                <small>{group.tasks.length} 项</small>
              </div>
              <div className="weekPlanDay__tasks">
                {group.tasks.length ? (
                  group.tasks.map((task) => (
                    <div
                      className={`weekPlanTask ${
                        task.is_content_item ? "is-content-item" : ""
                      } ${task.status === "done" ? "is-completed" : ""}`}
                      key={task.id}
                    >
                      <div className="weekPlanTask__body">
                        <strong>{task.title}</strong>
                        <span>
                          {task.is_content_item
                            ? `${task.context} · ${getProjectName(task.project_id)}`
                            : `${getTaskAssigneeNames(task.assignees).join(
                                "、"
                              )} · ${getProjectName(task.project_id)}`}
                        </span>
                        {task.is_content_item ? (
                          <div className="weekPlanTask__contentMeta">
                            <TaskPriorityTag priority={task.priority} />
                            <small>
                              <CalendarOutlined />
                              {formatTaskDueTime(task.start_time)} 至{" "}
                              {formatTaskDueTime(task.due_time)}
                            </small>
                          </div>
                        ) : null}
                      </div>
                      <Tag
                        className={
                          task.is_content_item && task.status === "done"
                            ? "weekPlanTask__status is-completed"
                            : "weekPlanTask__status"
                        }
                        color={
                          task.is_content_item && task.status === "done"
                            ? undefined
                            : getTaskStatusColor(task.status)
                        }
                      >
                        {task.is_content_item
                          ? task.status === "done"
                            ? "已完成"
                            : "未完成"
                          : getTaskStatusText(task.status)}
                      </Tag>
                    </div>
                  ))
                ) : (
                  <p>暂无安排</p>
                )}
              </div>
            </div>
          ))
        ) : (
          <div className="weekPlanDay">
            <p>打开本周计划后将同步最新任务安排。</p>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div className="homePage">
      {isLogin ? (
        <div className="workspaceShell">
          <HomeSidebar
            activeView={activeView}
            brandMark={brandMark}
            brandSubtitle={brandSubtitle}
            brandTitle={brandTitle}
            completed={weeklyStatus.completed}
            dueTasks={(weeklyStatus.due_tasks || []).map(
              (task) => `${task.project_name}：${task.title}`
            )}
            isTeamAdmin={isTeamAdmin}
            onChangeView={changeView}
            pending={weeklyStatus.pending}
            showDueTasks={!isTeamAdmin && userRole !== 1}
            statusText={weeklyStatus.text}
            statusTitle={weeklyStatus.title}
          />

          <main className="workspaceMain">
            <header className="workspaceHeader">
              <div className="workspaceHeader__content">
                <span className="workspaceTag">
                  {activeView === "workspace"
                    ? "Workspace Overview"
                    : activeView === "board"
                    ? isTeamAdmin
                      ? "Project Board"
                      : "Assigned Projects"
                    : activeView === "personalBoard"
                    ? "Personal Board"
                    : activeView === "projectFlow"
                    ? "Project Flow"
                    : "Profile Center"}
                </span>
                <h1>
                  {activeView === "workspace" &&
                    `欢迎回来，${nickname || userId}`}
                  {activeView === "board" &&
                    (isTeamAdmin ? "项目看板总览" : "我的项目与任务")}
                  {activeView === "personalBoard" && "我的个人看板"}
                  {activeView === "projectFlow" &&
                    (flowProject?.project_name || "项目节点图")}
                  {activeView === "profile" && "个人资料中心"}
                </h1>
                <p>
                  {activeView === "workspace" &&
                    (isTeamAdmin
                      ? "把项目进度、资源协作和任务节奏放在同一视图中，方便团队高效推进。"
                      : "聚焦你参与的项目、待办任务和协作提醒，减少切换成本。")}
                  {activeView === "board" &&
                    (isTeamAdmin
                      ? "集中查看项目状态、任务分布和关键节点，帮助你快速判断当前推进情况。"
                      : "查看你参与的项目卡片、当前进度和需要关注的协作节点。")}
                  {activeView === "personalBoard" &&
                    "记录只属于你的想法、事项和截止时间，内容不会共享给其他成员。"}
                  {activeView === "projectFlow" &&
                    "按依赖层级查看任务模块，并通过时间线掌握当前阶段进度。"}
                  {activeView === "profile" &&
                    "维护个人账号、部门和团队信息，让协作身份清晰可追踪。"}
                </p>
              </div>
              <div className="workspaceHeader__actions">
                <button
                  className="workspaceInboxButton"
                  onClick={openMessageDrawer}
                  type="button"
                >
                  <Badge
                    count={unreadMessageCount}
                    offset={[2, -2]}
                    overflowCount={99}
                    size="small"
                  >
                    <InboxOutlined />
                  </Badge>
                </button>
                <div className="workspaceUser">
                  <HeaderUserAvatar
                    avatarUrl={userAvatarUrl}
                    background={getTextAvatar(nickname || userId).background}
                    displayName={nickname || userId}
                  />
                  <Dropdown menu={{ items }}>
                    <button
                      className="workspaceUser__trigger"
                      onClick={(e) => e.preventDefault()}
                      type="button"
                    >
                      <Space>
                        {nickname || userId}
                        <DownOutlined />
                      </Space>
                    </button>
                  </Dropdown>
                </div>
              </div>
            </header>

            {!isTeamAdmin &&
              activeView !== "projectFlow" &&
              activeView !== "personalBoard" && (
              <MemberDashboard
                activeView={activeView}
                apiProjects={apiProjects}
                canCreateProject={userRole === 1}
                canEditProject={canEditProject}
                canArchiveProject={canArchiveProject}
                canUnarchiveProject={canUnarchiveProject}
                copyText={copyText}
                department={department}
                dashboardLoading={dashboardLoading}
                getCurrentTask={getCurrentTask}
                getMemberDisplayName={getMemberDisplayName}
                getProjectFallbackColor={getProjectFallbackColor}
                getProjectMemberSummary={getProjectMemberSummary}
                getProjectStepCurrent={getProjectStepCurrent}
                getProjectStepStatuses={getProjectStepStatuses}
                getTaskStatusClass={getTaskStatusClass}
                getTaskStatusText={getTaskStatusText}
                getTextAvatar={getTextAvatar}
                isMutationPending={isMutationPending}
                nickname={nickname}
                onCreateProject={openProjectModal}
                onArchiveProject={archiveProject}
                onDeleteProject={(project) => setDeletingProject(project)}
                onEditProject={openProjectEditModal}
                onOpenMemberCalendar={openMemberCalendar}
                onOpenDueRiskItem={(focus) =>
                  openProjectFlowById(focus.projectId, focus)
                }
                onOpenProjectContentItem={(focus) =>
                  openProjectFlowById(focus.projectId, focus)
                }
                onOpenProfileModal={openProfileModal}
                onOpenPasswordModal={openPasswordModal}
                onOpenProjectFlow={openProjectFlow}
                onOpenWeekPlan={handleWeekPlanOpenChange}
                onUnarchiveProject={unarchiveProject}
                projectStepNames={projectStepNames}
                teamTasks={teamTasks}
                teamMembers={teamMembers}
                teamCode={teamCode}
                userId={userId}
                userAvatar={userAvatarUrl}
                userMail={userMail}
                userPhone={userPhone}
                userRole={userRole}
                weekPlanContent={weekPlanContent}
                weeklyPending={weeklyStatus.pending}
                weeklyRanking={weeklyRanking}
              />
            )}

            {activeView === "personalBoard" && <PersonalBoardPage />}

            {isTeamAdmin && activeView === "workspace" && (
              <>
                <TaskLeaderboard
                  activeProjectCount={activeProjects.length}
                  currentUserId={userId}
                  getTextAvatar={getTextAvatar}
                  onWeekPlanOpenChange={handleWeekPlanOpenChange}
                  pendingTaskCount={weeklyStatus.pending}
                  ranking={weeklyRanking}
                  weekPlanContent={weekPlanContent}
                />

                <ContentDueRiskPanel
                  getMemberDisplayName={getMemberDisplayName}
                  includeAll
                  onOpenItem={(focus) =>
                    openProjectFlowById(focus.projectId, focus)
                  }
                  projects={workspaceProjects}
                  tasks={overviewTasks}
                  userId={userId}
                />

                <Card className="workspacePanel projectOverviewPanel">
                    <div className="workspacePanel__header">
                      <h3>项目总览</h3>
                      <div className="projectOverviewPager">
                        <Input
                          allowClear
                          aria-label="搜索项目或参与成员"
                          className="projectOverviewSearch"
                          onChange={(event) =>
                            setProjectOverviewSearchText(event.target.value)
                          }
                          placeholder="搜索项目或参与成员"
                          prefix={<SearchOutlined />}
                          value={projectOverviewSearchText}
                        />
                        <span>
                          {dashboardLoading
                            ? "加载中"
                            : debouncedProjectOverviewSearchText.trim()
                            ? `${workspaceProjectSearchResults.length} / ${workspaceProjects.length} 个项目`
                            : `${workspaceProjects.length} 个项目`}
                        </span>
                      </div>
                    </div>
                    {workspaceProjectSearchResults.length > 0 ? (
                      <div className="projectOverviewGrid">
                        {workspaceProjectSearchResults.map(
                          ({ project, matchedAssigneeId }) => {
                          const overdueContentItems =
                            collectProjectOverdueContentItems(
                              overviewTasks,
                              project.project_id
                            );
                          const overdueContentCount =
                            overdueContentItems.length;
                          return (
                            <div
                              aria-label={
                                overdueContentCount
                                  ? `${project.project_name}，${overdueContentCount} 项任务内容逾期`
                                  : project.project_name
                              }
                              className={`projectOverviewMiniCard projectOverviewMiniCard--clickable${
                                overdueContentCount
                                  ? " projectOverviewMiniCard--overdue"
                                  : ""
                              }`}
                              key={project.project_id}
                              onClick={() => openProjectFlow(project)}
                              onKeyDown={(event) => {
                                if (event.key === "Enter" || event.key === " ") {
                                  event.preventDefault();
                                  openProjectFlow(project);
                                }
                              }}
                              role="button"
                              tabIndex={0}
                            >
                              <div className="projectOverviewMiniCard__header">
                                {project.avatar ? (
                                  <img
                                    alt={project.project_name}
                                    className="projectAvatar"
                                    src={project.avatar}
                                  />
                                ) : (
                                  <div
                                    className="projectAvatar projectAvatar--fallback"
                                    style={{
                                      background: getProjectFallbackColor(
                                        project.project_name
                                      ),
                                    }}
                                  >
                                    {project.project_name.slice(0, 1)}
                                  </div>
                                )}
                                <div>
                                  <div className="projectOverviewMiniCard__titleRow">
                                    <h4>{project.project_name}</h4>
                                    {overdueContentCount > 0 && (
                                      <ProjectOverdueBadge
                                        getMemberDisplayName={getMemberDisplayName}
                                        items={overdueContentItems}
                                        projectName={project.project_name}
                                      />
                                    )}
                                  </div>
                                  <span>负责人：{getMemberDisplayName(project.creator)}</span>
                                  <span>
                                    项目成员：{getProjectMemberNames(project) || "暂无成员"}
                                  </span>
                                </div>
                              </div>
                              <ProjectContentItemList
                                getMemberDisplayName={getMemberDisplayName}
                                includeAll
                                projectId={project.project_id}
                                projectName={project.project_name}
                                projectTemplate={project.template}
                                preferredAssigneeId={matchedAssigneeId}
                                tasks={overviewTasks}
                                userId={userId}
                                onOpenContentItem={(focus) =>
                                  openProjectFlowById(focus.projectId, focus)
                                }
                              />
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="projectEmpty">
                        <ProjectOutlined />
                        <h3>
                          {workspaceProjects.length
                            ? "没有匹配的项目"
                            : "暂无项目数据"}
                        </h3>
                        <p>
                          {workspaceProjects.length
                            ? "换个项目名或成员昵称试试。"
                            : "创建项目后，这里会展示团队项目与成员任务节点。"}
                        </p>
                      </div>
                    )}
                  </Card>

                <section className="workspacePanelGrid">
                  <Card className="workspacePanel workspacePanel--wide">
                      <div className="workspacePanel__header">
                        <h3>团队人员</h3>
                        <span>{teamMembers.length} 人</span>
                      </div>
                      <div className="departmentMemberGroups">
                        {[...departmentMemberGroups, ...(ungroupedMembers.length ? [{
                          departmentName: "未分配部门",
                          members: ungroupedMembers,
                        }] : [])].map((group) => (
                          <div
                            className="departmentMemberGroup"
                            key={group.departmentName}
                          >
                            <div className="departmentMemberGroup__header">
                              <strong>{group.departmentName}</strong>
                              <span>{group.members.length} 人</span>
                            </div>
                                {group.members.length ? (
                                  <div className="teamMemberGrid">
                                    {group.members.map((member) => {
                                  const memberTasks =
                                    memberCalendarTasks[member.username] || [];
                                  return (
                                    <div
                                      className="teamMemberCard"
                                      key={`${group.departmentName}-${member.username}`}
                                    >
                                      <MemberAvatar
                                        avatar={member.avatar}
                                        displayName={
                                          member.nickname ||
                                          member.username ||
                                          "成员"
                                        }
                                        fallback={getTextAvatar(
                                          member.nickname ||
                                            member.username ||
                                            "成员"
                                        )}
                                      />
                                      <div>
                                        <div className="teamMemberCard__title">
                                          <div>
                                            <strong>
                                              {member.nickname ||
                                                member.username ||
                                                "未命名成员"}
                                            </strong>
                                            <span>{member.username}</span>
                                          </div>
                                          <div className="teamMemberCard__titleActions">
                                            <button
                                              className="teamMemberCard__calendar"
                                              onClick={() => openMemberCalendar(member)}
                                              title="查看近一个月任务日历"
                                              type="button"
                                            >
                                              <CalendarOutlined />
                                            </button>
                                            <Tag
                                              className={
                                                member.role === 1
                                                  ? "teamMemberRole teamMemberRole--admin"
                                                  : "teamMemberRole"
                                              }
                                            >
                                              {getMemberRoleText(member.role)}
                                            </Tag>
                                          </div>
                                        </div>
                                        <div className="teamMemberCard__tags">
                                          {(member.department || []).length ? (
                                            (member.department || []).map((item) => (
                                              <Tag key={item}>{item}</Tag>
                                            ))
                                          ) : (
                                            <Tag>未分配</Tag>
                                          )}
                                        </div>
                                        <div className="teamMemberCard__footer">
                                          <small>
                                            {memberTasks.length
                                              ? `${memberTasks.length} 个近 30 天任务`
                                              : "点击日历查看任务"}
                                          </small>
                                          <div className="teamMemberCard__actions">
                                            <Button
                                              onClick={() => openMemberEditModal(member)}
                                              size="small"
                                              type="link"
                                            >
                                              编辑
                                            </Button>
                                            <Button
                                              danger
                                              onClick={() => setDeletingMember(member)}
                                              size="small"
                                              type="link"
                                            >
                                              删除
                                            </Button>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            ) : (
                              <div className="departmentMemberGroup__empty">
                                暂无成员
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </Card>
                  <Card className="workspacePanel">
                    <div className="workspacePanel__header">
                      <h3>部门管理</h3>
                      <Button
                        className="departmentCreateButton"
                        icon={<PlusOutlined />}
                        onClick={() => openDepartmentModal()}
                        size="small"
                        type="primary"
                      >
                        新增部门
                      </Button>
                    </div>
                    <div className="departmentManager">
                        {teamDepartmentOptions.length ? (
                          teamDepartmentOptions.map((item) => (
                            <div className="departmentManager__item" key={item}>
                              <span>{item}</span>
                              <div>
                                <Button
                                  onClick={() => openDepartmentModal(item)}
                                  size="small"
                                  type="link"
                                >
                                  编辑
                                </Button>
                                <Button
                                  danger
                                  onClick={() => setDeletingDepartment(item)}
                                  size="small"
                                  type="link"
                                >
                                  删除
                                </Button>
                              </div>
                            </div>
                          ))
                        ) : (
                          <div className="departmentManager__empty">
                            暂无部门，请先新增部门。
                          </div>
                        )}
                    </div>
                  </Card>
                </section>
              </>
            )}

            {isTeamAdmin && activeView === "board" && (
              <section className="boardLayout">
                <div className="boardToolbar">
                  <div>
                    <h2>项目</h2>
                    <p>当前只展示项目卡片，进入项目后再管理子任务与里程碑。</p>
                  </div>
                  <Button
                    icon={<PlusOutlined />}
                    onClick={openProjectModal}
                    type="primary"
                  >
                    新建项目
                  </Button>
                </div>

                {apiProjects.length > 0 ? (
                  <div className="projectGrid">
                    {sortProjectsForBoard(apiProjects).map((project) => {
                      const isArchivedProject = canUnarchiveProject(project);
                      const archiveDisabled =
                        !isArchivedProject && !canArchiveProject(project);
                      return (
                        <Card
                          key={project.project_id}
                          className={
                            canUnarchiveProject(project)
                              ? "projectCard projectCard--clickable projectCard--archived"
                              : "projectCard projectCard--clickable"
                          }
                          onClick={() => openProjectFlow(project)}
                          role="button"
                          tabIndex={0}
                          onKeyDown={(event) => {
                            if (event.key === "Enter" || event.key === " ") {
                              event.preventDefault();
                              openProjectFlow(project);
                            }
                          }}
                        >
                          <div className="projectCard__header">
                            <div className="projectCard__identity">
                              {project.avatar ? (
                                <img
                                  alt={project.project_name}
                                  className="projectAvatar"
                                  src={project.avatar}
                                />
                              ) : (
                                <div
                                  className="projectAvatar projectAvatar--fallback"
                                  style={{
                                    background: getProjectFallbackColor(
                                      project.project_name
                                    ),
                                  }}
                                >
                                  {project.project_name.slice(0, 1)}
                                </div>
                              )}
                              <div>
                                <h3>{project.project_name}</h3>
                                <span>
                                  产品经理：
                                  {getMemberDisplayName(project.creator) || "未记录"}
                                </span>
                                <span style={{ marginLeft: 12 }}>
                                  参与成员：{getProjectMemberSummary(project)}
                                </span>
                              </div>
                            </div>
                            <div className="projectCard__actions">
                              <Button
                                className="projectCard__editButton"
                                icon={<EditOutlined />}
                                onClick={(event) => {
                                  event.stopPropagation();
                                  openProjectEditModal(project);
                                }}
                                size="small"
                              >
                                编辑
                              </Button>
                              <Button
                                className="projectCard__archiveButton"
                                disabled={archiveDisabled}
                                loading={isMutationPending(
                                  `project:${
                                    isArchivedProject ? "unarchive" : "archive"
                                  }:${project.project_id}`
                                )}
                                onClick={(event) => {
                                  event.stopPropagation();
                                  if (isArchivedProject) {
                                    unarchiveProject(project);
                                    return;
                                  }
                                  archiveProject(project);
                                }}
                                size="small"
                                type={isArchivedProject ? "default" : "primary"}
                              >
                                {isArchivedProject ? "撤销归档" : "归档"}
                              </Button>
                              {canDeleteProject(project) && (
                                <Button
                                  className="projectCard__deleteButton"
                                  danger
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    setDeletingProject(project);
                                  }}
                                  size="small"
                                >
                                  删除
                                </Button>
                              )}
                            </div>
                          </div>

                        <div className="projectCard__progress">
                          <span>当前进度</span>
                          {project.template === "single" ? (
                            <div className="projectCard__singleProgress">
                              <div>
                                <strong>单任务视图</strong>
                                <span>
                                  已完成 {project.task_summary?.completed || 0}，
                                  待完成 {project.task_summary?.pending || 0}
                                </span>
                              </div>
                              <Progress
                                percent={
                                  project.task_summary?.total
                                    ? Math.round(
                                        ((project.task_summary.completed || 0) /
                                          project.task_summary.total) *
                                          100
                                      )
                                    : 0
                                }
                                showInfo
                                size="small"
                                status={
                                  project.status === "done" ? "success" : "active"
                                }
                              />
                            </div>
                          ) : (
                            <Steps
                              current={getProjectStepCurrent(project)}
                              size="small"
                            >
                              {projectStepNames.map((step, stepIndex) => (
                                <Steps.Step
                                  key={step}
                                  status={
                                    getProjectStepStatuses(project)[stepIndex]
                                  }
                                  title={step}
                                />
                              ))}
                            </Steps>
                          )}
                        </div>

                        <ProjectRequirementCardSummary
                          requirement={project.remark}
                        />
                        </Card>
                      );
                    })}
                  </div>
                ) : projectFlowLoading ||
                  (flowProjectId && !projectFlowLoadFailed) ? (
                  <div className="projectEmpty">
                    <ProjectOutlined />
                    <h3>节点图加载中</h3>
                    <p>正在根据当前项目链接恢复项目节点视图。</p>
                  </div>
                ) : (
                  <div className="projectEmpty">
                    <ProjectOutlined />
                    <h3>{isTeamAdmin ? "还没有项目" : "暂无参与项目"}</h3>
                    <p>
                      {isTeamAdmin
                        ? "点击“新建项目”创建第一个团队项目。"
                        : "当你被加入项目后，这里会展示你的项目卡片。"}
                    </p>
                  </div>
                )}
              </section>
            )}

            {activeView === "projectFlow" && (
              <section
                className={
                  isProjectFlowFullscreen
                    ? "projectFlowPage projectFlowPage--fullscreen"
                    : "projectFlowPage"
                }
              >
                {flowProject ? (
                  <>
                    {!isProjectFlowFullscreen && (
                      <>
                        <div className="projectFlowPage__toolbar">
                          <div className="projectFlowPage__navigation">
                            <Button
                              icon={<LeftOutlined />}
                              onClick={backToProjectBoard}
                            >
                              返回项目看板
                            </Button>
                            <Button
                              className="projectFlowPage__requirementButton"
                              icon={<FileTextOutlined />}
                              onClick={() => setIsProjectRequirementOpen(true)}
                            >
                              项目需求
                            </Button>
                          </div>
                          <div className="projectFlowPage__heading">
                            <span>项目任务视图</span>
                            <div className="projectFlowPage__titleRow">
                              <h2>{flowProject.project_name}</h2>
                              {canEditProject(flowProject) && (
                                <Button
                                  aria-label="编辑项目资料"
                                  className="projectFlowPage__editProject"
                                  icon={<EditOutlined />}
                                  onClick={() => openProjectEditModal(flowProject)}
                                  size="small"
                                  title="编辑项目资料"
                                />
                              )}
                            </div>
                          </div>
                          <div className="projectFlowPage__meta">
                            <span>
                              负责人：{getMemberDisplayName(flowProject.creator)}
                            </span>
                            <span>
                              {projectFlowRenderMeta.taskMemberCount}{" "}
                              名存在任务模块成员
                            </span>
                            <span>{projectFlowRenderMeta.taskCount} 个任务模块</span>
                            <span>
                              已完成 {projectFlowData.completedCount} 个，待完成{" "}
                              {projectFlowData.pendingCount} 个
                            </span>
                            <span>
                              协作：
                              {projectFlowCollabStatus === "connected"
                                ? `${projectFlowCollaborators.length + 1} 人在线`
                                : projectFlowCollabStatus === "connecting"
                                ? "连接中"
                                : "未连接"}
                            </span>
                          </div>
                        </div>
                        {flowProject.template === "phased" && (
                          <ProjectPhaseSwitcher
                            activePhase={activeProjectPhase}
                            canManage={canManageProjectPhases}
                            disabled={projectPhaseUpdating}
                            loadingPhase={
                              projectFlowLoading ? activeProjectPhase : undefined
                            }
                            onEditableChange={changeProjectPhaseEditable}
                            onPhaseChange={changeProjectPhase}
                            permissions={flowProject.phase_permissions}
                            summaries={flowProject.phase_summaries}
                          />
                        )}
                      </>
                    )}
                    <div
                      className={`projectFlowContent ${
                        isProjectFlowPanelCollapsed
                          ? "projectFlowContent--panelCollapsed"
                          : ""
                      }`}
                    >
                      <div
                        className={`projectFlowCanvas projectFlowCanvas--page ${
                          projectFlowRenderMode === "timeline"
                            ? "projectFlowCanvas--timeline"
                            : ""
                        }`}
                        ref={projectFlowCanvasRef}
                      >
                        <div className="projectFlowCanvas__viewToolbar">
                          <div className="projectFlowCanvas__viewHeading">
                            <strong>
                              {activeProjectViewLabel}
                              {projectFlowRenderMode === "timeline"
                                ? "时间线"
                                : "任务模块"}
                            </strong>
                            <span>
                              {projectFlowRenderMode === "timeline"
                                ? "按任务时间范围查看阶段排期与负责人"
                                : "按前置任务关系分层，每行最多展示 4 格"}
                            </span>
                          </div>
                          <ProjectFlowModeSwitch
                            disabled={isProjectFlowViewBlocked}
                            mode={projectFlowRenderMode}
                            onChange={changeProjectFlowViewMode}
                          />
                          <div className="projectFlowCanvas__viewActions">
                            {projectFlowRenderMode === "matrix" &&
                              isCurrentProjectPhaseEditable && (
                                <Button
                                  className="projectFlowCanvas__createTask"
                                  disabled={isProjectFlowViewBlocked}
                                  icon={<PlusOutlined />}
                                  onClick={() => openTaskCreateModal(flowProject)}
                                  type="primary"
                                >
                                  新增任务
                                </Button>
                              )}
                            {flowProject.template === "phased" && (
                              <Button
                                className="projectFlowCanvas__overview"
                                disabled={projectTaskOverviewLoading}
                                icon={<AppstoreOutlined />}
                                onClick={openProjectTaskOverview}
                              >
                                查看总览
                              </Button>
                            )}
                            <Button
                              className="projectFlowCanvas__fullscreen"
                              icon={
                                isProjectFlowFullscreen ? (
                                  <FullscreenExitOutlined />
                                ) : (
                                  <FullscreenOutlined />
                                )
                              }
                              onClick={() =>
                                setIsProjectFlowFullscreen((prev) => !prev)
                              }
                            >
                              {isProjectFlowFullscreen ? "恢复视图" : "全屏查看"}
                            </Button>
                          </div>
                        </div>
                        <div
                          aria-busy={projectFlowLoading}
                          className="projectFlowCanvas__viewBody"
                        >
                          <div
                            className="projectFlowCanvas__viewSurface"
                            {...(isProjectFlowViewBlocked
                              ? ({ inert: "" } as any)
                              : {})}
                          >
                            {projectFlowRenderMode === "matrix" ? (
                              <ProjectTaskMatrixView
                                canComment={
                                  isCurrentProjectPhaseEditable &&
                                  !isProjectFlowViewBlocked
                                }
                                canManage={
                                  isCurrentProjectPhaseEditable &&
                                  !isProjectFlowViewBlocked
                                }
                                currentUserId={userId}
                                getMemberName={getMemberDisplayName}
                                onAddRootTask={() => {
                                  if (flowProject) {
                                    openTaskCreateModal(flowProject);
                                  }
                                }}
                                onAddSuccessor={(
                                  task: ProjectTaskMatrixTask
                                ) => {
                                  if (!flowProject || !task.id) {
                                    return;
                                  }
                                  openTaskCreateModal(flowProject, {
                                    predecessor_ids: [task.id],
                                  });
                                }}
                                onComment={(task: ProjectTaskMatrixTask) =>
                                  openTaskCommentModal(task as ApiTaskItem)
                                }
                                onDelete={(task: ProjectTaskMatrixTask) =>
                                  setConfirmingTaskDelete(task as ApiTaskItem)
                                }
                                onEdit={(task: ProjectTaskMatrixTask) =>
                                  openTaskEditModal(task as ApiTaskItem)
                                }
                                onTaskClick={(task: ProjectTaskMatrixTask) => {
                                  const targetTask = task as ApiTaskItem;
                                  openTaskDetailModal(targetTask);
                                }}
                                phaseLabel={activeProjectViewLabel}
                                tasks={projectFlowTimelineTasks}
                                unreadCommentCounts={Object.fromEntries(
                                  Object.entries(unreadTaskCommentMap).map(
                                    ([taskId, items]) => [taskId, items.length]
                                  )
                                )}
                              />
                            ) : (
                              <ProjectTimelineView
                                getMemberName={getMemberDisplayName}
                                onTaskClick={(task: ProjectTimelineTask) => {
                                  const targetTask = task as ApiTaskItem;
                                  openTaskDetailModal(targetTask);
                                }}
                                tasks={projectFlowTimelineTasks}
                              />
                            )}
                          </div>
                          {projectFlowLoading && (
                            <ProjectFlowViewStateOverlay
                              phaseLabel={activeProjectViewLabel}
                              status="loading"
                            />
                          )}
                          {!projectFlowLoading && projectFlowLoadFailed && (
                            <ProjectFlowViewStateOverlay
                              onRetry={() =>
                                fetchProjectFlowSnapshot(flowProject.project_id, {
                                  showLoading: true,
                                })
                              }
                              phaseLabel={activeProjectViewLabel}
                              status="error"
                            />
                          )}
                        </div>
                      </div>
                      <aside
                        className={`projectFlowLogPanel ${
                          isProjectFlowPanelCollapsed
                            ? "projectFlowLogPanel--collapsed"
                            : ""
                        }`}
                      >
                        {isProjectFlowPanelCollapsed ? (
                          <button
                            className="projectFlowPanelCollapseButton"
                            onClick={() => setIsProjectFlowPanelCollapsed(false)}
                            title="展开右侧面板"
                            type="button"
                          >
                            <MenuUnfoldOutlined />
                          </button>
                        ) : null}
                        {!isProjectFlowPanelCollapsed && (
                          <>
                        <div className="projectFlowLogPanel__header">
                          <div className="projectFlowLogPanel__titleRow">
                            <div>
                              <strong>
                                {projectFlowPanelTab === "announcements"
                                  ? "项目公告"
                                  : projectFlowPanelTab === "attachments"
                                  ? "项目附件"
                                  : projectFlowPanelTab === "members"
                                  ? "成员管理"
                                  : "项目日志"}
                              </strong>
                              <span>
                                {projectFlowPanelTab === "announcements"
                                  ? `${projectAnnouncementList.length} 条公告`
                                  : projectFlowPanelTab === "attachments"
                                  ? `${flowProject.attachments?.length || 0} 个文件`
                                  : projectFlowPanelTab === "members"
                                  ? `${flowProject.members.length} 名项目成员`
                                  : `${projectLogList.length} 条动态`}
                              </span>
                            </div>
                            <button
                              className="projectFlowPanelHeaderButton"
                              onClick={() => setIsProjectFlowPanelCollapsed(true)}
                              title="收起右侧面板"
                              type="button"
                            >
                              <MenuFoldOutlined />
                            </button>
                          </div>
                          <div className="projectFlowPanelTabs">
                            <button
                              className={
                                projectFlowPanelTab === "announcements"
                                  ? "active"
                                  : ""
                              }
                              onClick={() =>
                                setProjectFlowPanelTab("announcements")
                              }
                              type="button"
                            >
                              <Badge
                                count={getProjectAnnouncementUnreadCount()}
                                size="small"
                              >
                                <span>公告</span>
                              </Badge>
                            </button>
                            <button
                              className={
                                projectFlowPanelTab === "attachments"
                                  ? "active"
                                  : ""
                              }
                              onClick={() => setProjectFlowPanelTab("attachments")}
                              type="button"
                            >
                              附件
                            </button>
                            {canManageFlowProjectMembers && (
                              <button
                                className={
                                  projectFlowPanelTab === "members" ? "active" : ""
                                }
                                onClick={() => setProjectFlowPanelTab("members")}
                                type="button"
                              >
                                成员
                              </button>
                            )}
                            <button
                              className={
                                projectFlowPanelTab === "logs" ? "active" : ""
                              }
                              onClick={() => setProjectFlowPanelTab("logs")}
                              type="button"
                            >
                              日志
                            </button>
                          </div>
                          <div className="projectFlowLogPanel__actions">
                            {projectFlowPanelTab === "announcements" &&
                              isActiveFlowProjectParticipant && (
                              <Button
                                className="projectFlowPanelActionButton"
                                onClick={openAnnouncementModal}
                                size="small"
                              >
                                新增公告
                              </Button>
                            )}
                            {projectFlowPanelTab === "attachments" &&
                              isActiveFlowProjectParticipant && (
                              <Upload {...projectAttachmentUploadProps}>
                                <Button
                                  className="projectFlowPanelActionButton"
                                  icon={<UploadOutlined />}
                                  size="small"
                                >
                                  上传附件
                                </Button>
                              </Upload>
                            )}
                            {projectFlowPanelTab === "members" && (
                              <Button
                                className="projectFlowPanelActionButton"
                                onClick={openProjectMemberAddModal}
                                size="small"
                              >
                                新增成员
                              </Button>
                            )}
                          </div>
                        </div>
                        <div className="projectFlowLogPanel__list">
                          {projectFlowPanelTab === "announcements" ? (
                            <div className="projectAnnouncementPanel">
                              {projectAnnouncementLoading ? (
                                <div className="projectFlowLogPanel__empty">
                                  公告加载中...
                                </div>
                              ) : projectAnnouncementList.length ? (
                                <div className="projectAnnouncementList">
                                  {projectAnnouncementList.map((item) => (
                                    <div
                                      className={`projectAnnouncementItem ${
                                        item.read_at
                                          ? ""
                                          : "projectAnnouncementItem--unread"
                                      }`}
                                      key={item.message_id}
                                    >
                                      <div className="projectAnnouncementItem__header">
                                        <strong>
                                          {item.message?.title || "项目公告"}
                                        </strong>
                                        {!item.read_at && <Tag color="red">未读</Tag>}
                                      </div>
                                      <div
                                        className="projectAnnouncementItem__content"
                                        onClick={handleTaskDetailContentClick}
                                        dangerouslySetInnerHTML={{
                                          __html:
                                            item.message?.content ||
                                            "<p>暂无公告内容</p>",
                                        }}
                                      />
                                      <div className="projectAnnouncementItem__meta">
                                        <span>
                                          {getMemberDisplayName(
                                            item.message?.sender_id || ""
                                          ) ||
                                            item.message?.sender_id ||
                                            "未知发送人"}
                                        </span>
                                        <span>
                                          {formatMessageTime(
                                            item.message?.created_at ||
                                              item.created_at
                                          )}
                                        </span>
                                      </div>
                                      {!item.read_at && (
                                        <Button
                                          onClick={() => markMessageRead(item)}
                                          size="small"
                                          type="link"
                                        >
                                          我已查收
                                        </Button>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              ) : (
                                <div className="projectFlowLogPanel__empty">
                                  暂无与你相关的项目公告
                                </div>
                              )}
                            </div>
                          ) : projectFlowPanelTab === "attachments" ? (
                            (() => {
                              const attachments = flowProject.attachments || [];
                              const folders = flowProject.attachment_folders || [];
                              const visibleAttachments = activeAttachmentFolderId
                                ? attachments.filter(
                                    (attachment) =>
                                      attachment.folder_id ===
                                      activeAttachmentFolderId
                                  )
                                : attachments;
                              return (
                                <div className="projectAttachmentPanel">
                                  <div className="projectAttachmentFolders">
                                    <button
                                      className={
                                        activeAttachmentFolderId === ""
                                          ? "active"
                                          : ""
                                      }
                                      onClick={() => setActiveAttachmentFolderId("")}
                                      type="button"
                                    >
                                      全部
                                      <span>{attachments.length}</span>
                                    </button>
                                    {folders.map((folder) => (
                                      <button
                                        className={
                                          activeAttachmentFolderId ===
                                          folder.folder_id
                                            ? "active"
                                            : ""
                                        }
                                        key={folder.folder_id}
                                        onClick={() =>
                                          setActiveAttachmentFolderId(
                                            folder.folder_id
                                          )
                                        }
                                        type="button"
                                      >
                                        {folder.name}
                                        <span>
                                          {
                                            attachments.filter(
                                              (attachment) =>
                                                attachment.folder_id ===
                                                folder.folder_id
                                            ).length
                                          }
                                        </span>
                                      </button>
                                    ))}
                                  </div>
                                  <div className="projectAttachmentSystemHint">
                                    附件由后端按文件类型自动归类，且仅归属当前项目。
                                  </div>
                                  {visibleAttachments.length ? (
                                    visibleAttachments.map((attachment) => (
                                      <div
                                        className="projectAttachmentItem"
                                        key={attachment.attachment_id}
                                      >
                                        <div className="projectAttachmentItem__icon">
                                          <PaperClipOutlined />
                                        </div>
                                        <div className="projectAttachmentItem__body">
                                          <strong>{attachment.name}</strong>
                                          <span>
                                            {formatFileSize(attachment.size)} ·{" "}
                                            {getMemberDisplayName(
                                              attachment.uploaded_by
                                            ) || attachment.uploaded_by}{" "}
                                            ·{" "}
                                            {formatMessageTime(
                                              attachment.uploaded_at
                                            )}
                                          </span>
                                        </div>
                                        <div className="projectAttachmentItem__actions">
                                          <button
                                            onClick={() =>
                                              downloadProjectAttachment(
                                                attachment
                                              )
                                            }
                                            title="下载附件"
                                            type="button"
                                          >
                                            <DownloadOutlined />
                                          </button>
                                          {isActiveFlowProjectParticipant && (
                                            <button
                                              onClick={() =>
                                                setRemovingProjectAttachment(
                                                  attachment
                                                )
                                              }
                                              title="删除附件"
                                              type="button"
                                            >
                                              <DeleteOutlined />
                                            </button>
                                          )}
                                        </div>
                                      </div>
                                    ))
                                  ) : (
                                    <div className="projectFlowLogPanel__empty">
                                      当前文件夹暂无附件
                                    </div>
                                  )}
                                </div>
                              );
                            })()
                          ) : projectFlowPanelTab === "members" ? (
                            <div className="projectFlowMemberPanel">
                              {flowProject.members.map((member) => {
                                const memberName = getMemberDisplayName(member.user_id);
                                const memberTasks = flowTaskSnapshot.filter(
                                  (task) =>
                                    task.project_id === flowProject.project_id &&
                                    taskHasAssignee(task, member.user_id)
                                );
                                const isCreator =
                                  member.user_id === flowProject.creator;
                                return (
                                  <div
                                    className="projectFlowMemberPanel__item"
                                    key={member.user_id}
                                  >
                                    <Avatar className="projectFlowMemberPanel__avatar">
                                      {getTextAvatar(memberName || member.user_id).initial}
                                    </Avatar>
                                    <div className="projectFlowMemberPanel__body">
                                      <strong>{memberName || member.user_id}</strong>
                                      <span>
                                        {isCreator
                                          ? "项目负责人"
                                          : member.role === "manager"
                                          ? "项目管理员"
                                          : "项目成员"}{" "}
                                        · {memberTasks.length} 个任务节点
                                      </span>
                                    </div>
                                    {canRemoveProjectMember(member) ? (
                                      <button
                                        className="projectFlowMemberPanel__remove"
                                        onClick={() =>
                                          setRemovingProjectMember(member)
                                        }
                                        type="button"
                                      >
                                        移除
                                      </button>
                                    ) : (
                                      <span className="projectFlowMemberPanel__locked">
                                        {isCreator ? "负责人" : "无权限"}
                                      </span>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          ) : projectLogLoading ? (
                            <div className="projectFlowLogPanel__empty">
                              日志加载中...
                            </div>
                          ) : projectLogList.length ? (
                            projectLogList.map((log) => (
                              <button
                                className={`projectFlowLogItem projectFlowLogItem--${getProjectLogActionTone(
                                  log.action
                                )}`}
                                key={log.id}
                                onClick={() => setSelectedProjectLog(log)}
                                type="button"
                              >
                                <div className="projectFlowLogItem__dot" />
                                <div>
                                  <div className="projectFlowLogItem__header">
                                    <strong
                                      className={`projectFlowLogItem__badge projectFlowLogItem__badge--${getProjectLogActionTone(
                                        log.action
                                      )}`}
                                    >
                                      {getProjectLogActionText(log.action)}
                                    </strong>
                                    <span>{formatMessageTime(log.created_at)}</span>
                                  </div>
                                  <p>{getProjectLogSummary(log)}</p>
                                  <small>
                                    {getMemberDisplayName(log.operator)} ·{" "}
                                    {getProjectLogTargetText(log.target_type)}
                                  </small>
                                </div>
                              </button>
                            ))
                          ) : (
                            <div className="projectFlowLogPanel__empty">
                              暂无项目日志
                            </div>
                          )}
                        </div>
                          </>
                        )}
                      </aside>
                    </div>
                  </>
                ) : (
                  <div className="projectEmpty">
                    <ProjectOutlined />
                    <h3>未找到项目</h3>
                    <p>请返回项目看板重新选择要查看的项目。</p>
                    <Button onClick={backToProjectBoard} type="primary">
                      返回项目看板
                    </Button>
                  </div>
                )}
              </section>
            )}

            {isTeamAdmin && activeView === "profile" && (
              <section className="profileGrid">
                <Card className="workspacePanel profileCard">
                  <div className="profileCard__hero">
                    <div className="profileCard__avatar">
                      <Avatar
                        size={72}
                        src={userAvatarUrl || undefined}
                        style={
                          userAvatarUrl
                            ? undefined
                            : {
                                background: getTextAvatar(nickname || userId)
                                  .background,
                              }
                        }
                      >
                        {!userAvatarUrl &&
                          getTextAvatar(nickname || userId).initial}
                      </Avatar>
                      <button onClick={openProfileModal} type="button">
                        修改头像
                      </button>
                    </div>
                    <div>
                      <h3>{nickname || userId}</h3>
                      <span>{userId}</span>
                    </div>
                  </div>
                  <div className="profileInfoList">
                    <div className="profileInfoItem">
                      <span>邮箱</span>
                      <strong>{userMail || "未填写邮箱"}</strong>
                      <Button
                        onClick={openProfileModal}
                        size="small"
                        type="link"
                      >
                        编辑
                      </Button>
                    </div>
                    <div className="profileInfoItem">
                      <span>昵称</span>
                      <strong>{nickname || "未填写昵称"}</strong>
                      <Button onClick={openProfileModal} size="small" type="link">
                        编辑
                      </Button>
                    </div>
                    <div className="profileInfoItem">
                      <span>电话</span>
                      <strong>{userPhone || "---"}</strong>
                      <Button onClick={openProfileModal} size="small" type="link">
                        编辑
                      </Button>
                    </div>
                    <div className="profileInfoItem">
                      <span>密码</span>
                      <strong>已设置</strong>
                      <Button onClick={openPasswordModal} size="small" type="link">
                        修改
                      </Button>
                    </div>
                    <div className="profileInfoItem">
                      <span>部门</span>
                      <div className="profileDepartmentTags">
                        {department.length ? (
                          department.map((item) => <Tag key={item}>{item}</Tag>)
                        ) : (
                          <strong>未填写部门</strong>
                        )}
                      </div>
                    </div>
                    <div className="profileInfoItem">
                      <span>团队码</span>
                      <strong>{teamCode || "未记录"}</strong>
                      <Button
                        disabled={!teamCode}
                        onClick={() => copyText(teamCode)}
                        size="small"
                        type="link"
                      >
                        复制
                      </Button>
                    </div>
                  </div>
                </Card>

                <Card className="workspacePanel">
                  <div className="workspacePanel__header">
                    <h3>个人偏好</h3>
                    <SettingOutlined />
                  </div>
                  <ul className="workspaceBulletList">
                    <li>默认查看工作台概览</li>
                    <li>接收项目里程碑提醒</li>
                    <li>任务更新同步到个人消息中心</li>
                  </ul>
                </Card>
              </section>
            )}
          </main>
        </div>
      ) : (
        <Login
          onLogin={() => {
            const authSession = getAuthSession();
            hydrateAuthSession(authSession);
            setActiveView("workspace");
            const nextRouteMap =
              authSession?.user_type === "team_admin"
                ? viewRouteMap
                : memberViewRouteMap;
            window.history.pushState(null, "", nextRouteMap.workspace);
          }}
        />
      )}

      <TaskCommentModal
        canComment={
          commentingTaskNode?.status !== "discarded" && canCommentSelectedTask()
        }
        currentUserId={commentingTaskNode?.status === "discarded" ? "" : userId}
        editingCommentId={editingTaskComment?.comment_id}
        editor={
          <div
            className="taskRichEditor"
            contentEditable
            onInput={handleTaskCommentInput}
            onPaste={handleTaskCommentPaste}
            ref={taskCommentEditorRef}
            suppressContentEditableWarning
          />
        }
        formatTime={formatMessageTime}
        getMemberName={getMemberDisplayName}
        onCancelEdit={resetTaskCommentEditor}
        onClose={closeTaskCommentModal}
        onContentClick={handleTaskDetailContentClick}
        onDeleteComment={(task, comment) =>
          setDeletingTaskComment({
            task: task as ApiTaskItem,
            comment,
          })
        }
        onAddCommentReply={(task, comment, content) =>
          addTaskCommentReply(
            task as ApiTaskItem,
            comment as ApiTaskComment,
            content
          )
        }
        onUpdateCommentReply={(task, comment, reply, content) =>
          updateTaskCommentReply(
            task as ApiTaskItem,
            comment as ApiTaskComment,
            reply as ApiTaskCommentReply,
            content
          )
        }
        onDeleteCommentReply={(task, comment, reply) =>
          deleteTaskCommentReply(
            task as ApiTaskItem,
            comment as ApiTaskComment,
            reply as ApiTaskCommentReply
          )
        }
        onEditComment={(task, comment) =>
          openTaskCommentModal(task as ApiTaskItem, comment)
        }
        onSubmit={submitTaskComment}
        onSubtaskIdsChange={setTaskCommentSubtaskIds}
        open={!!commentingTaskNode}
        submitting={
          !!commentingTaskNode?.id &&
          isMutationPending(`task:comment:${commentingTaskNode.id}`)
        }
        selectedSubtaskIds={taskCommentSubtaskIds}
        subtasks={selectedTaskSubtasks}
        task={commentingTaskNode}
      />

      <Modal
        bodyStyle={{ maxHeight: "calc(100vh - 150px)", overflowY: "auto" }}
        centered
        className="projectProfileModal"
        maskClosable={false}
        keyboard={false}
        title={editingProject ? "编辑项目资料" : "新建项目"}
        open={isProjectModalOpen}
        onCancel={closeProjectModal}
        footer={null}
        destroyOnClose
        width={920}
      >
        <Form<ProjectFormValues>
          className="projectForm"
          form={projectForm}
          layout="vertical"
          onFinish={createProject}
        >
          {!editingProject && (
            <Form.Item
              label="项目模板"
              name="template"
              rules={[{ required: true, message: "请选择项目模板" }]}
            >
              <Radio.Group className="projectTemplateSelector">
                <Radio.Button value="phased">
                  <span className="projectTemplateSelector__icon">
                    <ProjectOutlined />
                  </span>
                  <span>
                    <strong>标准四阶段</strong>
                    <small>立项、研发、宣发、生产独立任务视图</small>
                  </span>
                </Radio.Button>
                <Radio.Button value="single">
                  <span className="projectTemplateSelector__icon">
                    <AppstoreOutlined />
                  </span>
                  <span>
                    <strong>单任务视图</strong>
                    <small>直接管理任务，适合轻量或临时项目</small>
                  </span>
                </Radio.Button>
              </Radio.Group>
            </Form.Item>
          )}

          <Form.Item
            label="项目名"
            name="name"
            rules={[{ required: true, message: "请输入项目名" }]}
          >
            <Input placeholder="例如：项目管理平台 v1.0" />
          </Form.Item>

          <Form.Item
            label={
              <div className="projectForm__memberLabel">
                <span>参与人员</span>
                <Button onClick={selectAllProjectMembers} size="small" type="link">
                  全部选择
                </Button>
              </div>
            }
            name="members"
          >
            <Select
              className="projectForm__memberSelect"
              listHeight={320}
              mode="multiple"
              optionFilterProp="label"
              options={getProjectMemberSelectOptions()}
              placeholder="请选择当前团队的参与人员"
              showSearch
            />
          </Form.Item>
          {editingProject && (
            <div className="projectForm__memberHint">
              <span>
                项目负责人：
                <strong>
                  {getMemberDisplayName(editingProject.creator) ||
                    editingProject.creator}
                </strong>
              </span>
              {editingProject.team_admin_id &&
                editingProject.team_admin_id !== editingProject.creator && (
                  <span>
                    团队管理员：
                    <strong>
                      {getMemberDisplayName(editingProject.team_admin_id) ||
                        editingProject.team_admin_id}
                    </strong>
                  </span>
                )}
              <small>
                项目负责人和团队管理员固定保留；移除其他成员只会收回项目权限，
                不会删除其历史任务。
              </small>
            </div>
          )}

          <Form.Item label="项目需求">
            <MarkdownRequirementEditor
              ariaLabel="项目需求"
              className="projectForm__requirementEditor"
              onChange={setProjectRequirementHtml}
              onError={(errorMessage) => message.warning(errorMessage)}
              onImageClick={setPreviewImage}
              showPreview={false}
              value={projectRequirementHtml}
            />
            <span className="projectForm__requirementHint">
              使用 Markdown 编写，支持直接粘贴图片；可进入项目后查看实时预览。
            </span>
          </Form.Item>

          <Form.Item label="项目头像">
            <div className="projectAvatarUpload">
              {projectAvatar ? (
                <img alt="项目头像预览" src={projectAvatar} />
              ) : (
                <PictureOutlined />
              )}
              <Upload {...avatarUploadProps}>
                <Button>{projectAvatar ? "替换头像" : "上传头像"}</Button>
              </Upload>
              {editingProject && projectAvatar && (
                <Button danger onClick={() => setProjectAvatar("")}>
                  移除头像
                </Button>
              )}
            </div>
          </Form.Item>

          <div className="projectForm__footer">
            <Button onClick={closeProjectModal}>取消</Button>
            <Button
              htmlType="submit"
              loading={isMutationPending(
                editingProject
                  ? `project:update:${editingProject.project_id}`
                  : "project:create"
              )}
              type="primary"
            >
              {editingProject ? "保存修改" : "创建项目"}
            </Button>
          </div>
        </Form>
      </Modal>

      <ProjectRequirementModal
        editable={!!flowProject && canEditProject(flowProject)}
        onClose={() => setIsProjectRequirementOpen(false)}
        onError={(errorMessage) => message.warning(errorMessage)}
        onImageClick={setPreviewImage}
        onSave={saveFlowProjectRequirement}
        open={isProjectRequirementOpen && !!flowProject}
        projectName={flowProject?.project_name}
        requirement={flowProject?.remark}
        saving={
          !!flowProject &&
          isMutationPending(`project:requirement:${flowProject.project_id}`)
        }
      />

      <Modal
        maskClosable={false}
        keyboard={false}
        title={editingDepartment ? "编辑部门" : "新增部门"}
        open={isDepartmentModalOpen}
        onCancel={closeDepartmentModal}
        footer={null}
        destroyOnClose
      >
        {editingDepartment ? (
          <Form<DepartmentEditFormValues>
            className="projectForm"
            form={departmentEditForm}
            layout="vertical"
            onFinish={updateDepartment}
          >
            <Form.Item
              label="部门名称"
              name="name"
              rules={[{ required: true, message: "请输入部门名称" }]}
            >
              <Input placeholder="例如：Researcher" />
            </Form.Item>
            <div className="projectForm__footer">
              <Button onClick={closeDepartmentModal}>取消</Button>
              <Button
                htmlType="submit"
                loading={isMutationPending(
                  `department:update:${editingDepartment}`
                )}
                type="primary"
              >
                保存修改
              </Button>
            </div>
          </Form>
        ) : (
          <Form<DepartmentFormValues>
            className="projectForm"
            form={departmentForm}
            layout="vertical"
            onFinish={createDepartment}
          >
            <Form.Item
              label="部门名称"
              name="name"
              rules={[{ required: true, message: "请输入部门名称" }]}
            >
              <Input placeholder="例如：Frontend / Backend / Researcher" />
            </Form.Item>
            <div className="projectForm__footer">
              <Button onClick={closeDepartmentModal}>取消</Button>
              <Button
                htmlType="submit"
                loading={isMutationPending("department:create")}
                type="primary"
              >
                新增部门
              </Button>
            </div>
          </Form>
        )}
      </Modal>

      <Modal
        maskClosable={false}
        keyboard={false}
        className="memberCalendarModal"
        destroyOnClose
        footer={null}
        onCancel={() => setSelectedMemberCalendar(null)}
        open={!!selectedMemberCalendar}
        title="成员任务日历"
        width={760}
      >
        {selectedMemberCalendar && (
          <div className="memberCalendar">
            <div className="memberCalendar__profile">
              <MemberAvatar
                avatar={selectedMemberCalendar.avatar}
                displayName={
                  selectedMemberCalendar.nickname ||
                  selectedMemberCalendar.username ||
                  "成员"
                }
                fallback={getTextAvatar(
                  selectedMemberCalendar.nickname ||
                    selectedMemberCalendar.username ||
                    "成员"
                )}
              />
              <div>
                <strong>
                  {selectedMemberCalendar.nickname ||
                    selectedMemberCalendar.username ||
                    "未命名成员"}
                </strong>
                <span>从今天开始最近 30 天任务内容分布</span>
              </div>
            </div>
            <div className="memberCalendar__grid">
              {memberCalendarLoading ? (
                <div className="memberCalendar__loading">任务日历加载中...</div>
              ) : (
                getMemberMonthTaskGroups(selectedMemberCalendar.username).map(
                  (day) => (
                    <Popover
                      content={renderMemberCalendarDayTasks(day.tasks)}
                      key={day.key}
                      overlayClassName="memberCalendarTooltipOverlay"
                      placement="top"
                    >
                      <button
                        className={
                          day.tasks.length
                            ? day.isToday
                              ? "memberCalendarDay memberCalendarDay--today memberCalendarDay--active"
                              : "memberCalendarDay memberCalendarDay--active"
                            : day.isToday
                            ? "memberCalendarDay memberCalendarDay--today"
                            : "memberCalendarDay"
                        }
                        type="button"
                      >
                        <span>{day.label}</span>
                        <strong>{day.day}</strong>
                        <em>{day.tasks.length} 项内容</em>
                      </button>
                    </Popover>
                  )
                )
              )}
            </div>
          </div>
        )}
      </Modal>

      <Modal
        maskClosable={false}
        keyboard={false}
        title="编辑成员"
        open={!!editingMember}
        onCancel={closeMemberEditModal}
        footer={null}
        destroyOnClose
      >
        <Form<MemberEditFormValues>
          className="projectForm"
          form={memberEditForm}
          layout="vertical"
          onFinish={submitMemberEdit}
        >
          <Form.Item label="成员">
            <div className="memberDepartmentModal__user">
              <MemberAvatar
                avatar={editingMember?.avatar}
                displayName={
                  editingMember?.nickname || editingMember?.username || "成员"
                }
                fallback={getTextAvatar(
                  editingMember?.nickname || editingMember?.username || "成员"
                )}
              />
              <div>
                <strong>
                  {editingMember?.nickname ||
                    editingMember?.username ||
                    "未命名成员"}
                </strong>
                <span>{editingMember?.username}</span>
              </div>
            </div>
          </Form.Item>
          <Form.Item label="部门" name="department">
            <Select
              mode="multiple"
              options={teamDepartmentOptions.map((item) => ({
                label: item,
                value: item,
              }))}
              placeholder="请选择成员所属部门"
            />
          </Form.Item>
          <Form.Item
            label="角色"
            name="role"
            rules={[{ required: true, message: "请选择成员角色" }]}
          >
            <Select
              options={[
                { label: "成员", value: 0 },
                { label: "管理员", value: 1 },
              ]}
              placeholder="请选择成员角色"
            />
          </Form.Item>
          <div className="projectForm__footer">
            <Button onClick={closeMemberEditModal}>取消</Button>
            <Button
              htmlType="submit"
              loading={isMutationPending(
                `team-member:update:${editingMember?.username}`
              )}
              type="primary"
            >
              保存修改
            </Button>
          </div>
        </Form>
      </Modal>

      <ProfileEditorModal
        avatarUrl={userAvatarUrl}
        initialValues={{
          mail: userMail,
          nickname,
          phone: userPhone,
        }}
        onCancel={closeProfileModal}
        onSubmit={submitProfile}
        open={isProfileModalOpen}
        submitting={isMutationPending("profile:update")}
      />

      <Modal
        maskClosable={false}
        keyboard={false}
        title="修改密码"
        open={isPasswordModalOpen}
        onCancel={closePasswordModal}
        footer={null}
        destroyOnClose
      >
        <Form<PasswordFormValues>
          className="projectForm"
          form={passwordForm}
          layout="vertical"
          onFinish={submitPassword}
        >
          <Form.Item
            label="当前密码"
            name="old_password"
            rules={[{ required: true, message: "请输入当前密码" }]}
          >
            <Input.Password placeholder="请输入当前密码" />
          </Form.Item>
          <Form.Item
            label="新密码"
            name="new_password"
            rules={[
              { required: true, message: "请输入新密码" },
              { min: 6, message: "新密码不能少于 6 位" },
            ]}
          >
            <Input.Password placeholder="请输入新密码" />
          </Form.Item>
          <Form.Item
            dependencies={["new_password"]}
            label="确认新密码"
            name="confirm_password"
            rules={[
              { required: true, message: "请再次输入新密码" },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue("new_password") === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error("两次输入的新密码不一致"));
                },
              }),
            ]}
          >
            <Input.Password placeholder="请再次输入新密码" />
          </Form.Item>
          <div className="projectForm__footer">
            <Button onClick={closePasswordModal}>取消</Button>
            <Button
              htmlType="submit"
              loading={isMutationPending("password:update")}
              type="primary"
            >
              保存
            </Button>
          </div>
        </Form>
      </Modal>

      <Modal
        maskClosable={false}
        keyboard={false}
        title="新增项目公告"
        open={isAnnouncementModalOpen}
        onCancel={closeAnnouncementModal}
        footer={null}
        destroyOnClose
      >
        <Form<ProjectAnnouncementFormValues>
          className="projectForm projectAnnouncementModalForm"
          form={announcementForm}
          initialValues={{ action_required: true }}
          layout="vertical"
          onFinish={createProjectAnnouncement}
        >
          <Form.Item label="公告内容">
            <div
              className="taskRichEditor"
              contentEditable
              onInput={handleAnnouncementContentInput}
              onPaste={handleAnnouncementContentPaste}
              ref={announcementContentEditorRef}
              suppressContentEditableWarning
            />
            <span className="taskRichEditor__hint">
              支持输入文本，也可以直接粘贴图片。
            </span>
          </Form.Item>
          <Form.Item label="查收人员">
            <Select
              key={announcementReceiverSelectKey}
              optionFilterProp="label"
              options={flowProject ? getProjectAnnouncementOptions(flowProject) : []}
              onSelect={(value) => addAnnouncementReceiver(value)}
              placeholder="选择后加入查收名单"
              showSearch
              value={announcementReceiverPicker}
            />
            <div className="announcementReceiverToolbar">
              <Button
                className="announcementReceiverToolbar__button"
                onClick={selectAllAnnouncementReceivers}
                size="small"
              >
                全选项目成员
              </Button>
              <span>{announcementReceivers.length} 人查收</span>
            </div>
            <div className="announcementReceiverTags">
              {announcementReceivers.length ? (
                announcementReceivers.map((receiverId) => (
                  <Tag
                    closable
                    key={receiverId}
                    onClose={(event) => {
                      event.preventDefault();
                      removeAnnouncementReceiver(receiverId);
                    }}
                  >
                    {getMemberDisplayName(receiverId) || receiverId}
                  </Tag>
                ))
              ) : (
                <span>请选择查收人员</span>
              )}
            </div>
          </Form.Item>
          <div className="projectAnnouncementForm__footer">
            <Form.Item name="action_required" valuePropName="checked">
              <Checkbox>需要查收</Checkbox>
            </Form.Item>
            <div>
              <Button onClick={closeAnnouncementModal}>取消</Button>
              <Button
                htmlType="submit"
                loading={isMutationPending("announcement:create")}
                type="primary"
              >
                发布
              </Button>
            </div>
          </div>
        </Form>
      </Modal>

      <ProjectPhasePermissionModal
        editable={pendingProjectPhasePermission?.editable || false}
        loading={projectPhaseUpdating}
        onCancel={() => {
          if (!projectPhaseUpdating) {
            setPendingProjectPhasePermission(null);
          }
        }}
        onConfirm={confirmProjectPhaseEditable}
        open={!!pendingProjectPhasePermission}
        phaseLabel={
          pendingProjectPhasePermission
            ? PROJECT_PHASE_LABELS[pendingProjectPhasePermission.phase]
            : ""
        }
      />

      <ProjectTaskOverviewModal
        getMemberName={getMemberDisplayName}
        loading={projectTaskOverviewLoading}
        onClose={() => setIsProjectTaskOverviewOpen(false)}
        onOpenPhase={openOverviewPhase}
        onOpenTask={openOverviewTask}
        open={isProjectTaskOverviewOpen}
        projectName={flowProject?.project_name || "项目任务总览"}
        tasksByPhase={projectTaskOverviewTasks as Record<
          ProjectPhase,
          ProjectTaskOverviewTask[]
        >}
      />

      <TaskNodeDetailModal
        assigneeNames={
          selectedTaskNode
            ? getTaskAssigneeNames(selectedTaskNode.assignees)
            : []
        }
        canComment={
          selectedTaskNode?.status !== "discarded" && canCommentSelectedTask()
        }
        canManage={
          isCurrentProjectPhaseEditable && projectFlowRenderMode !== "timeline"
        }
        canManageContentStatus={
          canManageCurrentPhaseContentStatus &&
          selectedTaskNode?.status !== "discarded"
        }
        currentUserId={selectedTaskNode?.status === "discarded" ? "" : userId}
        focusedContentItemId={focusedTaskContentItemId}
        focusedSubtaskId={focusedTaskSubtaskId}
        dueTimeText={
          selectedTaskNode
            ? formatTaskDueTime(selectedTaskNode.due_time)
            : ""
        }
        formatTime={formatMessageTime}
        getMemberName={getMemberDisplayName}
        loadingSubtasks={selectedTaskSubtasksLoading}
        onAddComment={(task, subtaskId) =>
          openTaskCommentModal(
            task as ApiTaskItem,
            undefined,
            subtaskId ? [subtaskId] : []
          )
        }
        onClose={() => {
          setSelectedTaskNode(null);
          setSelectedTaskSubtasks([]);
          setFocusedTaskSubtaskId("");
          setFocusedTaskContentItemId("");
        }}
        onContentClick={handleTaskDetailContentClick}
        onDelete={(task) => deleteTaskFromDetail(task as ApiTaskItem)}
        onApproveSubtask={approveTaskSubtask}
        onCompleteSubtask={completeTaskSubtask}
        onDeleteComment={(task, comment) =>
          setDeletingTaskComment({
            task: task as ApiTaskItem,
            comment: comment as ApiTaskComment,
          })
        }
        onAddCommentReply={(task, comment, content) =>
          addTaskCommentReply(
            task as ApiTaskItem,
            comment as ApiTaskComment,
            content
          )
        }
        onUpdateCommentReply={(task, comment, reply, content) =>
          updateTaskCommentReply(
            task as ApiTaskItem,
            comment as ApiTaskComment,
            reply as ApiTaskCommentReply,
            content
          )
        }
        onDeleteCommentReply={(task, comment, reply) =>
          deleteTaskCommentReply(
            task as ApiTaskItem,
            comment as ApiTaskComment,
            reply as ApiTaskCommentReply
          )
        }
        onEdit={(task) => editTaskFromDetail(task as ApiTaskItem)}
        onEditComment={(task, comment) =>
          openTaskCommentModal(
            task as ApiTaskItem,
            comment as ApiTaskComment
          )
        }
        onRejectSubtask={rejectTaskSubtask}
        onHandoffSubtask={handoffTaskSubtask}
        onSubmitSubtaskReview={submitTaskSubtaskReview}
        onUpdateSubtask={updateTaskSubtask}
        open={!!selectedTaskNode}
        phaseLabel={
          selectedTaskNode
            ? PROJECT_PHASE_LABELS[selectedTaskNode.phase]
            : ""
        }
        statusText={
          selectedTaskNode ? getTaskStatusText(selectedTaskNode.status) : ""
        }
        subtasks={selectedTaskSubtasks}
        task={selectedTaskNode}
      />

      <Modal
        maskClosable={false}
        keyboard={false}
        className="taskImagePreviewModal"
        footer={null}
        onCancel={() => setPreviewImage("")}
        open={!!previewImage}
        title={null}
        width="92vw"
        centered
        destroyOnClose
      >
        {previewImage && <img alt="图片预览" src={previewImage} />}
      </Modal>

      <TaskEditorModal
        form={taskEditorForm}
        mode={creatingTaskProject ? "create" : "edit"}
        onAddTag={addTaskDraftTag}
        onCancel={creatingTaskProject ? closeTaskCreateModal : closeTaskEditModal}
        onFinish={creatingTaskProject ? createTaskNode : updateTaskNode}
        onRemoveTag={removeTaskDraftTag}
        onTagInputChange={setTaskTagInput}
        open={Boolean(creatingTaskProject || editingTaskNode)}
        readOnly={editingTaskNode?.status === "discarded"}
        saving={
          creatingTaskProject
            ? isMutationPending("task:create")
            : isMutationPending(`task:update:${editingTaskNode?.id}`)
        }
        statusOptions={getTaskEditorStatusOptions(
          creatingTaskProject ? creatingTaskSubtasks : editingTaskSubtasks
        )}
        subtaskEditing={taskSubtaskEditing}
        subtaskManagerProps={{
          canManage:
            isCurrentProjectPhaseEditable &&
            editingTaskNode?.status !== "discarded",
          canManageContentStatus:
            canManageCurrentPhaseContentStatus &&
            editingTaskNode?.status !== "discarded",
          currentUserId: userId,
          defaultAssignee: userId,
          getMemberName: getMemberDisplayName,
          loading: creatingTaskProject ? false : editingTaskSubtasksLoading,
          memberOptions: flowProject
            ? getProjectTaskAssigneeOptions(flowProject)
            : [],
          onCreate: creatingTaskProject
            ? createTaskDraftSubtask
            : createTaskSubtask,
          onDelete: creatingTaskProject
            ? deleteTaskDraftSubtask
            : deleteTaskSubtask,
          onApprove: creatingTaskProject
            ? async () => false
            : approveTaskSubtask,
          onComplete: creatingTaskProject
            ? async () => false
            : completeTaskSubtask,
          onHandoff: creatingTaskProject
            ? async () => false
            : handoffTaskSubtask,
          onEditingChange: setTaskSubtaskEditing,
          onUpdate: creatingTaskProject
            ? updateTaskDraftSubtask
            : updateTaskSubtask,
          onReject: creatingTaskProject
            ? async () => false
            : rejectTaskSubtask,
          onSubmitReview: creatingTaskProject
            ? async () => false
            : submitTaskSubtaskReview,
          subtasks: creatingTaskProject
            ? creatingTaskSubtasks
            : editingTaskSubtasks,
        }}
        tagInput={taskTagInput}
        tags={taskDraftTags}
      />

      <Modal
        cancelText="返回编辑"
        centered
        className={`taskLifecycleConfirmModal taskLifecycleConfirmModal--${
          pendingTaskLifecycle?.kind || "discard"
        }`}
        keyboard={false}
        maskClosable={false}
        okButtonProps={{
          danger: pendingTaskLifecycle?.kind === "discard",
          disabled:
            pendingTaskLifecycle?.kind === "discard" &&
            taskDiscardReason.trim().length < 2,
        }}
        okText={
          pendingTaskLifecycle?.kind === "discard" ? "确认废弃" : "确认恢复"
        }
        onCancel={() => {
          setPendingTaskLifecycle(null);
          setTaskDiscardReason("");
        }}
        onOk={async () => {
          const pending = pendingTaskLifecycle;
          if (!pending) return;
          const reason = taskDiscardReason.trim();
          setPendingTaskLifecycle(null);
          await runMutation(`task:update:${editingTaskNode?.id}`, () =>
            performUpdateTaskNode(pending.values, true, reason)
          );
          setTaskDiscardReason("");
        }}
        open={Boolean(pendingTaskLifecycle)}
        title={
          <div className="taskLifecycleConfirmModal__title">
            <span>
              {pendingTaskLifecycle?.kind === "discard" ? (
                <WarningOutlined />
              ) : (
                <AuditOutlined />
              )}
            </span>
            <div>
              <strong>
                {pendingTaskLifecycle?.kind === "discard"
                  ? "废弃任务节点"
                  : "恢复任务节点"}
              </strong>
              <small>
                {pendingTaskLifecycle?.kind === "discard"
                  ? "危险操作 · 节点数据仍会完整保留"
                  : "任务将重新进入项目流程"}
              </small>
            </div>
          </div>
        }
        zIndex={3600}
      >
        <div className="taskLifecycleConfirmModal__content">
          <div>
            <span className="taskLifecycleConfirmModal__eyebrow">
              {pendingTaskLifecycle?.kind === "discard" ? "影响确认" : "恢复确认"}
            </span>
            <strong>
              {pendingTaskLifecycle?.kind === "discard"
                ? `即将废弃「${editingTaskNode?.title || "当前任务"}」`
                : "恢复后将重新参与项目统计与提醒"}
            </strong>
            <p>
              {pendingTaskLifecycle?.kind === "discard"
                ? "子任务、内容项、批注和历史记录会完整保留，后续任务不会被该节点阻塞。"
                : "任务会恢复到所选状态，原有子任务、内容项和历史记录保持不变。"}
            </p>
          </div>
        </div>
        {pendingTaskLifecycle?.kind === "discard" && (
          <>
            <div className="taskLifecycleConfirmModal__impactGrid">
              <div>
                <span>01</span>
                <strong>停止统计</strong>
                <small>不再计入进度、排行与到期提醒</small>
              </div>
              <div>
                <span>02</span>
                <strong>释放流程</strong>
                <small>后续任务不再被该节点阻塞</small>
              </div>
              <div>
                <span>03</span>
                <strong>保留数据</strong>
                <small>子任务、批注与历史记录不会删除</small>
              </div>
            </div>
            <div className="taskLifecycleConfirmModal__reason">
              <div>
                <label htmlFor="task-discard-reason">废弃原因</label>
                <span>必填，至少 2 个字</span>
              </div>
              <Input.TextArea
                id="task-discard-reason"
                maxLength={500}
                onChange={(event) => setTaskDiscardReason(event.target.value)}
                placeholder="例如：需求已取消，当前节点不再继续执行"
                rows={4}
                showCount
                value={taskDiscardReason}
              />
            </div>
          </>
        )}
      </Modal>

      <TaskConflictModal
        baseTask={taskConflict?.baseTask || {}}
        loading={taskConflictSaving}
        memberName={getMemberDisplayName}
        mine={taskConflict?.draft || {}}
        onCancel={() => setTaskConflict(null)}
        onConfirmMine={confirmTaskConflictMine}
        onUseServer={() => {
          const current = taskConflict?.serverTask;
          setTaskConflict(null);
          if (current) {
            openTaskEditModal(current);
            message.info("已载入服务器最新版本，可继续编辑");
          }
        }}
        open={!!taskConflict}
        serverTask={taskConflict?.serverTask || {}}
      />

      <Modal
        maskClosable={false}
        keyboard={false}
        title="新增项目成员"
        open={isProjectMemberAddModalOpen}
        onCancel={closeProjectMemberAddModal}
        footer={null}
        destroyOnClose
      >
        <Form<ProjectMemberAddFormValues>
          className="projectForm"
          form={projectMemberAddForm}
          layout="vertical"
          onFinish={addProjectMember}
        >
          <Form.Item
            label="项目成员"
            name="user_id"
            rules={[{ required: true, message: "请选择要新增的项目成员" }]}
          >
            <Select
              disabled={!projectMemberAddOptions.length}
              optionFilterProp="label"
              options={projectMemberAddOptions}
              placeholder={
                projectMemberAddOptions.length
                  ? "请选择要加入项目的成员"
                  : "暂无可新增成员"
              }
              showSearch
            />
          </Form.Item>
          <div className="projectMemberAddHint">
            只能添加当前团队内、且尚未加入该项目的成员。
          </div>
          <div className="projectForm__footer">
            <Button onClick={closeProjectMemberAddModal}>取消</Button>
            <Button
              disabled={!projectMemberAddOptions.length}
              htmlType="submit"
              loading={isMutationPending("project:member:add")}
              type="primary"
            >
              确认新增
            </Button>
          </div>
        </Form>
      </Modal>

      <Modal
        maskClosable={false}
        keyboard={false}
        title="删除项目"
        open={!!deletingProject}
        onCancel={() => setDeletingProject(null)}
        footer={null}
        destroyOnClose
      >
        <div className="deleteMemberConfirm">
          <div className="deleteMemberConfirm__icon">
            <DeleteOutlined />
          </div>
          <div className="deleteMemberConfirm__body">
            <p>
              确认删除项目{" "}
              <strong>{deletingProject?.project_name || "当前项目"}</strong> 吗？
            </p>
            <span>
              删除后项目将从项目看板中移除，项目成员将无法继续访问该项目。
            </span>
          </div>
          <div className="deleteMemberConfirm__footer">
            <Button onClick={() => setDeletingProject(null)}>取消</Button>
            <Button
              danger
              onClick={async () => {
                const current = deletingProject;
                setDeletingProject(null);
                if (current) {
                  await deleteProject(current);
                }
              }}
              type="primary"
            >
              确认删除
            </Button>
          </div>
        </div>
      </Modal>

      <Modal
        maskClosable={false}
        keyboard={false}
        title="确认顺延后续任务"
        open={!!cascadeDelayConfirm}
        onCancel={() => {
          if (!cascadeDelaySaving) {
            setCascadeDelayConfirm(null);
          }
        }}
        footer={null}
        closable={!cascadeDelaySaving}
        className="cascadeDelayModal"
        destroyOnClose
      >
        {cascadeDelayConfirm && (
          <div className="cascadeDelayConfirm">
            <div className="cascadeDelayConfirm__header">
              <div className="cascadeDelayConfirm__badge">
                +{cascadeDelayConfirm.days} 天
              </div>
              <div>
                <strong>{cascadeDelayConfirm.taskTitle}</strong>
                <p>预计完成时间已比原计划延期</p>
              </div>
            </div>
            <div className="cascadeDelayConfirm__body">
              <p>
                {cascadeDelayConfirm.scope === "main"
                  ? "确认顺延后，该成员后续任务模块会自动顺延；这些后续任务下的同级任务节点也会一起顺延。"
                  : "确认顺延后，同一主任务下排序更靠后的同级任务节点会自动顺延。"}
              </p>
              <span>不顺延则只保存当前节点的预计完成时间。</span>
            </div>
            <div className="cascadeDelayConfirm__footer">
              <Button
                className="cascadeDelayConfirm__secondary"
                disabled={cascadeDelaySaving}
                onClick={async () => {
                  const current = cascadeDelayConfirm;
                  if (current) {
                    setCascadeDelaySaving(true);
                    try {
                      const result = await runMutation(
                        `task:cascade:${editingTaskNode?.id}`,
                        () => current.onSave(false)
                      );
                      const saved = result.executed && result.value;
                      if (saved) {
                        setCascadeDelayConfirm(null);
                      }
                    } finally {
                      setCascadeDelaySaving(false);
                    }
                  }
                }}
              >
                不顺延，仅保存
              </Button>
              <Button
                className="cascadeDelayConfirm__primary"
                loading={cascadeDelaySaving}
                onClick={async () => {
                  const current = cascadeDelayConfirm;
                  if (current) {
                    setCascadeDelaySaving(true);
                    try {
                      const result = await runMutation(
                        `task:cascade:${editingTaskNode?.id}`,
                        () => current.onSave(true)
                      );
                      const saved = result.executed && result.value;
                      if (saved) {
                        setCascadeDelayConfirm(null);
                      }
                    } finally {
                      setCascadeDelaySaving(false);
                    }
                  }
                }}
                type="primary"
              >
                确认并顺延
              </Button>
            </div>
          </div>
        )}
      </Modal>

      <Modal
        maskClosable={false}
        keyboard={false}
        title="移除项目成员"
        open={!!removingProjectMember}
        onCancel={() => setRemovingProjectMember(null)}
        footer={null}
        destroyOnClose
      >
        <div className="deleteMemberConfirm">
          <div className="deleteMemberConfirm__icon">
            <DeleteOutlined />
          </div>
          <div className="deleteMemberConfirm__body">
            <p>
              确认将{" "}
              <strong>
                {getMemberDisplayName(removingProjectMember?.user_id || "") ||
                  removingProjectMember?.user_id ||
                  "该成员"}
              </strong>{" "}
              从项目中移除吗？
            </p>
            <span>
              移除后该成员将无法访问当前项目；已有任务节点、批注和日志记录会继续保留。
            </span>
            <div className="deleteMemberConfirm__meta">
              <span>项目</span>
              <strong>{flowProject?.project_name || "-"}</strong>
            </div>
          </div>
          <div className="deleteMemberConfirm__footer">
            <Button onClick={() => setRemovingProjectMember(null)}>取消</Button>
            <Button
              danger
              onClick={async () => {
                const current = removingProjectMember;
                setRemovingProjectMember(null);
                if (current) {
                  await removeProjectMember(current);
                }
              }}
              type="primary"
            >
              确认移除
            </Button>
          </div>
        </div>
      </Modal>

      <Modal
        maskClosable={false}
        keyboard={false}
        title="删除任务节点"
        open={!!confirmingTaskDelete}
        onCancel={() => setConfirmingTaskDelete(null)}
        footer={null}
        destroyOnClose
      >
        <div className="deleteMemberConfirm">
          <div className="deleteMemberConfirm__icon">
            <DeleteOutlined />
          </div>
          <div className="deleteMemberConfirm__body">
            <p>
              确认删除任务节点{" "}
              <strong>{confirmingTaskDelete?.title || "当前节点"}</strong> 吗？
            </p>
            <span>删除任务会影响项目节点流程，请确认后再继续。</span>
          </div>
          <div className="deleteMemberConfirm__footer">
            <Button onClick={() => setConfirmingTaskDelete(null)}>取消</Button>
            <Button
              danger
              onClick={async () => {
                const current = confirmingTaskDelete;
                setConfirmingTaskDelete(null);
                if (current) {
                  await deleteTaskNode(current);
                }
              }}
              type="primary"
            >
              确认删除
            </Button>
          </div>
        </div>
      </Modal>

      <Modal
        maskClosable={false}
        keyboard={false}
        title="移除项目附件"
        open={!!removingProjectAttachment}
        onCancel={() => setRemovingProjectAttachment(null)}
        footer={null}
        destroyOnClose
      >
        <div className="deleteMemberConfirm">
          <div className="deleteMemberConfirm__icon">
            <DeleteOutlined />
          </div>
          <div className="deleteMemberConfirm__body">
            <p>
              确认移除附件{" "}
              <strong>{removingProjectAttachment?.name || "当前附件"}</strong> 吗？
            </p>
            <span>移除后该附件文件将从当前项目附件中删除。</span>
          </div>
          <div className="deleteMemberConfirm__footer">
            <Button onClick={() => setRemovingProjectAttachment(null)}>
              取消
            </Button>
            <Button
              danger
              onClick={async () => {
                const current = removingProjectAttachment;
                setRemovingProjectAttachment(null);
                if (current) {
                  await removeProjectAttachment(current.attachment_id);
                }
              }}
              type="primary"
            >
              确认移除
            </Button>
          </div>
        </div>
      </Modal>

      <Modal
        maskClosable={false}
        keyboard={false}
        title="删除节点批注"
        open={!!deletingTaskComment}
        onCancel={() => setDeletingTaskComment(null)}
        footer={null}
        destroyOnClose
      >
        <div className="deleteMemberConfirm">
          <div className="deleteMemberConfirm__icon">
            <DeleteOutlined />
          </div>
          <div className="deleteMemberConfirm__body">
            <p>确认删除这条节点批注吗？</p>
            <span>删除后该批注内容将不再展示在节点详情中。</span>
          </div>
          <div className="deleteMemberConfirm__footer">
            <Button onClick={() => setDeletingTaskComment(null)}>取消</Button>
            <Button
              danger
              onClick={async () => {
                const current = deletingTaskComment;
                setDeletingTaskComment(null);
                if (current) {
                  await deleteTaskComment(current.comment, current.task);
                }
              }}
              type="primary"
            >
              确认删除
            </Button>
          </div>
        </div>
      </Modal>

      <Modal
        maskClosable={false}
        keyboard={false}
        title="项目日志详情"
        open={!!selectedProjectLog}
        onCancel={() => setSelectedProjectLog(null)}
        footer={null}
        destroyOnClose
        width={680}
      >
        {selectedProjectLog && (
          <div className="projectLogDetailModal">
            <div className="projectLogDetailModal__summary">
              <strong>{getProjectLogActionText(selectedProjectLog.action)}</strong>
              <p>{getProjectLogSummary(selectedProjectLog)}</p>
            </div>
            <div className="projectLogDetailModal__meta">
              <span>操作人</span>
              <strong>
                {getMemberDisplayName(selectedProjectLog.operator) ||
                  selectedProjectLog.operator}
              </strong>
              <span>操作时间</span>
              <strong>{formatMessageTime(selectedProjectLog.created_at)}</strong>
              <span>对象类型</span>
              <strong>{getProjectLogTargetText(selectedProjectLog.target_type)}</strong>
              <span>对象 ID</span>
              <strong>{selectedProjectLog.target_id}</strong>
            </div>
            <div className="projectLogDetailModal__compare">
              <div className="projectLogDetailModal__compareHeader">
                <span>字段</span>
                <span>变更前</span>
                <span>变更后</span>
              </div>
              {getProjectLogDetailRows(selectedProjectLog).length ? (
                getProjectLogDetailRows(selectedProjectLog).map((row) => (
                  <div className="projectLogDetailModal__compareRow" key={row.key}>
                    <strong>{row.key}</strong>
                    <span>{row.before}</span>
                    <span>{row.after}</span>
                  </div>
                ))
              ) : (
                <div className="projectLogDetailModal__empty">
                  该日志没有可对比字段
                </div>
              )}
            </div>
          </div>
        )}
      </Modal>

      <Modal
        maskClosable={false}
        keyboard={false}
        title="删除成员"
        open={!!deletingMember}
        onCancel={() => setDeletingMember(null)}
        footer={null}
        destroyOnClose
      >
        <div className="deleteMemberConfirm">
          <div className="deleteMemberConfirm__icon">
            <DeleteOutlined />
          </div>
          <div className="deleteMemberConfirm__body">
            <p>
              确认删除成员{" "}
              <strong>
                {deletingMember?.nickname || deletingMember?.username || "成员"}
              </strong>
              吗？
            </p>
            <span>
              删除后该账号将无法登录，并会从当前团队的项目成员列表中移除；已有任务和日志记录会保留。
            </span>
            <div className="deleteMemberConfirm__meta">
              <span>账号</span>
              <strong>{deletingMember?.username || "-"}</strong>
            </div>
          </div>
          <div className="deleteMemberConfirm__footer">
            <Button onClick={() => setDeletingMember(null)}>取消</Button>
            <Button
              danger
              type="primary"
              onClick={async () => {
                const current = deletingMember;
                setDeletingMember(null);
                if (current) {
                  await deleteTeamMember(current);
                }
              }}
            >
              确认删除
            </Button>
          </div>
        </div>
      </Modal>

      <DeleteDepartmentModal
        departmentName={deletingDepartment}
        loading={departmentDeleting}
        memberCount={teamMembers.filter((member) =>
          (member.department || []).includes(deletingDepartment)
        ).length}
        onCancel={() => {
          if (!departmentDeleting) {
            setDeletingDepartment("");
          }
        }}
        onConfirm={() => deleteDepartment(deletingDepartment)}
        open={!!deletingDepartment}
      />

      <Drawer
        className="messageDrawer"
        destroyOnClose={false}
        keyboard={false}
        maskClosable={false}
        onClose={() => setIsMessageDrawerOpen(false)}
        open={isMessageDrawerOpen}
        placement="right"
        title={
          <div className="messageDrawer__title">
            <div>
              <strong>收件箱</strong>
              <span>{unreadMessageCount} 条未读消息</span>
            </div>
            <Button
              disabled={!messageList.length}
              onClick={markAllMessagesRead}
              size="small"
              type="link"
            >
              全部已读
            </Button>
          </div>
        }
        width={420}
      >
        <div className="messageDrawer__content">
          {messageLoading ? (
            <div className="messageDrawer__empty">消息加载中...</div>
          ) : messageList.length ? (
            messageList.map((item) => {
              const detail = item.message;
              const isRead = !!item.read_at;
              const isProjectAnnouncement = detail?.type === "project.announcement";
              const isTaskMessage = isTaskNodeMessage(detail?.type);
              const projectId = detail?.payload?.project_id || detail?.project_id;
              const projectName = getMessageProjectName(detail);
              return (
                <div
                  className={
                    isRead
                      ? "messageItem messageItem--read"
                      : "messageItem messageItem--unread"
                  }
                  key={`${item.message_id}-${item.receiver_id}`}
                  onClick={() => openMessageDetail(item)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      openMessageDetail(item);
                    }
                  }}
                  role="button"
                  tabIndex={0}
                >
                  <div className="messageItem__header">
                    <Tag>{getMessageTypeText(detail?.type)}</Tag>
                    <span>{formatMessageTime(detail?.created_at || item.created_at)}</span>
                  </div>
                  <h4>{detail?.title || "未知消息"}</h4>
                  {(isProjectAnnouncement || isTaskMessage) && projectName && (
                    <div className="messageItem__project">
                      归属项目：<strong>{projectName}</strong>
                    </div>
                  )}
                  <div
                    className="messageItem__content"
                    dangerouslySetInnerHTML={{
                      __html: detail?.content || "暂无消息内容",
                    }}
                  />
                  {item.action_required && !isProjectAnnouncement && (
                    <small>
                      处理状态：
                      {item.action_status === "pending"
                        ? "待处理"
                        : item.action_status}
                    </small>
                  )}
                  {(isProjectAnnouncement || isTaskMessage) && (
                    <Button
                      className={`messageItem__action ${
                        isSubtaskReviewRequestMessage(detail)
                          ? "messageItem__action--review"
                          : ""
                      }`}
                      icon={
                        isSubtaskReviewRequestMessage(detail) ? (
                          <AuditOutlined />
                        ) : (
                          <ProjectOutlined />
                        )
                      }
                      onClick={async (event) => {
                        event.stopPropagation();
                        await markMessageRead(item);
                        setIsMessageDrawerOpen(false);
                        openProjectFlowById(
                          projectId,
                          getTaskMessageFlowTarget(detail)
                        );
                      }}
                      size="small"
                      type="primary"
                    >
                      {getMessageProjectActionText(detail)}
                    </Button>
                  )}
                </div>
              );
            })
          ) : (
            <div className="messageDrawer__empty">暂无消息</div>
          )}
        </div>
      </Drawer>

      <Modal
        maskClosable={false}
        keyboard={false}
        className="messageDetailModal"
        destroyOnClose
        footer={null}
        onCancel={() => setSelectedMessage(null)}
        open={!!selectedMessage}
        title="消息详情"
        width={720}
      >
        {selectedMessage && (
          <div className="messageDetail">
            <div className="messageDetail__header">
              <Tag>{getMessageTypeText(selectedMessage.message?.type)}</Tag>
              <span>
                {formatMessageTime(
                  selectedMessage.message?.created_at ||
                    selectedMessage.created_at
                )}
              </span>
            </div>
            <h3>{selectedMessage.message?.title || "未知消息"}</h3>
            {(selectedMessage.message?.type === "project.announcement" ||
              isTaskNodeMessage(selectedMessage.message?.type)) &&
              getMessageProjectName(selectedMessage.message) && (
                <div className="messageDetail__project">
                  归属项目：
                  <strong>{getMessageProjectName(selectedMessage.message)}</strong>
                </div>
              )}
            <div
              className="messageDetail__content"
              dangerouslySetInnerHTML={{
                __html: selectedMessage.message?.content || "暂无消息内容",
              }}
            />
            <div className="messageDetail__footer">
              {(selectedMessage.message?.type === "project.announcement" ||
                isTaskNodeMessage(selectedMessage.message?.type)) && (
                <Button
                  className={
                    isSubtaskReviewRequestMessage(selectedMessage.message)
                      ? "messageDetail__reviewAction"
                      : ""
                  }
                  icon={
                    isSubtaskReviewRequestMessage(selectedMessage.message) ? (
                      <AuditOutlined />
                    ) : (
                      <ProjectOutlined />
                    )
                  }
                  onClick={() => {
                    const projectId =
                      selectedMessage.message?.payload?.project_id ||
                      selectedMessage.message?.project_id;
                    setSelectedMessage(null);
                    setIsMessageDrawerOpen(false);
                    openProjectFlowById(
                      projectId,
                      getTaskMessageFlowTarget(selectedMessage.message)
                    );
                  }}
                  type="primary"
                >
                  {getMessageProjectActionText(selectedMessage.message)}
                </Button>
              )}
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default Home;
